import { useEffect, useMemo, useRef, useState } from "react";
import CameraCapture from "../components/CameraCapture";
import SignaturePad from "../components/SignaturePad";
import { getApiUrl, getAppBaseUrl, getJson, patchJson, postForm, postJson } from "../api";
import {
  buildRows,
  CaptureResultCard,
  FormCard,
  Input,
  PortalLayout,
  PortalListItem,
  ProcessingPanel,
  StatusBadge,
  StatusMessage,
  formatDate,
  humanizeLabel,
} from "./shared";

const initialVehicle = {
  titular_cuit: "",
  dominio: "",
  marca: "",
  modelo: "",
  anio: "",
  tipo: "",
};

const UNIT_REGISTRATION_REQUEST_STORAGE_KEY = "portalPendingUnitRegistrationRequest";
const TRANSPORT_SESSION_STORAGE_KEY = "portalTransporteSession";

function getUnitRegistrationLabel(unitType) {
  return unitType === "tractor_unit" ? "Unidad tractora" : "Unidad remolque";
}

function getUnitDocumentLabel(documentType) {
  const labels = {
    rto: "RTO",
    seguro_camion: "Seguro de camión",
    seguro_acoplado: "Seguro de remolque",
    cedula_frente: "Cédula frente",
    cedula_dorso: "Cédula dorso",
  };
  return labels[documentType] || humanizeLabel(documentType || "documento");
}

function getOperationDocumentRequestLabel(request) {
  return request?.metadata?.document_label || humanizeLabel(request?.document_type || "documento");
}

function isMockPortalDocument(document) {
  if (document?.mock) {
    return true;
  }

  const numericId = Number(document?.id);
  if (Number.isInteger(numericId) && numericId >= 9000 && numericId < 10000 && !document?.s3_key) {
    return true;
  }

  return false;
}

function isVehicleInsuranceDocumentType(documentType) {
  return documentType === "seguro_camion" || documentType === "seguro_acoplado";
}

function hasVehicleCedulaLoaded(entityData) {
  const documents = Array.isArray(entityData?.documentos) ? entityData.documentos : [];
  return documents.some((document) => {
    const type = String(document?.tipo_documento || "");
    return type === "cedula_frente" || type === "cedula";
  });
}

function driverStatusLabel(status) {
  if (status === "approved") {
    return "Vínculo activo";
  }
  if (status === "pending") {
    return "Pendiente";
  }
  if (status === "revoked") {
    return "Revocado";
  }
  return humanizeLabel(status || "sin estado");
}

function readPendingUnitRegistrationRequest() {
  try {
    const raw = window.localStorage.getItem(UNIT_REGISTRATION_REQUEST_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
}

function clearPendingUnitRegistrationRequest() {
  window.localStorage.removeItem(UNIT_REGISTRATION_REQUEST_STORAGE_KEY);
}

function closePendingUnitRequestAndReturnToRequests(setPendingUnitRequest) {
  clearPendingUnitRegistrationRequest();
  setPendingUnitRequest(null);
  window.location.hash = "#/portal-empresa-transporte/app/solicitudes";
}

const PDFJS_SCRIPT_URL = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.5.141/pdf.min.js";
const PDFJS_WORKER_URL = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.5.141/pdf.worker.min.js";
let pdfJsLoader = null;

async function ensurePdfJs() {
  if (typeof window === "undefined") {
    throw new Error("La conversión de PDF solo está disponible en el navegador.");
  }

  if (window?.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
    return window.pdfjsLib;
  }

  if (pdfJsLoader) {
    return pdfJsLoader;
  }

  pdfJsLoader = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = PDFJS_SCRIPT_URL;
    script.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error("No se pudo cargar PDF.js."));
        return;
      }
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error("No se pudo cargar PDF.js."));
    document.head.appendChild(script);
  });

  return pdfJsLoader;
}

function canvasToBlob(canvas, type = "image/jpeg", quality = 0.9) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("No se pudo generar la imagen."));
          return;
        }
        resolve(blob);
      },
      type,
      quality
    );
  });
}

async function convertPdfFirstPageToImage(file) {
  const pdfjsLib = await ensurePdfJs();
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdfDocument = await loadingTask.promise;

  try {
    const page = await pdfDocument.getPage(1);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const context = canvas.getContext("2d");
    await page.render({ canvasContext: context, viewport }).promise;
    const blob = await canvasToBlob(canvas);
    const sanitizedName = (file.name || "constancia").replace(/\.pdf$/i, "");
    page.cleanup();
    return new File([blob], `${sanitizedName}-page1.jpg`, { type: "image/jpeg" });
  } finally {
    pdfDocument.destroy();
  }
}

async function extractTextFromPdf(file) {
  const pdfjsLib = await ensurePdfJs();
  const arrayBuffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
  const pdfDocument = await loadingTask.promise;

  try {
    const page = await pdfDocument.getPage(1);
    const content = await page.getTextContent();
    return (content.items || []).map((item) => item.str || "").join(" ");
  } finally {
    pdfDocument.destroy();
  }
}

function findCuitInText(text) {
  if (!text) {
    return "";
  }
  const normalized = text.replace(/\s+/g, " ");
  const patterns = [
    /\bCUIT\s*(?::|-)?\s*(\d{2})[- ]*(\d{8})[- ]*(\d)\b/i,
    /\b(20|23|24|27|30|33|34)(?:[- ]?)(\d{8})(?:[- ]?)(\d)\b/i,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (match) {
      const parts = match.slice(1, 4).filter(Boolean);
      if (parts.length) {
        return parts.join("");
      }
    }
  }

  return "";
}

const initialCompanyAuth = {
  cuit: "",
  razon_social: "",
  email: "",
  password: "",
};

const initialTransportAuth = {
  email: "",
  password: "",
};

const initialTransportCompanyCompletion = {
  company_cuit: "",
  company_name: "",
  company_email: "",
};

const MOCK_TITULAR_CUIT = "30712345671";

const MOCK_TRANSPORTE_DASHBOARD = {
  titular: {
    cuit: MOCK_TITULAR_CUIT,
    razon_social: "Transportes Debuchy SA",
    email: "operaciones@transportesdebuchy.com",
  },
  documentos: [],
  unidades: [
    {
      dominio: "AE563TR",
      marca: "Iveco",
      modelo: "Tector",
      anio: "2022",
      tipo: "Tractor",
      mock: true,
      documentos: [
        {
          id: 9001,
          mock: true,
          tipo_documento: "cedula_frente",
          estado: "vigente",
          mime_type: "image/jpeg",
          created_at: "2026-03-10",
        },
        {
          id: 9002,
          mock: true,
          tipo_documento: "rto",
          estado: "vigente",
          mime_type: "application/pdf",
          fecha_vencimiento: "2026-09-30",
          created_at: "2026-03-10",
        },
        {
          id: 9003,
          mock: true,
          tipo_documento: "seguro_camion",
          estado: "vigente",
          mime_type: "application/pdf",
          fecha_vencimiento: "2026-12-31",
          created_at: "2026-03-10",
        },
      ],
    },
    {
      dominio: "AF771LM",
      marca: "Scania",
      modelo: "R450",
      anio: "2021",
      tipo: "Tractor",
      mock: true,
      documentos: [
        {
          id: 9004,
          mock: true,
          tipo_documento: "cedula_frente",
          estado: "vigente",
          mime_type: "image/jpeg",
          created_at: "2026-03-09",
        },
        {
          id: 9005,
          mock: true,
          tipo_documento: "rto",
          estado: "observado",
          mime_type: "application/pdf",
          created_at: "2026-03-09",
        },
      ],
    },
  ],
  acoplados: [
    {
      dominio: "AC875QP",
      marca: "Randon",
      modelo: "SR CA",
      anio: "2020",
      tipo: "Semi-remolque",
      mock: true,
      documentos: [
        {
          id: 9010,
          mock: true,
          tipo_documento: "cedula_frente",
          estado: "vigente",
          mime_type: "image/jpeg",
          created_at: "2026-03-08",
        },
        {
          id: 9011,
          mock: true,
          tipo_documento: "seguro_acoplado",
          estado: "vigente",
          mime_type: "application/pdf",
          fecha_vencimiento: "2026-11-15",
          created_at: "2026-03-08",
        },
      ],
    },
    {
      dominio: "AD998RX",
      marca: "Hermann",
      modelo: "Baranda volcable",
      anio: "2019",
      tipo: "Acoplado",
      mock: true,
      documentos: [
        {
          id: 9012,
          mock: true,
          tipo_documento: "cedula_frente",
          estado: "observado",
          mime_type: "image/jpeg",
          created_at: "2026-03-08",
        },
      ],
    },
  ],
};

const UNIT_PHOTO_STEPS = [
  { value: "cedula_frente", label: "Cedula frente" },
  { value: "cedula_dorso", label: "Cedula dorso" },
  { value: "rto", label: "RTO" },
  { value: "seguro_camion", label: "Seguro" },
];

const DEFAULT_TRANSPORT_DOCUMENT_OPTIONS = {
  chofer: [
    { value: "licencia", label: "Licencia", description: "Licencia del chofer" },
    { value: "seguro_vida", label: "Seguro de vida", description: "Poliza o constancia" },
    { value: "f931", label: "F931", description: "Comprobante F931" },
    { value: "psicofisico", label: "Psicofisico", description: "Apto psicofisico" },
  ],
  unidad: [
    { value: "cedula_frente", label: "Cedula frente", description: "Frente de la cedula de la unidad" },
    { value: "cedula_dorso", label: "Cedula dorso", description: "Dorso de la cedula de la unidad" },
    { value: "rto", label: "RTO", description: "Revision tecnica" },
    { value: "seguro_camion", label: "Seguro camion", description: "Poliza vigente" },
  ],
  acoplado: [
    { value: "cedula_frente", label: "Cedula frente", description: "Frente de la cedula del acoplado" },
    { value: "cedula_dorso", label: "Cedula dorso", description: "Dorso de la cedula del acoplado" },
    { value: "rto", label: "RTO", description: "Revision tecnica del acoplado" },
    { value: "seguro_acoplado", label: "Seguro acoplado", description: "Poliza vigente" },
  ],
  titular: [
    { value: "constancia_afip", label: "Certificado AFIP", description: "Certificado de alta en AFIP" },
    { value: "cm05", label: "CM05", description: "Convenio multilateral" },
    { value: "inscripcion_ruta", label: "Inscripcion RUTA", description: "Registro Único del Transporte Automotor" },
    { value: "habilitacion_cnrt", label: "Habilitacion CNRT", description: "Si hace transporte interjurisdiccional" },
    { value: "seguro_responsabilidad_civil", label: "Seguro RC", description: "Seguro de responsabilidad civil" },
    { value: "seguro_cargas", label: "Seguro de cargas", description: "Cobertura de cargas" },
    { value: "documentacion_legal", label: "Documentacion legal", description: "Resumen de papeles societarios" },
    { value: "estatuto_contrato_social", label: "Estatuto o contrato", description: "Estatuto o contrato social" },
    { value: "acta_autoridades", label: "Acta autoridades", description: "Acta de designación de autoridades" },
    { value: "poder", label: "Poder", description: "Poder otorgado por la empresa" },
  ],
};

const TRANSPORT_POLICY_ENTITY_MAP = {
  chofer: "driver",
  unidad: "tractor_unit",
  acoplado: "trailer_unit",
  titular: "transport_company",
};

const TRANSPORT_POLICY_DOCUMENT_ALIAS_MAP = {
  titular: {
    afip_certificate: "constancia_afip",
    ruta_registration: "inscripcion_ruta",
    cnrt_authorization: "habilitacion_cnrt",
    rc_insurance: "seguro_responsabilidad_civil",
    cargo_insurance: "seguro_cargas",
    legal_documentation: "documentacion_legal",
    bylaws_or_social_contract: "estatuto_contrato_social",
    authorities_minutes: "acta_autoridades",
    power_of_attorney: "poder",
  },
};

function normalizeTransportDocumentType(entidadTipo, documentType) {
  const normalizedEntityType = String(entidadTipo || "").trim();
  const normalizedDocumentType = String(documentType || "").trim();
  if (!normalizedDocumentType) {
    return "";
  }

  const aliasMap = TRANSPORT_POLICY_DOCUMENT_ALIAS_MAP[normalizedEntityType] || {};
  return aliasMap[normalizedDocumentType] || normalizedDocumentType;
}

function matchesTransportDocumentType(entidadTipo, documentType, optionValue) {
  const normalizedDocumentType = normalizeTransportDocumentType(entidadTipo, documentType);
  const normalizedOptionValue = normalizeTransportDocumentType(entidadTipo, optionValue);
  if (!normalizedDocumentType || !normalizedOptionValue) {
    return false;
  }

  return normalizedDocumentType === normalizedOptionValue;
}

function usePortalDocumentPolicyCatalog() {
  const [policyCatalog, setPolicyCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function loadPolicyCatalog() {
      try {
        const response = await getJson("/document-policies/catalog");
        if (!cancelled) {
          setPolicyCatalog(response.data || []);
        }
      } catch (_error) {
        if (!cancelled) {
          setPolicyCatalog([]);
        }
      }
    }

    loadPolicyCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  return policyCatalog;
}

function buildTransportDocumentOptionsFromCatalog(entidadTipo, policyCatalog = []) {
  const policyEntityType = TRANSPORT_POLICY_ENTITY_MAP[entidadTipo];
  if (!policyEntityType) {
    return [];
  }

  const aliasMap = TRANSPORT_POLICY_DOCUMENT_ALIAS_MAP[entidadTipo] || {};
  const options = (policyCatalog || [])
    .filter((entry) => entry.entity_type === policyEntityType)
    .map((entry) => ({
      value: aliasMap[entry.document_type] || entry.document_type,
      label: entry.label || humanizeLabel(entry.document_type),
      description: entry.description || "",
      sort_order: entry.sort_order || 100,
    }))
    .filter((item, index, list) => list.findIndex((candidate) => candidate.value === item.value) === index)
    .sort((a, b) => {
      const aPriority = getTransportDocumentDisplayPriority(entidadTipo, a.value);
      const bPriority = getTransportDocumentDisplayPriority(entidadTipo, b.value);
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      if ((a.sort_order || 100) !== (b.sort_order || 100)) {
        return (a.sort_order || 100) - (b.sort_order || 100);
      }
      return a.label.localeCompare(b.label);
    });

  return options;
}

function getTransportDocumentDisplayPriority(entidadTipo, documentType) {
  if (entidadTipo !== "chofer") {
    return 100;
  }

  const normalized = String(documentType || "").toLowerCase();
  if (normalized === "dni" || normalized.startsWith("dni")) {
    return 0;
  }
  if (normalized === "biometria" || normalized.includes("biometr") || normalized.includes("selfie")) {
    return 1;
  }
  return 100;
}

function isRestrictedChoferIdentityDocumentForTransport(documentType) {
  const normalized = normalizeTransportDocumentType("chofer", documentType).toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized === "dni" ||
    normalized.startsWith("dni") ||
    normalized === "biometria" ||
    normalized.includes("biometr") ||
    normalized.includes("selfie")
  );
}

function getUnitDocumentTypesForBatch(batchType, count = 0) {
  if (batchType === "nueva_unidad") {
    return Array.from({ length: count }, (_item, index) => (index === 0 ? "cedula_frente" : "cedula_dorso"));
  }

  if (batchType === "cedula_dorso") {
    return Array.from({ length: count }, () => "cedula_dorso");
  }

  if (batchType === "rto") {
    return Array.from({ length: count }, () => "rto");
  }

  return Array.from({ length: count }, () => "seguro_camion");
}

function readTransportSession() {
  try {
    const raw = window.localStorage.getItem(TRANSPORT_SESSION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_error) {
    return null;
  }
}

function getTransportAppHomeHash() {
  return "#/portal-empresa-transporte/app";
}

function getTransportPortalBackHash() {
  return readTransportSession() ? getTransportAppHomeHash() : "#/portal-empresa-transporte/auth";
}

async function buildTransportCompanyCuists(titularCuit) {
  return titularCuit ? [titularCuit] : [];
}

async function loadScopedTransportCompanies(titularCuit, userEmail = "") {
  const ownedCuits = new Set(readOwnedTransportCompanies());
  const normalizedEmail = String(userEmail || "").trim().toLowerCase();

  if (!ownedCuits.size && !normalizedEmail) {
    return [];
  }

  try {
    const response = await getJson("/portal-transporte/titulares");
    const companies = Array.isArray(response.data) ? response.data : [];
    const filtered = companies.filter((company) => {
      const companyCuit = String(company?.cuit || "");
      const companyEmail = String(company?.email || "").trim().toLowerCase();
      return ownedCuits.has(companyCuit) || (normalizedEmail && companyEmail === normalizedEmail);
    });
    const deduped = dedupeTransportCompanies(filtered);
    writeOwnedTransportCompanies(deduped.map((company) => company.cuit));
    return deduped;
  } catch (_error) {
    return [];
  }
}

function normalizeTransportCompanyName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]/g, "")
    .trim()
    .toUpperCase();
}

function getTransportCompanyScore(company) {
  const documentsCount = Array.isArray(company?.documentos) ? company.documentos.length : 0;
  const updatedAt = new Date(company?.updated_at || company?.created_at || 0).getTime();
  return {
    documentsCount,
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : 0,
  };
}

function pickPreferredTransportCompany(current, incoming) {
  if (!current) {
    return incoming;
  }

  const currentScore = getTransportCompanyScore(current);
  const incomingScore = getTransportCompanyScore(incoming);

  if (incomingScore.documentsCount !== currentScore.documentsCount) {
    return incomingScore.documentsCount > currentScore.documentsCount ? incoming : current;
  }

  if (incomingScore.updatedAt !== currentScore.updatedAt) {
    return incomingScore.updatedAt > currentScore.updatedAt ? incoming : current;
  }

  return current;
}

function dedupeTransportCompanies(companies = []) {
  const byCuit = new Map();
  companies.forEach((company) => {
    const companyCuit = String(company?.cuit || "");
    if (!companyCuit) {
      return;
    }
    byCuit.set(companyCuit, pickPreferredTransportCompany(byCuit.get(companyCuit), company));
  });

  const byName = new Map();
  Array.from(byCuit.values()).forEach((company) => {
    const normalizedName = normalizeTransportCompanyName(company?.razon_social);
    if (!normalizedName) {
      byName.set(`cuit:${company.cuit}`, company);
      return;
    }
    byName.set(normalizedName, pickPreferredTransportCompany(byName.get(normalizedName), company));
  });

  return Array.from(byName.values()).sort(
    (left, right) => new Date(right.updated_at || right.created_at || 0) - new Date(left.updated_at || left.created_at || 0)
  );
}

function flattenSettledResults(results) {
  return results
    .filter((item) => item.status === "fulfilled")
    .flatMap((item) => item.value?.data || []);
}

async function fetchRequestsForTransportCuists(cuists) {
  if (!cuists.length) {
    return { authorizationRequests: [], unitRequests: [], operationDocumentRequests: [] };
  }

  const authPromises = cuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/authorization-requests`));
  const unitPromises = cuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/unit-registration-requests`));
  const operationDocumentPromises = cuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/operation-document-requests`));
  const [authResults, unitResults, operationDocumentResults] = await Promise.all([
    Promise.allSettled(authPromises),
    Promise.allSettled(unitPromises),
    Promise.allSettled(operationDocumentPromises),
  ]);
  return {
    authorizationRequests: flattenSettledResults(authResults),
    unitRequests: flattenSettledResults(unitResults),
    operationDocumentRequests: flattenSettledResults(operationDocumentResults),
  };
}

async function fetchRecentApprovedDriversForTransportCuists(cuists, limitPerCompany = 3) {
  if (!cuists.length) {
    return [];
  }

  const responses = await Promise.allSettled(
    cuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/choferes?limit=${limitPerCompany}`))
  );

  const byChoferCuit = new Map();
  responses
    .filter((result) => result.status === "fulfilled")
    .flatMap((result) => result.value?.data || [])
    .filter((driver) => driver?.status === "approved" && isValidCuit(driver?.chofer_cuit))
    .forEach((driver) => {
      const current = byChoferCuit.get(driver.chofer_cuit);
      const currentDate = new Date(current?.updated_at || current?.created_at || 0).getTime();
      const nextDate = new Date(driver?.updated_at || driver?.created_at || 0).getTime();
      if (!current || nextDate >= currentDate) {
        byChoferCuit.set(driver.chofer_cuit, driver);
      }
    });

  return Array.from(byChoferCuit.values())
    .sort((left, right) => new Date(right.updated_at || right.created_at || 0) - new Date(left.updated_at || left.created_at || 0))
    .slice(0, 3);
}

const HIDDEN_COMPANIES_STORAGE_KEY = "camionero_hidden_companies";
const OWNED_TRANSPORT_COMPANIES_STORAGE_KEY = "portalTransporteOwnedCompanies";

function readHiddenCompanies() {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(HIDDEN_COMPANIES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_error) {
    return [];
  }
}

function writeHiddenCompanies(value = []) {
  try {
    window.localStorage.setItem(HIDDEN_COMPANIES_STORAGE_KEY, JSON.stringify(value));
  } catch (_error) {}
}

function readOwnedTransportCompanies() {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(OWNED_TRANSPORT_COMPANIES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((value) => isValidCuit(value)) : [];
  } catch (_error) {
    return [];
  }
}

function writeOwnedTransportCompanies(value = []) {
  try {
    const unique = Array.from(new Set((value || []).filter((item) => isValidCuit(item))));
    window.localStorage.setItem(OWNED_TRANSPORT_COMPANIES_STORAGE_KEY, JSON.stringify(unique));
  } catch (_error) {}
}

function addOwnedTransportCompany(cuit) {
  if (!isValidCuit(cuit)) {
    return;
  }
  const current = readOwnedTransportCompanies();
  if (!current.includes(cuit)) {
    writeOwnedTransportCompanies([...current, cuit]);
  }
}

function saveTransportSession(session) {
  window.localStorage.setItem(TRANSPORT_SESSION_STORAGE_KEY, JSON.stringify(session));
  if (session?.company_cuit) {
    window.localStorage.setItem("portalTransporteTitularCuit", session.company_cuit);
  }
}

function readHashQuery() {
  const [, query = ""] = String(window.location.hash || "").split("?");
  return new URLSearchParams(query);
}

async function openSignedContractDocumentPopup(documentId) {
  const popup = window.open("about:blank", "_blank");
  if (popup) {
    popup.opener = null;
  }
  try {
    const response = await getJson(`/signatures/documents/${documentId}/url`);
    if (popup) {
      popup.location.href = response.data.url;
    }
  } catch (error) {
    if (popup) {
      popup.document.open();
      popup.document.write(buildDocumentErrorHtml(error?.message || "No pudimos abrir el archivo firmado."));
      popup.document.close();
    }
  }
}

async function loadAuthorizationRequestsForTransportCompanies(companies) {
  const responses = await Promise.allSettled(
    (companies || []).map(async (company) => {
      const response = await getJson(`/portal-transporte/titulares/${company.cuit}/authorization-requests`);
      return (response.data || []).map((request) => ({
        ...request,
        transport_company_name: company.razon_social || `Empresa ${company.cuit}`,
        transport_company_cuit: company.cuit,
      }));
    })
  );

  return responses.flatMap((result) => (result.status === "fulfilled" ? result.value : []));
}

async function loadRecentFleetItemsForTransportCompanies(companies) {
  const responses = await Promise.allSettled(
    (companies || []).map(async (company) => {
      const response = await getJson(`/portal-transporte/titulares/${company.cuit}/dashboard`);
      const dashboard = response.data || {};
      return [
        ...(dashboard.unidades || []).map((item) => ({
          ...item,
          entity_kind: "unidad",
          transport_company_name: company.razon_social || `Empresa ${company.cuit}`,
          transport_company_cuit: company.cuit,
        })),
        ...(dashboard.acoplados || []).map((item) => ({
          ...item,
          entity_kind: "acoplado",
          transport_company_name: company.razon_social || `Empresa ${company.cuit}`,
          transport_company_cuit: company.cuit,
        })),
      ];
    })
  );

  return responses
    .flatMap((result) => (result.status === "fulfilled" ? result.value : []))
    .sort((left, right) => new Date(right.updated_at || right.created_at) - new Date(left.updated_at || left.created_at))
    .slice(0, 3)
    .map((item) => ({
      domain: item.dominio,
      type: item.tipo || (item.entity_kind === "acoplado" ? "Remolque" : "Unidad"),
      updated_at: item.updated_at || item.created_at,
      marca: item.marca || item.marca_vehiculo || "",
      modelo: item.modelo || item.modelo_vehiculo || "",
      anio: item.anio || item.anio_modelo || "",
      documentos: item.documentos || item.documentos_registros || [],
      transport_company_name: item.transport_company_name,
      transport_company_cuit: item.transport_company_cuit,
    }));
}

export function PortalEmpresaTransporteAuthScreen() {
  return <TransportCompanyAuthScreen />;
}

export function PortalEmpresaTransporteAppScreen() {
  const session = readTransportSession();
  const titularCuit = session?.company_cuit || window.localStorage.getItem("portalTransporteTitularCuit") || "";
  const [transportSession, setTransportSession] = useState(session);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [recentFleetItems, setRecentFleetItems] = useState([]);
  const [recentDrivers, setRecentDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversMessage, setDriversMessage] = useState("");
  const [pendingContracts, setPendingContracts] = useState([]);
  const [signedContracts, setSignedContracts] = useState([]);
  const [authorizationRequests, setAuthorizationRequests] = useState([]);
  const [unitRegistrationRequests, setUnitRegistrationRequests] = useState([]);
  const [operationDocumentRequests, setOperationDocumentRequests] = useState([]);
  const [companyCompletionForm, setCompanyCompletionForm] = useState(initialTransportCompanyCompletion);
  const [companyCompletionLoading, setCompanyCompletionLoading] = useState(false);
  const [companyCompletionMessage, setCompanyCompletionMessage] = useState("");
  const [phoneForm, setPhoneForm] = useState({
    phone_number: session?.phone_number || "",
    code: "",
  });
  const [phoneVerificationStage, setPhoneVerificationStage] = useState("idle");
  const [phoneVerificationMessage, setPhoneVerificationMessage] = useState("");
  const [phoneVerificationMockCode, setPhoneVerificationMockCode] = useState("");
  const [phoneVerificationLoading, setPhoneVerificationLoading] = useState(false);
  const [phoneVerificationPanelVisible, setPhoneVerificationPanelVisible] = useState(false);
  const [phoneVerificationResendVisible, setPhoneVerificationResendVisible] = useState(false);
  const pendingNotificationsCount =
    authorizationRequests.filter((request) => request.status === "pending_acceptance").length +
    unitRegistrationRequests.filter((request) => request.status === "pending_acceptance").length +
    operationDocumentRequests.filter((request) => request.status === "pending").length +
    pendingContracts.length;
  const hasPendingNotifications = pendingNotificationsCount > 0;

  const recentCompaniesWithValidCuit = useMemo(
    () => recentCompanies.filter((company) => isValidCuit(company?.cuit)),
    [recentCompanies]
  );

  const userCompanyCuists = useMemo(() => {
    const uniqueCuists = new Set();
    if (titularCuit) {
      uniqueCuists.add(titularCuit);
    }
    recentCompaniesWithValidCuit.forEach((company) => {
      if (company?.cuit) {
        uniqueCuists.add(company.cuit);
      }
    });
    return Array.from(uniqueCuists);
  }, [titularCuit, recentCompaniesWithValidCuit]);

  useEffect(() => {
    if (!userCompanyCuists.length) {
      setPendingContracts([]);
      setSignedContracts([]);
      setAuthorizationRequests([]);
      setUnitRegistrationRequests([]);
      setOperationDocumentRequests([]);
      return undefined;
    }
    let cancelled = false;

    async function loadPendingContracts() {
      try {
        const pendingResults = await Promise.allSettled(
          userCompanyCuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/contracts/pending`))
        );
        const signedResults = await Promise.allSettled(
          userCompanyCuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/contracts/history`))
        );
        const authorizationResults = await Promise.allSettled(
          userCompanyCuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/authorization-requests`))
        );
        const unitResults = await Promise.allSettled(
          userCompanyCuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/unit-registration-requests`))
        );
        const operationDocumentResults = await Promise.allSettled(
          userCompanyCuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/operation-document-requests`))
        );

        const flattenData = (results) =>
          results
            .filter((item) => item.status === "fulfilled")
            .flatMap((item) => item.value.data || []);

        if (!cancelled) {
          setPendingContracts(flattenData(pendingResults));
          setSignedContracts(flattenData(signedResults));
          setAuthorizationRequests(flattenData(authorizationResults));
          setUnitRegistrationRequests(flattenData(unitResults));
          setOperationDocumentRequests(flattenData(operationDocumentResults));
        }
      } catch (error) {
        if (!cancelled) {
          setPendingContracts([]);
          setAuthorizationRequests([]);
          setUnitRegistrationRequests([]);
          setOperationDocumentRequests([]);
        }
      }
    }

    loadPendingContracts();

    return () => {
      cancelled = true;
    };
  }, [userCompanyCuists]);

  useEffect(() => {
    let cancelled = false;

    async function loadCompaniesOverview() {
      try {
        const companies = await loadScopedTransportCompanies(titularCuit, session?.email);
        const latestFleetItems = await loadRecentFleetItemsForTransportCompanies(companies);
        if (!cancelled) {
          setRecentCompanies(companies.slice(0, 3));
          setRecentFleetItems(latestFleetItems);
          if (!window.localStorage.getItem("portalTransporteTitularCuit") && titularCuit) {
            window.localStorage.setItem("portalTransporteTitularCuit", titularCuit);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setRecentCompanies([]);
          setRecentFleetItems([]);
        }
      }
    }

    loadCompaniesOverview();
    return () => {
      cancelled = true;
    };
  }, [titularCuit]);

  useEffect(() => {
    if (!userCompanyCuists.length) {
      setRecentDrivers([]);
      setDriversMessage("");
      return undefined;
    }

    let cancelled = false;

    async function loadDriversPreview() {
      setDriversLoading(true);
      setDriversMessage("");
      try {
        if (!cancelled) {
          const approvedDrivers = await fetchRecentApprovedDriversForTransportCuists(userCompanyCuists, 3);
          setRecentDrivers(approvedDrivers);
        }
      } catch (error) {
        if (!cancelled) {
          setRecentDrivers([]);
          setDriversMessage(error.message);
        }
      } finally {
        if (!cancelled) {
          setDriversLoading(false);
        }
      }
    }

    loadDriversPreview();
    return () => {
      cancelled = true;
    };
  }, [userCompanyCuists]);

  async function completeCompanyProfile() {
    if (!session?.user_id) {
      setCompanyCompletionMessage("No encontramos una sesión válida para completar la empresa.");
      return;
    }

    setCompanyCompletionLoading(true);
    setCompanyCompletionMessage("");
    try {
      const response = await postJson("/portal-transporte/auth/complete-company", {
        user_id: session.user_id,
        company_cuit: companyCompletionForm.company_cuit,
        company_name: companyCompletionForm.company_name,
        company_email: companyCompletionForm.company_email || session.email,
      });
      addOwnedTransportCompany(companyCompletionForm.company_cuit);
      saveTransportSession(response.data);
      setCompanyCompletionMessage("Empresa asociada correctamente. Ingresando al portal.");
      window.setTimeout(() => {
        window.location.hash = "#/portal-empresa-transporte/app";
      }, 700);
    } catch (error) {
      setCompanyCompletionMessage(error.message);
    } finally {
      setCompanyCompletionLoading(false);
    }
  }

  useEffect(() => {
    setTransportSession(session);
    setPhoneForm((current) => ({
      ...current,
      phone_number: session?.phone_number || current.phone_number || "",
    }));
  }, [session?.phone_number, session?.email, session?.user_id]);

  async function requestTransportPhoneVerificationInApp() {
    if (!transportSession?.email) {
      setPhoneVerificationMessage("No encontramos una sesión válida para verificar el teléfono.");
      return;
    }
    if (!phoneForm.phone_number) {
      setPhoneVerificationMessage("Ingresá un teléfono para enviar el código.");
      return;
    }

    setPhoneVerificationLoading(true);
    setPhoneVerificationMessage("");
    try {
      const response = await postJson("/portal-transporte/auth/phone-verification/request", {
        email: transportSession.email,
        phone_number: phoneForm.phone_number,
      });
      setPhoneVerificationStage("pending");
      setPhoneVerificationResendVisible(false);
      setPhoneVerificationMessage(response.data?.verification_hint || "Te enviamos un código por WhatsApp.");
      setPhoneVerificationMockCode(response.data?.mock_verification_code || "");
    } catch (error) {
      setPhoneVerificationMessage(error.message);
    } finally {
      setPhoneVerificationLoading(false);
    }
  }

  async function confirmTransportPhoneVerificationInApp() {
    if (!transportSession?.email) {
      setPhoneVerificationMessage("No encontramos una sesión válida para verificar el teléfono.");
      return;
    }
    if (!phoneForm.code) {
      setPhoneVerificationMessage("Ingresá el código que llegó por WhatsApp.");
      return;
    }

    setPhoneVerificationLoading(true);
    setPhoneVerificationMessage("");
    try {
      const response = await postJson("/portal-transporte/auth/phone-verification/verify", {
        email: transportSession.email,
        phone_number: phoneForm.phone_number,
        code: phoneForm.code,
      });
      const nextSession = {
        ...(transportSession || {}),
        phone_number: response.data?.phone_number || phoneForm.phone_number,
        phone_verified: Boolean(response.data?.phone_verified),
      };
      setTransportSession(nextSession);
      saveTransportSession(nextSession);
      setPhoneForm((current) => ({ ...current, code: "", phone_number: nextSession.phone_number || current.phone_number }));
      setPhoneVerificationStage("verified");
      setPhoneVerificationMockCode("");
      setPhoneVerificationResendVisible(false);
      setPhoneVerificationMessage("Teléfono validado exitosamente.");
      setPhoneVerificationPanelVisible(false);
    } catch (error) {
      setPhoneVerificationResendVisible(true);
      setPhoneVerificationMessage(error.message);
    } finally {
      setPhoneVerificationLoading(false);
    }
  }

  if (!session) {
    return (
      <PortalLayout
        eyebrow="Portal Empresa de Transporte"
        title="Sesión requerida"
        description="Primero debes crear una cuenta o iniciar sesión."
        backHref="#/portal-empresa-transporte/auth"
      >
        <section className="detail-panel">
          <div className="portal-list pending-requests-list">
            <PortalListItem title="Sin sesión activa" description="Vuelve al alta o login del portal transporte para continuar." />
          </div>
        </section>
      </PortalLayout>
    );
  }

  if (!titularCuit) {
    return (
      <PortalLayout
        eyebrow="Portal Empresa de Transporte"
        title="Crear empresa"
        description="Antes de usar el portal, completa los datos básicos de una empresa de transporte."
        backHref="#/portal-empresa-transporte/auth"
      >
        <section className="portal-section-grid">
          <FormCard
            title="Datos básicos de la empresa"
            description="No puedes entrar al portal operativo hasta crear la empresa de transporte con sus datos mínimos."
            onSubmit={(event) => {
              event.preventDefault();
              completeCompanyProfile();
            }}
          >
            <Input
              label="CUIT empresa"
              value={companyCompletionForm.company_cuit}
              onChange={(value) => setCompanyCompletionForm((current) => ({ ...current, company_cuit: value }))}
            />
            <Input
              label="Razón social"
              value={companyCompletionForm.company_name}
              onChange={(value) => setCompanyCompletionForm((current) => ({ ...current, company_name: value }))}
            />
            <Input
              label="Email empresa"
              value={companyCompletionForm.company_email}
              onChange={(value) => setCompanyCompletionForm((current) => ({ ...current, company_email: value }))}
            />
            <div className="biometry-actions">
              <button className="submit-button" type="submit" disabled={companyCompletionLoading}>
                {companyCompletionLoading ? "Procesando..." : "Crear empresa"}
              </button>
            </div>
            <StatusMessage message={companyCompletionMessage} />
          </FormCard>
        </section>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout
      eyebrow="Portal Empresa de Transporte"
      title={(
        <>
          <span>App operativa</span>
          <span>de transporte</span>
        </>
      )}
      description="Gestiona tu flota, unidades y empresas desde este portal"
      heroClassName="transport-app-header"
      heroContentClassName="transport-app-header-left"
      titleClassName="transport-app-header-title"
      heroAsideClassName="transport-app-user-box"
      heroAside={(
        <>
          <div className="session-summary">
            <div className="session-summary-row">
              <span>Estás logueado con</span>
              <strong>{session?.email || "-"}</strong>
            </div>
          </div>
          <div className="transport-whatsapp-float">
            <div className="hero-action-row">
              <button
                className={`secondary-button transport-whatsapp-trigger ${transportSession?.phone_verified ? "hero-action-static-button" : ""}`}
                type="button"
                onClick={transportSession?.phone_verified ? undefined : () => setPhoneVerificationPanelVisible((current) => !current)}
                aria-disabled={transportSession?.phone_verified ? "true" : "false"}
              >
                <span className="hero-action-button-content">
                  <span className="whatsapp-button-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                      <path d="M19.05 4.95A9.9 9.9 0 0 0 12.02 2a10 10 0 0 0-8.67 15l-1.29 4.7 4.82-1.26A10 10 0 1 0 19.05 4.95Zm-7.03 15.4a8.3 8.3 0 0 1-4.22-1.15l-.3-.18-2.86.75.77-2.8-.2-.3a8.33 8.33 0 1 1 6.8 3.68Zm4.57-6.14c-.25-.13-1.47-.72-1.7-.8-.23-.08-.4-.13-.57.12-.17.25-.65.8-.8.96-.15.17-.3.19-.56.06-.25-.13-1.07-.39-2.04-1.24-.75-.66-1.25-1.47-1.4-1.72-.15-.25-.02-.38.11-.5.12-.12.25-.3.38-.44.13-.15.17-.25.25-.42.08-.17.04-.32-.02-.44-.06-.13-.57-1.38-.78-1.89-.2-.49-.4-.42-.57-.43h-.49c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.9 2.43 1.03 2.6.13.17 1.76 2.7 4.27 3.78.6.26 1.07.42 1.43.54.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.17-.48-.3Z" />
                    </svg>
                  </span>
                  <span className="hero-action-button-copy">
                    {transportSession?.phone_verified ? (
                      <>
                        <span className="hero-action-button-title">{transportSession?.phone_number || phoneForm.phone_number || "Número validado"}</span>
                        <span className="hero-action-button-status">Validado</span>
                      </>
                    ) : (
                      <span className="hero-action-button-title">
                        {phoneVerificationPanelVisible ? "Ocultar WhatsApp" : "WhatsApp · Validá tu número"}
                      </span>
                    )}
                  </span>
                </span>
              </button>
              {transportSession?.phone_verified ? (
                <button
                  className="secondary-button hero-action-inline-button"
                  type="button"
                  onClick={() => {
                    setPhoneVerificationStage("idle");
                    setPhoneVerificationMessage("");
                    setPhoneVerificationMockCode("");
                    setPhoneVerificationResendVisible(false);
                    setPhoneForm((current) => ({ ...current, code: "" }));
                    setPhoneVerificationPanelVisible(true);
                  }}
                >
                  Actualizar número
                </button>
              ) : null}
            </div>
          </div>
        </>
      )}
    >
      {phoneVerificationMessage && !phoneVerificationPanelVisible ? <StatusMessage message={phoneVerificationMessage} /> : null}
      {phoneVerificationPanelVisible ? (
        <section className="detail-panel transport-whatsapp-panel">
          <div className="detail-panel-header">
            <h2>WhatsApp</h2>
            <StatusBadge
              label={transportSession?.phone_verified ? "Validado" : "Pendiente"}
              tone={transportSession?.phone_verified ? "vigente" : "faltante"}
            />
          </div>
          <div className="panel-body">
            <div className="portal-list">
              <PortalListItem
                title={transportSession?.phone_verified ? (transportSession?.phone_number || phoneForm.phone_number || "Número validado") : "Validá tu número"}
                description={transportSession?.phone_verified ? "Validado" : "Asociá y validá tu número de WhatsApp."}
              />
            </div>
            <div className="inline-action-field">
              <Input
                label={phoneVerificationStage === "pending" ? "Código de WhatsApp" : "Teléfono WhatsApp"}
                value={phoneVerificationStage === "pending" ? phoneForm.code : phoneForm.phone_number}
                onChange={(value) => {
                  if (phoneVerificationStage === "pending") {
                    setPhoneForm((current) => ({ ...current, code: value }));
                    return;
                  }
                  setPhoneForm((current) => ({ ...current, phone_number: value }));
                  if (phoneVerificationStage === "verified") {
                    setPhoneVerificationStage("idle");
                  }
                }}
                autoComplete={phoneVerificationStage === "pending" ? "one-time-code" : "tel"}
                placeholder={phoneVerificationStage === "pending" ? "Ingresá el código de 6 dígitos" : "+5491123456789"}
              />
              <button
                className="submit-button inline-action-field-button"
                type="button"
                disabled={phoneVerificationLoading}
                onClick={phoneVerificationStage === "pending" ? confirmTransportPhoneVerificationInApp : requestTransportPhoneVerificationInApp}
              >
                {phoneVerificationLoading
                  ? (phoneVerificationStage === "pending" ? "Verificando..." : "Enviando...")
                  : (phoneVerificationStage === "pending" ? "Confirmar código" : "Confirmar teléfono")}
              </button>
              {phoneVerificationStage === "pending" && phoneVerificationResendVisible ? (
                <button
                  className="secondary-button"
                  type="button"
                  disabled={phoneVerificationLoading}
                  onClick={requestTransportPhoneVerificationInApp}
                >
                  Reenviar código
                </button>
              ) : null}
              <button className="secondary-button" type="button" onClick={() => setPhoneVerificationPanelVisible(false)}>
                Cerrar
              </button>
            </div>
            {phoneVerificationMockCode ? <StatusMessage message={`Código mock: ${phoneVerificationMockCode}`} /> : null}
            {phoneVerificationMessage ? <StatusMessage message={phoneVerificationMessage} /> : null}
          </div>
        </section>
      ) : null}
      <section className="portal-section-grid fleet-creation-grid">
        <section
          className={`detail-panel detail-panel-clickable ${hasPendingNotifications ? "detail-panel-solicitudes-pending" : ""}`}
          role="button"
          tabIndex={0}
          onClick={() => {
            window.location.hash = "#/portal-empresa-transporte/app/empresas";
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              window.location.hash = "#/portal-empresa-transporte/app/empresas";
            }
          }}
        >
          <div className="detail-panel-header">
            <h2>Empresas</h2>
          </div>
          <div className="portal-list linked-companies-home-list">
            {!recentCompaniesWithValidCuit.length ? (
              <PortalListItem title="Sin empresas dadas de alta" description="Abrí la tarjeta para dar de alta una empresa." />
            ) : (
              recentCompaniesWithValidCuit.map((company) => (
                <div
                  key={`transport-home-company-${company.cuit}`}
                  className="company-home-row"
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div className="company-home-row-main">
                    <PortalListItem
                      title={company.razon_social || `Empresa ${company.cuit}`}
                      description={`CUIT ${company.cuit || "-"} · Última actualización ${formatDate(company.updated_at || company.created_at)}`}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </section>

        <section
          className="detail-panel detail-panel-clickable contract-history-panel"
          role="button"
          tabIndex={0}
          onClick={() => {
            window.localStorage.setItem("portalTransporteTitularCuit", titularCuit);
            window.location.hash = "#/portal-empresa-transporte/app/flota";
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              window.localStorage.setItem("portalTransporteTitularCuit", titularCuit);
              window.location.hash = "#/portal-empresa-transporte/app/flota";
            }
          }}
        >
          <div className="detail-panel-header">
            <h2>Flota</h2>
          </div>
          <div className="portal-list linked-companies-home-list">
            {!recentFleetItems.length ? (
              <PortalListItem title="Sin unidades dadas de alta" description="Abrí la tarjeta para dar de alta la primera unidad." />
            ) : (
              recentFleetItems.map((item) => (
                <PortalListItem
                  key={`transport-home-fleet-${item.domain}`}
                  title={item.domain || "-"}
                  description={`${item.type || "Unidad"} · ${item.transport_company_name || "Empresa de transporte"} · Última actualización ${formatDate(item.updated_at)}`}
                />
              ))
            )}
          </div>
          <div className="biometry-actions">
            <button
              className="submit-button"
              type="button"
              onClick={() => {
                window.localStorage.setItem("portalTransporteTitularCuit", titularCuit);
                window.location.hash = "#/portal-empresa-transporte/app/flota";
              }}
            >
              Ir a unidades
            </button>
            <button className="secondary-button" type="button" onClick={() => { window.location.hash = "#/portal-empresa-transporte/debug"; }}>
              Carga agil de documentos
            </button>
          </div>
        </section>

        <section
          className="detail-panel detail-panel-clickable"
          role="button"
          tabIndex={0}
          onClick={() => {
            window.location.hash = "#/portal-empresa-transporte/app/solicitudes";
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              window.location.hash = "#/portal-empresa-transporte/app/solicitudes";
            }
          }}
        >
          <div className="detail-panel-header">
            <h2>Solicitudes</h2>
            <StatusBadge
              label={pendingNotificationsCount ? `${pendingNotificationsCount} pendientes` : "Sin pendientes"}
              tone={pendingNotificationsCount ? "faltante" : "vigente"}
            />
            {hasPendingNotifications ? (
              <p className="solicitudes-pending-summary">
                Tenés {pendingNotificationsCount} solicitud{pendingNotificationsCount === 1 ? "" : "es"} pendientes de revisión.
              </p>
            ) : null}
          </div>
          <div className="portal-list">
            <PortalListItem
              title="Solicitudes de vinculación"
              description={`${authorizationRequests.filter((request) => request.status === "pending_acceptance").length} pendientes.`}
            />
            <PortalListItem
              title="Solicitudes de alta de unidades"
              description={`${unitRegistrationRequests.filter((request) => request.status === "pending_acceptance").length} pendientes.`}
            />
            <PortalListItem
              title="Solicitudes de documentación"
              description={`${operationDocumentRequests.filter((request) => request.status === "pending").length} pendientes.`}
            />
          </div>
        </section>

        <section
          className="detail-panel detail-panel-clickable"
          role="button"
          tabIndex={0}
          onClick={() => {
            window.location.hash = "#/portal-empresa-transporte/app/choferes";
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              window.location.hash = "#/portal-empresa-transporte/app/choferes";
            }
          }}
        >
          <div className="detail-panel-header">
            <h2>Choferes vinculados</h2>
            <StatusBadge
              label={recentDrivers.length ? `${recentDrivers.length} recientes` : "Sin choferes"}
              tone={recentDrivers.length ? "vigente" : "faltante"}
            />
            <p className="mini-copy">
              Mostramos solamente los últimos choferes asociados; hacé clic para ingresar a la pantalla completa donde podés invitar a más y subir la documentación clave (931, seguros, psicofísico y carnet).
            </p>
          </div>
          {driversLoading ? (
            <ProcessingPanel title="Cargando choferes" message="Buscando últimos choferes vinculados." />
          ) : null}
          <div className="portal-list linked-companies-home-list">
            {!recentDrivers.length ? (
              <PortalListItem
                title="Sin choferes vinculados"
                description="Invitá choferes desde la nueva pantalla para ver más detalles."
              />
            ) : (
              recentDrivers.map((driver) => (
                <div
                  key={`transport-home-driver-${driver.chofer_cuit}`}
                  className="company-home-row"
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div className="company-home-row-main">
                    <PortalListItem
                      title={`${driver.chofer_nombre || "Chofer"} ${driver.chofer_apellido || ""}`.trim() || driver.chofer_cuit}
                      description={`CUIT ${driver.chofer_cuit || "-"} · ${driverStatusLabel(driver.status)} · Última actualización ${formatDate(
                        driver.updated_at || driver.created_at
                      )}`}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="biometry-actions">
            <button
              className="submit-button"
              type="button"
              onClick={() => {
                window.location.hash = "#/portal-empresa-transporte/app/choferes";
              }}
            >
              Gestionar choferes
            </button>
          </div>
          {driversMessage ? <p className="status-message">{driversMessage}</p> : null}
        </section>
        <section
          className="detail-panel detail-panel-clickable contract-history-panel"
          role="button"
          tabIndex={0}
          onClick={() => {
            window.location.hash = "#/portal-empresa-transporte/app/contratos-firmados";
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              window.location.hash = "#/portal-empresa-transporte/app/contratos-firmados";
            }
          }}
        >
          <div className="detail-panel-header contract-history-header">
            <div>
              <h2>Histórico firmado</h2>
              {signedContracts.length ? (
                <div className="contract-history-header-preview">
                  <PortalListItem
                    key={`signed-contract-preview-header-${signedContracts[0].id}`}
                    title={signedContracts[0].title}
                    description={`${
                      signedContracts[0].signer_name || "Sin firmante"
                    } · ${formatDate(
                      signedContracts[0].signed_at || signedContracts[0].updated_at || signedContracts[0].created_at
                    )}`}
                  />
                </div>
              ) : null}
            </div>
            <StatusBadge
              label={signedContracts.length ? `${signedContracts.length} firmados` : "Sin historial"}
              tone={signedContracts.length ? "vigente" : "faltante"}
            />
          </div>
          {signedContracts.length > 1 ? (
            <div className="contract-summary">
              <div className="portal-list">
                {signedContracts.slice(1, 3).map((contract) => (
                  <PortalListItem
                    key={`signed-contract-preview-${contract.id}`}
                    title={contract.title}
                    description={`${contract.signer_name || "Sin firmante"} · ${formatDate(
                      contract.signed_at || contract.updated_at || contract.created_at
                    )}`}
                  />
                ))}
                {signedContracts.length > 3 ? (
                  <PortalListItem
                    title={`+${signedContracts.length - 3} contratos más`}
                    description="Abrí la tarjeta para ver todo el histórico firmado."
                  />
                ) : null}
              </div>
            </div>
          ) : signedContracts.length === 0 ? (
            <div className="contract-summary-empty">
              <div className="portal-list">
                <PortalListItem title="Sin historial" description="Todavía no hay contratos firmados por esta empresa." />
              </div>
            </div>
          ) : null}
        </section>
      </section>
    </PortalLayout>
  );
}

export function PortalEmpresaTransporteLinkedCompaniesScreen() {
  const session = readTransportSession();
  const titularCuit = session?.company_cuit || window.localStorage.getItem("portalTransporteTitularCuit") || "";
  const [linkedCompanies, setLinkedCompanies] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [respondingRequestId, setRespondingRequestId] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadLinkedCompanies() {
      setLoading(true);
      setMessage("");
      try {
        const companies = await loadScopedTransportCompanies(titularCuit, session?.email);
        if (!cancelled) {
          setLinkedCompanies(
            (await loadAuthorizationRequestsForTransportCompanies(companies))
              .filter((request) => request.status === "approved")
              .sort((left, right) => new Date(right.updated_at || right.created_at) - new Date(left.updated_at || left.created_at))
          );
        }
      } catch (error) {
        if (!cancelled) {
          setLinkedCompanies([]);
          setMessage(error.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadLinkedCompanies();
    return () => {
      cancelled = true;
    };
  }, [titularCuit]);

  async function revokeLinkedCompany(requestId) {
    setRespondingRequestId(String(requestId));
    setMessage("");
    try {
      await patchJson(`/portal-transporte/authorization-requests/${requestId}`, { status: "revoked" });
      setLinkedCompanies((current) => current.filter((request) => String(request.id) !== String(requestId)));
      setMessage("Empresa desvinculada.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setRespondingRequestId("");
    }
  }

  return (
    <PortalLayout
      eyebrow="Portal Empresa de Transporte"
      title="Empresas vinculadas"
      description="Empresas consumidoras vinculadas a cualquiera de tus empresas de transporte."
      backHref="#/portal-empresa-transporte/app"
    >
      <section className="detail-panel detail-panel-wide">
        <div className="detail-panel-header">
          <h2>Vínculos activos</h2>
        </div>
        <div className="panel-body">
          <StatusMessage message={message} />
          {loading ? <ProcessingPanel title="Cargando vínculos" message="Estamos buscando empresas consumidoras vinculadas." /> : null}
          {!loading && !linkedCompanies.length ? (
            <div className="portal-list">
              <PortalListItem title="Sin empresas vinculadas" description="Todavía no hay empresas consumidoras vinculadas a tus empresas de transporte." />
            </div>
          ) : (
            <div className="contract-list">
              {linkedCompanies.map((request) => (
                <details className="detail-panel expandable-card request-card" key={`transport-linked-company-${request.id}`}>
                  <summary className="expandable-card-summary">
                    <div>
                      <h2>{request.consumer_company_name || `Empresa ${request.consumer_company_id}`}</h2>
                      <p>{request.transport_company_name || "Empresa de transporte"} · {formatDate(request.updated_at || request.created_at)}</p>
                    </div>
                    <div className="expandable-card-summary-side">
                      <StatusBadge label="Vinculada" tone="vigente" />
                      <span className="expandable-card-chevron" aria-hidden="true">
                        ▾
                      </span>
                    </div>
                  </summary>
                  <div className="expandable-card-body">
                    <div className="detail-table">
                      <div className="detail-row">
                        <span>Empresa consumidora</span>
                        <strong>{request.consumer_company_name || `Empresa ${request.consumer_company_id}`}</strong>
                      </div>
                      <div className="detail-row">
                        <span>CUIT consumidora</span>
                        <strong>{request.consumer_company_cuit || "-"}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Empresa transporte</span>
                        <strong>{request.transport_company_name || "Empresa de transporte"}</strong>
                      </div>
                      <div className="detail-row">
                        <span>CUIT transporte</span>
                        <strong>{request.transport_company_cuit || "-"}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Vinculada desde</span>
                        <strong>{formatDate(request.updated_at || request.created_at)}</strong>
                      </div>
                    </div>
                    <div className="request-card-actions">
                      <button
                        className="secondary-button"
                        type="button"
                        disabled={respondingRequestId === String(request.id)}
                        onClick={() => revokeLinkedCompany(request.id)}
                      >
                        {respondingRequestId === String(request.id) ? "Desvinculando..." : "Desvincular"}
                      </button>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      </section>
    </PortalLayout>
  );
}

export function PortalEmpresaTransporteSignedContractsScreen() {
  const session = readTransportSession();
  const titularCuit = session?.company_cuit || window.localStorage.getItem("portalTransporteTitularCuit") || "";
  const [signedContracts, setSignedContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [companyCuists, setCompanyCuists] = useState([]);

  const effectiveCompanyCuists = useMemo(() => {
    const set = new Set(companyCuists.filter((cuit) => isValidCuit(cuit)));
    if (titularCuit) {
      set.add(titularCuit);
    }
    return Array.from(set);
  }, [companyCuists, titularCuit]);

  useEffect(() => {
    let cancelled = false;

    async function loadSignedContracts() {
      if (!titularCuit) {
        setSignedContracts([]);
        return;
      }

      if (!effectiveCompanyCuists.length) {
        setSignedContracts([]);
        return;
      }

      setLoading(true);
      setMessage("");
      try {
        const signedResults = await Promise.allSettled(
          effectiveCompanyCuists.map((cuit) => getJson(`/portal-transporte/titulares/${cuit}/contracts/history`))
        );
        const flatten = (results) =>
          results
            .filter((result) => result.status === "fulfilled")
            .flatMap((result) => result.value.data || []);
        if (!cancelled) {
          setSignedContracts(flatten(signedResults));
        }
      } catch (error) {
        if (!cancelled) {
          setSignedContracts([]);
          setMessage(error.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadSignedContracts();
    return () => {
      cancelled = true;
    };
  }, [effectiveCompanyCuists]);

  useEffect(() => {
    let cancelled = false;

async function loadCompanies() {
      if (!cancelled) {
        setCompanyCuists(titularCuit ? [titularCuit] : []);
      }
    }

    loadCompanies();
    return () => {
      cancelled = true;
    };
  }, [titularCuit]);

  return (
    <PortalLayout
      eyebrow="Portal Empresa de Transporte"
      title="Contratos firmados"
      description="Histórico completo de contratos firmados para esta cuenta."
      backHref="#/portal-empresa-transporte/app"
    >
      <section className="detail-panel detail-panel-wide">
        <div className="detail-panel-header">
          <h2>Histórico firmado</h2>
        </div>
        <div className="panel-body">
          <StatusMessage message={message} />
          {loading ? <ProcessingPanel title="Cargando contratos firmados" message="Estamos buscando el histórico firmado." /> : null}
          {!loading && !signedContracts.length ? (
            <div className="portal-list">
              <PortalListItem title="Sin historial" description="Todavía no hay contratos firmados por esta empresa." />
            </div>
          ) : null}
          {signedContracts.length ? (
            <div className="contract-list">
              {signedContracts.map((contract) => (
                <details className="detail-panel expandable-card contract-card" key={`company-signed-contract-full-${contract.id}`}>
                  <summary className="expandable-card-summary">
                    <div>
                      <h2>{contract.title}</h2>
                      <p>{contract.document_name || "Contrato firmado"}</p>
                    </div>
                    <div className="expandable-card-summary-side">
                      <StatusBadge
                        label={contract.status === "signed" ? "Firmado" : "Observado"}
                        tone={contract.status === "signed" ? "vigente" : "faltante"}
                      />
                      <span className="expandable-card-chevron" aria-hidden="true">
                        ▾
                      </span>
                    </div>
                  </summary>
                  <div className="expandable-card-body">
                    <div className="detail-table">
                      <div className="detail-row">
                        <span>Firmante</span>
                        <strong>{contract.signer_name || "-"}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Rol</span>
                        <strong>{contract.signer_role || "-"}</strong>
                      </div>
                      <div className="detail-row">
                        <span>DNI</span>
                        <strong>{contract.signer_dni || "-"}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Fecha de firma</span>
                        <strong>{formatDate(contract.signed_at || contract.updated_at || contract.created_at)}</strong>
                      </div>
                      <div className="detail-row">
                        <span>Similitud biométrica</span>
                        <strong>{contract.biometric_similarity ? `${Number(contract.biometric_similarity).toFixed(2)}%` : "-"}</strong>
                      </div>
                    </div>
                    <div className="biometry-actions">
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={() => openSignedContractDocumentPopup(contract.id)}
                      >
                        Ver documento firmado
                      </button>
                    </div>
                  </div>
                </details>
              ))}
            </div>
          ) : null}
        </div>
      </section>
    </PortalLayout>
  );
}

function TransportPendingContractsSection({ titularCuit }) {
  const [pendingContracts, setPendingContracts] = useState([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [contractsMessage, setContractsMessage] = useState("");
  const [activeContractId, setActiveContractId] = useState("");
  const [companySignerForm, setCompanySignerForm] = useState({});
  const [companySignerFiles, setCompanySignerFiles] = useState({});
  const [companySignerFileStates, setCompanySignerFileStates] = useState({});
  const [signingContractId, setSigningContractId] = useState("");
  const [signingResults, setSigningResults] = useState({});
  const [signatureRequests, setSignatureRequests] = useState({});
  const [signatureDrafts, setSignatureDrafts] = useState({});
  const [applyingSignatureId, setApplyingSignatureId] = useState("");
  const [contractMessagesById, setContractMessagesById] = useState({});
  const [openingDocumentId, setOpeningDocumentId] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadPendingContracts() {
      if (!titularCuit) {
        setPendingContracts([]);
        return;
      }

      setContractsLoading(true);
      setContractsMessage("");
      try {
        const response = await getJson(`/portal-transporte/titulares/${titularCuit}/contracts/pending`);
        if (!cancelled) {
          setPendingContracts(response.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          setPendingContracts([]);
          setContractsMessage(error.message);
        }
      } finally {
        if (!cancelled) {
          setContractsLoading(false);
        }
      }
    }

    loadPendingContracts();
    return () => {
      cancelled = true;
    };
  }, [titularCuit]);

  async function refreshPendingContracts() {
    if (!titularCuit) {
      setPendingContracts([]);
      return;
    }
    const response = await getJson(`/portal-transporte/titulares/${titularCuit}/contracts/pending`);
    setPendingContracts(response.data || []);
  }

  async function loadSignatureRequest(documentId) {
    const response = await getJson(`/signatures/documents/${documentId}/signature-request?viewer_type=titular&viewer_id=${titularCuit}`);
    setCompanySignerForm((current) => {
      const existing = current[documentId] || {};
      if (existing.signer_name || existing.signer_dni) {
        return current;
      }
      return {
        ...current,
        [documentId]: {
          signer_name: "",
          signer_dni: "",
        },
      };
    });
    setSignatureRequests((current) => ({
      ...current,
      [documentId]: response.data,
    }));
  }

  async function processSignerDniFront(contractId, file) {
    setSigningResults((current) => {
      const next = { ...current };
      delete next[contractId];
      return next;
    });
    setCompanySignerFileStates((current) => ({
      ...current,
      [contractId]: {
        ...(current[contractId] || {}),
        dni_frente: { status: "processing", message: "Procesando" },
      },
    }));
    setCompanySignerFiles((current) => ({
      ...current,
      [contractId]: {
        ...(current[contractId] || {}),
        dni_frente: file,
      },
    }));
    setContractMessagesById((current) => ({ ...current, [contractId]: "" }));

    try {
      const response = await postForm(
        "/identidad/registro-dni",
        { lado: "frente" },
        { archivo: file },
        {
          timeoutMs: 120000,
          timeoutMessage: "La lectura del DNI está tardando más de lo esperado.",
        }
      );

      const extractedDni = response.data?.datos_extraidos?.nro_dni || "";
      const extractedName = [response.data?.datos_extraidos?.nombre, response.data?.datos_extraidos?.apellido]
        .filter(Boolean)
        .join(" ")
        .trim();
      const trustedExtractedName = looksLikeHumanPersonName(extractedName) ? extractedName : "";
      if (!response.data?.is_dni_argentina || !extractedDni || !trustedExtractedName) {
        throw new Error("No pudimos completar nombre y DNI del firmante desde la foto del frente.");
      }

      setCompanySignerForm((current) => ({
        ...current,
        [contractId]: {
          ...(current[contractId] || {}),
          signer_name: trustedExtractedName,
          signer_dni: extractedDni,
        },
      }));
      setCompanySignerFileStates((current) => ({
        ...current,
        [contractId]: {
          ...(current[contractId] || {}),
          dni_frente: { status: "processed", message: "Procesada" },
        },
      }));
    } catch (error) {
      setCompanySignerForm((current) => ({
        ...current,
        [contractId]: {
          ...(current[contractId] || {}),
          signer_name: "",
          signer_dni: "",
        },
      }));
      setCompanySignerFileStates((current) => ({
        ...current,
        [contractId]: {
          ...(current[contractId] || {}),
          dni_frente: { status: "error", message: error.message || "No pudimos procesar el DNI." },
        },
      }));
      setContractMessagesById((current) => ({ ...current, [contractId]: error.message || "No pudimos procesar el DNI." }));
    }
  }

  function processSignerSelfie(contractId, file) {
    setSigningResults((current) => {
      const next = { ...current };
      delete next[contractId];
      return next;
    });
    setCompanySignerFileStates((current) => ({
      ...current,
      [contractId]: {
        ...(current[contractId] || {}),
        selfie: { status: "processing", message: "Procesando" },
      },
    }));
    setCompanySignerFiles((current) => ({
      ...current,
      [contractId]: {
        ...(current[contractId] || {}),
        selfie: file,
      },
    }));
    window.setTimeout(() => {
      setCompanySignerFileStates((current) => ({
        ...current,
        [contractId]: {
          ...(current[contractId] || {}),
          selfie: { status: "processed", message: "Procesada" },
        },
      }));
    }, 150);
  }

  async function openContractDocument(documentId) {
    setOpeningDocumentId(String(documentId));
    const popup = window.open("about:blank", "_blank");
    if (popup) {
      popup.opener = null;
    }
    try {
      const response = await getJson(`/signatures/documents/${documentId}/url`);
      if (popup) {
        popup.location.href = response.data.url;
      }
    } catch (error) {
      if (popup) {
        popup.document.open();
        popup.document.write(buildDocumentErrorHtml(error?.message || "No pudimos abrir el archivo."));
        popup.document.close();
      }
      setContractsMessage(error.message);
    } finally {
      setOpeningDocumentId("");
    }
  }

  async function signPendingContract(contractId) {
    const form = companySignerForm[contractId] || {};
    const files = companySignerFiles[contractId] || {};

    if (!files.dni_frente || !files.selfie) {
      setContractsMessage("Completa DNI frente y selfie para validar biometría.");
      return;
    }

    setSigningContractId(String(contractId));
    setContractsMessage("");
    setContractMessagesById((current) => ({ ...current, [contractId]: "" }));

    try {
      const response = await postForm(
        `/portal-transporte/contracts/${contractId}/sign`,
        {
          signer_name: form.signer_name || "",
          signer_dni: form.signer_dni || "",
        },
        {
          dni_frente: files.dni_frente,
          selfie: files.selfie,
        },
        {
          timeoutMs: 120000,
          timeoutMessage: "La validación biométrica está tardando más de lo esperado. Reintenta en unos segundos.",
        }
      );

      setSigningResults((current) => ({
        ...current,
        [contractId]: response.data,
      }));
      if (response.data?.signer_identity?.name || response.data?.signer_identity?.dni) {
        setCompanySignerForm((current) => ({
          ...current,
          [contractId]: {
            ...(current[contractId] || {}),
            signer_name: response.data?.signer_identity?.name || current[contractId]?.signer_name || "",
            signer_dni: response.data?.signer_identity?.dni || current[contractId]?.signer_dni || "",
          },
        }));
      }
      if (response.data?.biometric_validation_id) {
        await refreshPendingContracts();
        await loadSignatureRequest(contractId);
      }
      const nextMessage = response.data?.signer_issue?.message
        || (response.data?.biometric?.validado
          ? "Biometría aprobada. Ahora firma el documento."
          : "La biometría quedó observada. Reintenta la validación.");
      setContractMessagesById((current) => ({
        ...current,
        [contractId]: nextMessage,
      }));
      setContractsMessage(nextMessage);
    } catch (error) {
      setContractMessagesById((current) => ({ ...current, [contractId]: error.message }));
      setContractsMessage(error.message);
    } finally {
      setSigningContractId("");
    }
  }

  async function applyContractSignature(contractId) {
    const signatureDraft = signatureDrafts[contractId];
    const biometricResult = signingResults[contractId];
    const signatureRequest = signatureRequests[contractId];
    const placeholder = signatureRequest?.placeholders?.find((item) => item.status === "pending");
    const signerForm = companySignerForm[contractId] || {};

    if (!signatureDraft || !placeholder || !biometricResult?.biometric_validation_id) {
      setContractMessagesById((current) => ({
        ...current,
        [contractId]: "Primero valida biometría y confirma la firma manuscrita.",
      }));
      setContractsMessage("Primero valida biometría y confirma la firma manuscrita.");
      return;
    }

    setApplyingSignatureId(String(contractId));
    setContractsMessage("");
    setContractMessagesById((current) => ({ ...current, [contractId]: "Aplicando firma al PDF..." }));

    try {
      const response = await postJson(
        `/signatures/documents/${contractId}/sign`,
        {
          placeholder_id: placeholder.id,
          signature_image_base64: signatureDraft.signature_image_base64,
          biometric_validation_id: biometricResult.biometric_validation_id,
          viewer_type: "titular",
          viewer_id: titularCuit,
          signer_name: signerForm.signer_name || "",
          add_visible_metadata: true,
        },
        {
          timeoutMs: 120000,
          timeoutMessage: "La firma visual está tardando más de lo esperado.",
        }
      );

      setSignatureDrafts((current) => {
        const next = { ...current };
        delete next[contractId];
        return next;
      });
      setSigningResults((current) => {
        const next = { ...current };
        delete next[contractId];
        return next;
      });
      await refreshPendingContracts();
      await loadSignatureRequest(contractId).catch(() => null);
      setContractMessagesById((current) => ({
        ...current,
        [contractId]: `Documento firmado. Estado final: ${humanizeLabel(response.data.status)}.`,
      }));
      setContractsMessage(`Documento firmado. Estado final: ${humanizeLabel(response.data.status)}.`);
    } catch (error) {
      setContractMessagesById((current) => ({ ...current, [contractId]: error.message }));
      setContractsMessage(error.message);
    } finally {
      setApplyingSignatureId("");
    }
  }

  return (
    <section className="detail-panel">
      <div className="detail-panel-header">
        <h2>Solicitudes de firma de contrato</h2>
      </div>
      <div className="panel-body">
        <StatusMessage message={contractsMessage} />
        {contractsLoading ? <ProcessingPanel title="Cargando contratos" message="Estamos buscando documentos pendientes de firma." /> : null}
        {!contractsLoading && !pendingContracts.length ? (
          <div className="portal-list">
            <PortalListItem title="Sin pendientes" description="No hay contratos pendientes para esta empresa de transporte." />
          </div>
        ) : null}
        <div className="contract-list">
          {pendingContracts.map((contract) => {
            const isActive = activeContractId === String(contract.id);
            const signerForm = companySignerForm[contract.id] || { signer_name: "", signer_dni: "" };
            const signingResult = signingResults[contract.id];
            const contractMessage = contractMessagesById[contract.id];
            const signatureRequest = signatureRequests[contract.id];
            const signingContext = signatureRequest?.signing_context || {};
            const availableSigners = Array.isArray(signatureRequest?.available_signers) ? signatureRequest.available_signers : [];
            const fileStates = companySignerFileStates[contract.id] || {};
            const dniFrenteState = fileStates.dni_frente || { status: "pending", message: "Pendiente" };
            const selfieState = fileStates.selfie || { status: "pending", message: "Pendiente" };
            const photosReady = dniFrenteState.status === "processed" && selfieState.status === "processed";
            const canStartBiometricFlow = signingContext.is_autonomous || availableSigners.length > 0;
            const biometricApproved = Boolean(signingResult?.biometric?.validado);
            const canContinueWithSignature = Boolean(signingResult?.biometric_validation_id);
            const canRetakePhotos = Boolean(signingResult && !signingResult?.biometric?.validado);

            return (
              <details className="detail-panel expandable-card contract-card" key={`pending-company-contract-${contract.id}`} open={isActive}>
                <summary
                  className="expandable-card-summary"
                  onClick={(event) => {
                    event.preventDefault();
                    const nextId = activeContractId === String(contract.id) ? "" : String(contract.id);
                    setActiveContractId(nextId);
                    if (nextId) {
                      loadSignatureRequest(contract.id).catch((error) => setContractsMessage(error.message));
                    }
                  }}
                >
                  <div>
                    <h2>{contract.title}</h2>
                    <p>{contract.document_name || "Contrato pendiente"}</p>
                  </div>
                  <div className="expandable-card-summary-side">
                    <StatusBadge label="Pendiente" tone="faltante" />
                    <span className="expandable-card-chevron" aria-hidden="true">
                      ▾
                    </span>
                  </div>
                </summary>
                <div className="expandable-card-body">
                  <div className="detail-table">
                    <div className="detail-row">
                      <span>Empresa destino</span>
                      <strong>{contract.target_id}</strong>
                    </div>
                    <div className="detail-row">
                      <span>Fecha de alta</span>
                      <strong>{formatDate(contract.created_at)}</strong>
                    </div>
                  </div>
                  <div className="biometry-actions">
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => openContractDocument(contract.id)}
                      disabled={openingDocumentId === String(contract.id)}
                    >
                      {openingDocumentId === String(contract.id) ? "Abriendo..." : "Ver contrato"}
                    </button>
                  </div>
                  <div className="contract-sign-capture-grid">
                    <section className="detail-panel">
                      <div className="detail-panel-header">
                        <h2>DNI frente</h2>
                      </div>
                      <div className="panel-body">
                        <CameraCapture
                          mode="documento"
                          title="DNI frente"
                          description="Captura el frente del DNI del firmante."
                          completed={dniFrenteState.status === "processed" && !canRetakePhotos}
                          processing={dniFrenteState.status === "processing"}
                          resultMessage={dniFrenteState.status === "error" ? dniFrenteState.message : ""}
                          resultError={dniFrenteState.status === "error"}
                          onConfirm={(file) => processSignerDniFront(contract.id, file)}
                        />
                        <StatusBadge
                          label={dniFrenteState.message}
                          tone={dniFrenteState.status === "processed" ? "vigente" : dniFrenteState.status === "error" ? "vencido" : "faltante"}
                        />
                      </div>
                    </section>
                    <section className="detail-panel">
                      <div className="detail-panel-header">
                        <h2>Selfie</h2>
                      </div>
                      <div className="panel-body">
                        <CameraCapture
                          mode="selfie"
                          title="Selfie"
                          description="Captura una selfie del firmante para validar biometría."
                          completed={selfieState.status === "processed" && !canRetakePhotos}
                          processing={selfieState.status === "processing"}
                          resultMessage={selfieState.status === "error" ? selfieState.message : ""}
                          resultError={selfieState.status === "error"}
                          onConfirm={(file) => processSignerSelfie(contract.id, file)}
                        />
                        <StatusBadge
                          label={selfieState.message}
                          tone={selfieState.status === "processed" ? "vigente" : selfieState.status === "error" ? "vencido" : "faltante"}
                        />
                      </div>
                    </section>
                  </div>
                  <label className="field">
                    <span>Nombre del firmante</span>
                    <input value={signerForm.signer_name || ""} readOnly />
                  </label>
                  <label className="field">
                    <span>DNI del firmante</span>
                    <input value={signerForm.signer_dni || ""} readOnly />
                  </label>
                  {!canStartBiometricFlow ? (
                    <StatusMessage message="No tiene firmantes habilitados. Da de alta la designación de autoridades o un poder válido." />
                  ) : null}
                  {!biometricApproved ? (
                    <div className="biometry-actions">
                      <button
                        className="submit-button"
                        type="button"
                        onClick={() => signPendingContract(contract.id)}
                        disabled={signingContractId === String(contract.id) || !photosReady}
                      >
                        {signingContractId === String(contract.id) ? "Validando..." : "Validar biometría"}
                      </button>
                    </div>
                  ) : null}
                  {signingContractId === String(contract.id) ? (
                    <ProcessingPanel title="Validando biometría" message="Estamos validando identidad y biometría del firmante." />
                  ) : null}
                  {signingResult ? <ContractSignatureResultCard result={signingResult} defaultOpen={!biometricApproved} /> : null}
                  {contractMessage && !signingResult?.biometric?.validado ? <StatusMessage message={contractMessage} /> : null}
                  {signingResult?.biometric?.validado ? (
                    <section className="detail-panel detail-panel-wide">
                      <div className="detail-panel-header">
                        <h2>Firma manuscrita</h2>
                      </div>
                      <div className="panel-body">
                        <SignaturePad
                          title="Firma manuscrita"
                          description="Dibuja la firma del firmante dentro del recuadro."
                          onConfirm={(signature) =>
                            setSignatureDrafts((current) => ({
                              ...current,
                              [contract.id]: signature,
                            }))
                          }
                        />
                        <div className="biometry-actions">
                          <button
                            className="submit-button"
                            type="button"
                            onClick={() => applyContractSignature(contract.id)}
                            disabled={applyingSignatureId === String(contract.id) || !signatureDrafts[contract.id] || !canContinueWithSignature}
                          >
                            {applyingSignatureId === String(contract.id) ? "Firmando..." : "Firmar contrato"}
                          </button>
                        </div>
                        {applyingSignatureId === String(contract.id) ? (
                          <ProcessingPanel title="Aplicando firma al PDF" message="Estamos generando el PDF firmado." />
                        ) : null}
                        {contractMessage ? <StatusMessage message={contractMessage} /> : null}
                      </div>
                    </section>
                  ) : null}
                </div>
              </details>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function PortalEmpresaTransporteRequestsScreen() {
  const session = readTransportSession();
  const titularCuit = session?.company_cuit || window.localStorage.getItem("portalTransporteTitularCuit") || "";
  const [authorizationRequests, setAuthorizationRequests] = useState([]);
  const [unitRegistrationRequests, setUnitRegistrationRequests] = useState([]);
  const [operationDocumentRequests, setOperationDocumentRequests] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [respondingAuthorizationId, setRespondingAuthorizationId] = useState("");
  const [respondingUnitRequestId, setRespondingUnitRequestId] = useState("");
  useEffect(() => {
    let cancelled = false;

    async function loadRequests() {
      if (!titularCuit) {
        setAuthorizationRequests([]);
        setUnitRegistrationRequests([]);
        setOperationDocumentRequests([]);
        return;
      }

      setLoading(true);
      try {
        const cuists = await buildTransportCompanyCuists(titularCuit);
        if (!cuists.length) {
          if (!cancelled) {
            setAuthorizationRequests([]);
            setUnitRegistrationRequests([]);
            setOperationDocumentRequests([]);
          }
          return;
        }

        const { authorizationRequests: authRequests, unitRequests, operationDocumentRequests: docRequests } =
          await fetchRequestsForTransportCuists(cuists);

        if (cancelled) {
          return;
        }

        setAuthorizationRequests(authRequests);
        setUnitRegistrationRequests(unitRequests);
        setOperationDocumentRequests(docRequests);
      } catch (error) {
        if (!cancelled) {
          setAuthorizationRequests([]);
          setUnitRegistrationRequests([]);
          setOperationDocumentRequests([]);
          setMessage(error.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadRequests();
    return () => {
      cancelled = true;
    };
  }, [titularCuit]);

  async function refreshRequests() {
    if (!titularCuit) {
      setAuthorizationRequests([]);
      setUnitRegistrationRequests([]);
      setOperationDocumentRequests([]);
      return;
    }

    const cuists = await buildTransportCompanyCuists(titularCuit);
    if (!cuists.length) {
      setAuthorizationRequests([]);
      setUnitRegistrationRequests([]);
      setOperationDocumentRequests([]);
      return;
    }

    const { authorizationRequests: authRequests, unitRequests, operationDocumentRequests: docRequests } =
      await fetchRequestsForTransportCuists(cuists);
    setAuthorizationRequests(authRequests);
    setUnitRegistrationRequests(unitRequests);
    setOperationDocumentRequests(docRequests);
  }

  async function respondAuthorizationRequest(requestId, status) {
    setRespondingAuthorizationId(String(requestId));
    setMessage("");
    try {
      await patchJson(`/portal-transporte/authorization-requests/${requestId}`, { status });
      await refreshRequests();
      setMessage(status === "approved" ? "Vinculación aceptada." : "Vinculación rechazada.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setRespondingAuthorizationId("");
    }
  }

  async function respondUnitRegistrationRequest(requestId, status) {
    setRespondingUnitRequestId(String(requestId));
    setMessage("");
    try {
      await patchJson(`/portal-transporte/unit-registration-requests/${requestId}`, { status });
      await refreshRequests();
      setMessage(status === "rejected" ? "Solicitud rechazada." : "Solicitud actualizada.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setRespondingUnitRequestId("");
    }
  }

  function openUnitRegistrationFlow(request) {
    window.localStorage.setItem(UNIT_REGISTRATION_REQUEST_STORAGE_KEY, JSON.stringify(request));
    window.localStorage.setItem("portalTransporteTitularCuit", titularCuit);
    window.location.hash = "#/portal-empresa-transporte/app/flota";
  }

  const pendingAuthorizationRequests = authorizationRequests.filter((request) => request.status === "pending_acceptance");
  const pendingUnitRequests = unitRegistrationRequests.filter((request) => request.status === "pending_acceptance");
  const pendingOperationDocumentRequests = operationDocumentRequests.filter((request) => request.status === "pending");

  return (
    <PortalLayout
      eyebrow="Portal Empresa de Transporte"
      title="Solicitudes"
      description="Pendientes de vinculación, alta de unidades y firma de contratos."
      backHref="#/portal-empresa-transporte/app"
    >
      <StatusMessage message={message} />
      {loading ? <ProcessingPanel title="Cargando solicitudes" message="Estamos buscando pendientes para esta empresa." /> : null}

      <section className="portal-section-grid">
        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>Solicitudes de vinculación</h2>
          </div>
          <div className="panel-body">
            {!pendingAuthorizationRequests.length ? (
              <div className="portal-list">
                <PortalListItem title="Sin solicitudes" description="No hay nuevas solicitudes de vinculación para esta empresa de transporte." />
              </div>
            ) : (
              <div className="portal-list">
                {pendingAuthorizationRequests.map((request) => (
                  <div className="request-card" key={`transport-authorization-request-${request.id}`}>
                    <PortalListItem
                      title={request.consumer_company_name || `Empresa ${request.consumer_company_id}`}
                      description={`CUIT ${request.consumer_company_cuit || "-"} · Solicitado ${formatDate(request.created_at)}`}
                    />
                    <p className="mini-copy">
                      La vinculación habilita operar con esta contraparte. La empresa consumidora solo podrá controlar y persistir documentación dentro de una operación concreta.
                    </p>
                    <div className="biometry-actions">
                      <button
                        className="submit-button"
                        type="button"
                        disabled={respondingAuthorizationId === String(request.id)}
                        onClick={() => respondAuthorizationRequest(request.id, "approved")}
                      >
                        {respondingAuthorizationId === String(request.id) ? "Procesando..." : "Aceptar vinculación"}
                      </button>
                      <button
                        className="secondary-button"
                        type="button"
                        disabled={respondingAuthorizationId === String(request.id)}
                        onClick={() => respondAuthorizationRequest(request.id, "access_denied")}
                      >
                        Rechazar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>Solicitudes de alta de unidades</h2>
          </div>
          <div className="panel-body">
            {!pendingUnitRequests.length ? (
              <div className="portal-list">
                <PortalListItem title="Sin solicitudes" description="No hay nuevas solicitudes de alta de unidades para esta empresa de transporte." />
              </div>
            ) : (
              <div className="portal-list">
                {pendingUnitRequests.map((request) => (
                  <div className="request-card" key={`transport-unit-request-${request.id}`}>
                    <PortalListItem
                      title={`${getUnitRegistrationLabel(request.unit_type)} ${request.domain}`}
                      description={`Solicitado ${formatDate(request.created_at)}${request.request_kind === "document" ? ` · Documento ${getUnitDocumentLabel(request.document_type)}` : ""}`}
                    />
                    <div className="biometry-actions">
                      <button
                        className="submit-button"
                        type="button"
                        onClick={() => openUnitRegistrationFlow(request)}
                      >
                        {request.request_kind === "document" ? "Cargar documento" : "Dar de alta"}
                      </button>
                      <button
                        className="secondary-button"
                        type="button"
                        disabled={respondingUnitRequestId === String(request.id)}
                        onClick={() => respondUnitRegistrationRequest(request.id, "rejected")}
                      >
                        {respondingUnitRequestId === String(request.id) ? "Procesando..." : "Rechazar"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>Solicitudes de documentación</h2>
          </div>
          <div className="panel-body">
            {!pendingOperationDocumentRequests.length ? (
              <div className="portal-list">
                <PortalListItem title="Sin solicitudes" description="No hay pedidos de documentos pendientes para esta empresa de transporte." />
              </div>
            ) : (
              <div className="portal-list">
                {pendingOperationDocumentRequests.map((request) => (
                  <div className="request-card" key={`transport-operation-document-request-${request.id}`}>
                    <PortalListItem
                      title={getOperationDocumentRequestLabel(request)}
                      description={`Operación ${request.operation_id} · Solicitado ${formatDate(request.created_at)}`}
                    />
                    <p className="mini-copy">
                      {request.request_reason || "Cargá este documento para hacer efectiva la operación."}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
        <TransportPendingContractsSection titularCuit={titularCuit} />
      </section>
    </PortalLayout>
  );
}

export function PortalEmpresaTransporteChoferesScreen() {
  const documentPolicyCatalog = usePortalDocumentPolicyCatalog();
  const session = readTransportSession();
  const titularCuit = session?.company_cuit || window.localStorage.getItem("portalTransporteTitularCuit") || "";
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [availableCompaniesLoading, setAvailableCompaniesLoading] = useState(false);
  const [invitationCompanyCuit, setInvitationCompanyCuit] = useState(titularCuit);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversError, setDriversError] = useState("");
  const [driversVersion, setDriversVersion] = useState(0);
  const [selectedDriverCuit, setSelectedDriverCuit] = useState("");
  const [driverDocuments, setDriverDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsMessage, setDocumentsMessage] = useState("");
  const [documentsVersion, setDocumentsVersion] = useState(0);
  const [documentUploadStates, setDocumentUploadStates] = useState({});
  const [driverDocChoice, setDriverDocChoice] = useState({});
  const [invitationInput, setInvitationInput] = useState("");
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationMessage, setInvitationMessage] = useState("");
  const [invitationResults, setInvitationResults] = useState([]);
  const invitationStatusLabel = (status) => {
    if (status === "invited") {
      return "enviada";
    }
    if (status === "missing") {
      return "chofer NO dado de alta";
    }
    if (status === "invalid") {
      return "CUIT inválido";
    }
    if (status === "duplicate") {
      return "CUIT duplicado";
    }
    if (status === "error") {
      return "error en la invitación";
    }
    return status || "estado desconocido";
  };

  const invitationSummaryLines = useMemo(() => {
    if (!invitationResults.length) {
      return [];
    }
    const counts = invitationResults.reduce((acc, result) => {
      const normalizedStatus = (result.status || "unknown").toLowerCase();
      acc[normalizedStatus] = (acc[normalizedStatus] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([status, amount]) => `${invitationStatusLabel(status)}: ${amount}`);
  }, [invitationResults]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingRequestsLoading, setPendingRequestsLoading] = useState(false);
  const [pendingRequestsMessage, setPendingRequestsMessage] = useState("");
  const [pendingRequestsVersion, setPendingRequestsVersion] = useState(0);

  const driverDocumentDefinitions = useMemo(
    () =>
      buildTransportDriverDocumentDefinitions(documentPolicyCatalog).filter(
        (definition) => !isRestrictedChoferIdentityDocumentForTransport(definition.tipo)
      ),
    [documentPolicyCatalog]
  );
  const driverPhotoDefinitions = [];

  const describeDriverName = (driverDetail = {}) => {
    const firstName = driverDetail.nombre || driverDetail.chofer_nombre || "";
    const lastName = driverDetail.apellido || driverDetail.chofer_apellido || "";
    return `${firstName} ${lastName}`.trim();
  };

  const buildInviteSuccessMessage = (driverName, cuit) => {
    const label = driverName ? `${driverName} (CUIT ${cuit})` : `CUIT ${cuit}`;
    return `Se invitó a ${label} de forma satisfactoria.`;
  };

  const buildInvitationStatusMessage = (results = []) => {
    if (!results.length) {
      return "No se procesaron CUIT válidos.";
    }
    const invitedCount = results.filter((item) => item.status === "invited").length;
    if (invitedCount) {
      return invitedCount === 1 ? "Se envió una invitación." : `Se enviaron ${invitedCount} invitaciones.`;
    }
    if (results.some((item) => ["invalid", "duplicate"].includes(item.status))) {
      return "Revisá los CUIT inválidos antes de reintentar.";
    }
    if (results.some((item) => item.status === "missing")) {
      return "Algunos choferes no están dados de alta.";
    }
    if (results.some((item) => item.status === "error")) {
      return "No pudimos procesar los CUIT ingresados.";
    }
    return "No se procesaron los CUIT solicitados.";
  };

  useEffect(() => {
    let cancelled = false;

    async function loadAvailableCompanies() {
      if (!session?.email && !titularCuit) {
        if (!cancelled) {
          setAvailableCompanies([]);
        }
        return;
      }

      setAvailableCompaniesLoading(true);
      try {
        const companies = await loadScopedTransportCompanies(titularCuit, session?.email);
        if (cancelled) {
          return;
        }
        setAvailableCompanies(companies);
        setInvitationCompanyCuit((current) => {
          if (current && companies.some((company) => company.cuit === current)) {
            return current;
          }
          if (titularCuit && companies.some((company) => company.cuit === titularCuit)) {
            return titularCuit;
          }
          return companies[0]?.cuit || "";
        });
      } catch (_error) {
        if (!cancelled) {
          setAvailableCompanies([]);
          setInvitationCompanyCuit(titularCuit || "");
        }
      } finally {
        if (!cancelled) {
          setAvailableCompaniesLoading(false);
        }
      }
    }

    loadAvailableCompanies();
    return () => {
      cancelled = true;
    };
  }, [session?.email, titularCuit, driversVersion]);

  useEffect(() => {
    if (!titularCuit) {
      setDrivers([]);
      setDriversError("");
      setSelectedDriverCuit("");
      return undefined;
    }

    let cancelled = false;
    async function loadChoferes() {
      setDriversLoading(true);
      setDriversError("");
      try {
        const response = await getJson(`/portal-transporte/titulares/${titularCuit}/choferes?limit=25`);
        if (cancelled) {
          return;
        }
        const resolved = response.data || [];
        setDrivers(resolved);
        const firstApproved = resolved.find((item) => item.status === "approved");
        setSelectedDriverCuit((current) => current || (firstApproved?.chofer_cuit || ""));
      } catch (error) {
        if (!cancelled) {
          setDrivers([]);
          setDriversError(error.message);
          setSelectedDriverCuit("");
        }
      } finally {
        if (!cancelled) {
          setDriversLoading(false);
        }
      }
    }

    loadChoferes();
    return () => {
      cancelled = true;
    };
  }, [titularCuit, driversVersion]);

  useEffect(() => {
    if (!titularCuit) {
      setPendingRequests([]);
      setPendingRequestsMessage("");
      return undefined;
    }

    let cancelled = false;
    async function loadPendingRequests() {
      setPendingRequestsLoading(true);
      setPendingRequestsMessage("");
      try {
        const response = await getJson(`/portal-transporte/titulares/${titularCuit}/choferes/solicitudes`);
        if (cancelled) {
          return;
        }
      const rawRequests = response.data || [];
      const freshRequests = rawRequests.filter((request) => {
        const createdAt = request.created_at || request.updated_at;
        if (!createdAt) {
          return true;
        }
        const ageMs = Date.now() - new Date(createdAt).getTime();
        return ageMs <= 72 * 60 * 60 * 1000;
      });
      setPendingRequests(freshRequests);
      } catch (error) {
        if (!cancelled) {
          setPendingRequests([]);
          setPendingRequestsMessage(error.message);
        }
      } finally {
        if (!cancelled) {
          setPendingRequestsLoading(false);
        }
      }
    }

    loadPendingRequests();
    return () => {
      cancelled = true;
    };
  }, [titularCuit, pendingRequestsVersion]);

  useEffect(() => {
    if (!selectedDriverCuit) {
      setDriverDocuments([]);
      setDocumentsMessage("");
      return undefined;
    }

    let cancelled = false;
    async function loadDocuments() {
      setDocumentsLoading(true);
      setDocumentsMessage("");
      try {
        const response = await getJson(`/documentos?entidad_tipo=chofer&entidad_id=${selectedDriverCuit}`);
        if (cancelled) {
          return;
        }
        setDriverDocuments(response.data || []);
      } catch (error) {
        if (!cancelled) {
          setDriverDocuments([]);
          setDocumentsMessage(error.message);
        }
      } finally {
        if (!cancelled) {
          setDocumentsLoading(false);
        }
      }
    }

    loadDocuments();
    return () => {
      cancelled = true;
    };
  }, [selectedDriverCuit, documentsVersion]);

  const latestDocuments = getLatestDocumentsByType(driverDocuments);
  const approvedDrivers = drivers.filter((driver) => driver.status === "approved");

  const driverStatusLabel = (status) => {
    if (status === "approved") {
      return "Vinculado";
    }
    if (status === "pending") {
      return "Pendiente";
    }
    if (status === "revoked") {
      return "Revocado";
    }
    if (status === "denied") {
      return "Denegado";
    }
    return status || "Sin estado";
  };

  const requestStatusTone = (status) => {
    if (status === "pending") {
      return "faltante";
    }
    if (status === "revoked" || status === "denied") {
      return "alerta";
    }
    return "vigente";
  };

  const handleInviteSubmit = async () => {
    const effectiveInvitationCompanyCuit = invitationCompanyCuit || titularCuit;
    const rawValues = String(invitationInput || "")
      .split(/[,;\\n]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (!effectiveInvitationCompanyCuit) {
      setInvitationMessage("Seleccioná la empresa de transporte desde la que querés invitar.");
      return;
    }
    if (!rawValues.length) {
      setInvitationMessage("Ingresá al menos un CUIT separado por comas o saltos de línea.");
      return;
    }

    setInvitationLoading(true);
    setInvitationMessage("");
    setInvitationResults([]);

    try {
      const results = await inviteChoferes(rawValues, effectiveInvitationCompanyCuit);
      setInvitationResults(results);
      setInvitationMessage(buildInvitationStatusMessage(results));
      if (results.some((item) => item.status === "invited")) {
        setDriversVersion((current) => current + 1);
        const invited = results.find((item) => item.status === "invited");
        if (invited) {
          setSelectedDriverCuit(invited.cuit);
        }
        const invitedRequests = results
          .filter((item) => item.status === "invited")
          .map((item, index) => ({
            id: `inline-${item.cuit}-${Date.now()}-${index}`,
            chofer_cuit: item.cuit,
            status: "pending",
            chofer_nombre: item.name || null,
            chofer_apellido: item.name ? "" : null,
            created_at: new Date().toISOString(),
          }));
        if (invitedRequests.length) {
          setPendingRequests((current) => [...invitedRequests, ...current]);
        }
      }
      setPendingRequestsVersion((current) => current + 1);
      if (results.length > 5) {
        downloadInvitationResults(results, "masiva");
      }
    } catch (error) {
      setInvitationMessage(error.message || "No pudimos procesar las invitaciones.");
    } finally {
      setInvitationLoading(false);
      setInvitationInput("");
    }
  };

  const handleDocumentUpload = async (tipo, event, driverCuitParam) => {
    const file = event.target.files?.[0];
    const targetDriver = driverCuitParam || selectedDriverCuit;
    if (!file || !targetDriver) {
      event.target.value = "";
      return;
    }
    event.target.value = "";
    setDocumentUploadStates((current) => ({
      ...current,
      [targetDriver]: {
        ...(current[targetDriver] || {}),
        [tipo]: { stage: "processing" },
      },
    }));
    try {
      await postForm(
        "/documentos",
        {
          entidad_tipo: "chofer",
          entidad_id: targetDriver,
          tipo_documento: tipo,
          estado: "observado",
        },
        { archivo: file },
        { timeoutMs: 90000, timeoutMessage: "La carga del documento demoró demasiado." }
      );
      setDocumentUploadStates((current) => ({
        ...current,
        [targetDriver]: {
          ...(current[targetDriver] || {}),
          [tipo]: { stage: "done" },
        },
      }));
      if (targetDriver === selectedDriverCuit) {
        setDocumentsVersion((current) => current + 1);
      }
    } catch (error) {
      setDocumentUploadStates((current) => ({
        ...current,
        [targetDriver]: {
          ...(current[targetDriver] || {}),
          [tipo]: { stage: "error", message: error.message },
        },
      }));
    }
  };

  function downloadInvitationResults(results, label = "invitaciones") {
    if (!results.length) {
      return;
    }
    const lines = ["CUIT;Nombre;Estado;Detalle"];
    results.forEach((result) => {
      const safeName = result.name || "";
      lines.push(`${result.cuit || ""};${safeName};${result.status || ""};${result.message || ""}`);
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = `invitacion-choferes-${label}-${new Date().toISOString()}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(href);
  }

  function collectInvitationInputs(rawValues = []) {
    const normalizedSet = new Set();
    const results = [];
    const validCuits = [];

    const pushInvalid = (normalizedValue, status, message) => {
      results.push({
        cuit: normalizedValue || "sin datos",
        status,
        message,
      });
    };

    const addNormalized = (normalizedValue) => {
      if (normalizedValue.length !== 11) {
        pushInvalid(normalizedValue, "invalid", `CUIT ${normalizedValue || "sin datos"} debe tener 11 dígitos numéricos.`);
        return;
      }
      if (normalizedSet.has(normalizedValue)) {
        pushInvalid(normalizedValue, "duplicate", `El CUIT ${normalizedValue} ya fue incluido.`);
        return;
      }
      normalizedSet.add(normalizedValue);
      validCuits.push(normalizedValue);
    };

    for (const raw of rawValues) {
      const normalized = normalizeDigits(raw);
      if (!normalized) {
        pushInvalid(raw, "invalid", "No se detectaron dígitos válidos en el CUIT ingresado.");
        continue;
      }
      if (normalized.length === 11) {
        addNormalized(normalized);
        continue;
      }
      if (normalized.length > 11) {
        const fullGroups = Math.floor(normalized.length / 11);
        for (let offset = 0; offset < fullGroups * 11; offset += 11) {
          addNormalized(normalized.slice(offset, offset + 11));
        }
        const remainderLength = normalized.length % 11;
        if (remainderLength) {
          const remainder = normalized.slice(normalized.length - remainderLength);
          pushInvalid(remainder, "invalid", `CUIT ${remainder} debe tener 11 dígitos numéricos.`);
        }
        continue;
      }
      pushInvalid(normalized, "invalid", `CUIT ${normalized} debe tener 11 dígitos numéricos.`);
    }

    return { results, validCuits };
  }

  async function inviteChoferes(rawValues = [], transportCompanyCuit = "") {
    const { results, validCuits } = collectInvitationInputs(rawValues);
    const resolvedTransportCompanyCuit = String(transportCompanyCuit || "").trim();

    for (const cuit of validCuits) {
      let driverDetail = null;
      try {
        driverDetail = await fetchChoferDetail(cuit);
      } catch (_error) {
        driverDetail = null;
      }

      try {
        await postJson(`/portal-transporte/choferes/${resolvedTransportCompanyCuit}/transport-links`, {
          chofer_cuit: cuit,
        });

        const driverName = describeDriverName(driverDetail);
        const successMessage = driverDetail
          ? buildInviteSuccessMessage(driverName, cuit)
          : `El chofer CUIT ${cuit} no se encuentra en la base, pero la solicitud fue enviada.`;
        results.push({ cuit, status: "invited", name: driverName, message: successMessage });
      } catch (error) {
        const message = error.message || "Error al invitar.";
        results.push({ cuit, status: "error", message });
      }
    }

    return results;
  }

  async function fetchChoferDetail(cuit) {
    if (!cuit) {
      return null;
    }
    const url = `${getApiUrl()}/portal-chofer/choferes/${cuit}`;
    const response = await fetch(url);
    const payloadText = await response.text();
    let payload = {};
    try {
      payload = payloadText ? JSON.parse(payloadText) : {};
    } catch (_error) {
      payload = {};
    }
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      throw new Error(payload.error || "No pudimos consultar al chofer.");
    }
    return payload.data || null;
  }

  if (!session) {
    return (
      <PortalLayout
        eyebrow="Portal Empresa de Transporte"
        title="Sesión requerida"
        description="Iniciá sesión para invitar choferes y administrar documentos."
        backHref="#/portal-empresa-transporte/app"
      >
        <section className="detail-panel">
          <div className="portal-list">
            <PortalListItem title="Sin sesión activa" description="Vuelve al login o alta del portal para continuar." />
          </div>
        </section>
      </PortalLayout>
    );
  }

  if (!titularCuit) {
    return (
      <PortalLayout
        eyebrow="Portal Empresa de Transporte"
        title="Asociá una empresa"
        description="Seleccioná o creá una empresa de transporte para administrar choferes."
        backHref="#/portal-empresa-transporte/app"
      >
        <section className="detail-panel">
          <StatusMessage message="Asociá la empresa desde el portal principal para ver esta pantalla." />
        </section>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout
      eyebrow="Portal Empresa de Transporte"
      title={
        <>
          <span>Choferes</span>
          <span>y documentación</span>
        </>
      }
      description="Invitá choferes, monitoreá el vínculo y subí los documentos clave desde este módulo."
      heroClassName="transport-app-header"
      heroContentClassName="transport-app-header-left"
      titleClassName="transport-app-header-title"
      heroAsideClassName="transport-app-user-box"
      heroAside={(
        <div className="session-summary">
          <div className="session-summary-row">
            <span>Empresa</span>
            <strong>{session?.company_name || titularCuit}</strong>
          </div>
          <div className="session-summary-row">
            <span>CUIT</span>
            <strong>{titularCuit}</strong>
          </div>
        </div>
      )}
      heroAction={(
        <button
          className="secondary-button"
          type="button"
          onClick={() => {
            window.location.hash = "#/portal-empresa-transporte/app";
          }}
        >
          Volver a la pantalla principal
        </button>
      )}
      >
      <StatusMessage message={driversError} />
      {driversLoading ? <ProcessingPanel title="Cargando choferes" message="Buscando vínculos asociados." /> : null}

      <section className="portal-section-grid invite-requests-grid">
        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>Invitar choferes</h2>
            <p className="mini-copy">
              El vínculo habilita gestionar documentación clave (931, seguros, apto psicofísico y carnet). Hasta 5 CUITs se procesan aquí mismo; más de 5 muestran el resultado y generan un archivo CSV.
            </p>
          </div>
          <div className="panel-body">
            <label className="field">
              <span>Empresa que invita</span>
              <select
                value={invitationCompanyCuit}
                onChange={(event) => setInvitationCompanyCuit(event.target.value)}
                disabled={availableCompaniesLoading || !availableCompanies.length}
              >
                {!availableCompanies.length ? (
                  <option value="">
                    {availableCompaniesLoading ? "Cargando empresas..." : "Sin empresas disponibles"}
                  </option>
                ) : null}
                {availableCompanies.map((company) => (
                  <option key={company.cuit} value={company.cuit}>
                    {company.razon_social || `Empresa ${company.cuit}`} · {company.cuit}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>CUITs (uno por línea o separados por coma)</span>
              <textarea
                rows="4"
                value={invitationInput}
                onChange={(event) => setInvitationInput(event.target.value)}
                placeholder="20300111222\n27300111222"
              />
            </label>
            <div className="biometry-actions">
              <button
                className="submit-button"
                type="button"
                disabled={invitationLoading || !invitationCompanyCuit}
                onClick={handleInviteSubmit}
              >
                {invitationLoading ? "Procesando..." : "Enviar invitaciones"}
              </button>
            </div>
            {invitationMessage ? <p className="status-message">{invitationMessage}</p> : null}
            {invitationResults.length ? (
              <div className="invitation-results">
                <pre className="invitation-result-pre">
                  {invitationResults
                    .map((result) => `${result.cuit || "-"} - ${invitationStatusLabel(result.status).toLowerCase()}`)
                    .join("\n")}
                </pre>
              </div>
            ) : null}
          </div>
        </section>
        <section className="detail-panel detail-panel-requests">
          <div className="detail-panel-header">
            <div>
              <h2>Solicitudes enviadas</h2>
              <p className="mini-copy">
                Estas son las solicitudes que aún no fueron aprobadas. Aparecen aquí por más que el chofer todavía no aceptó el vínculo o fue revocado.
              </p>
            </div>
            <StatusBadge
              label={pendingRequests.length ? `${pendingRequests.length} pendientes` : "Sin solicitudes pendientes"}
              tone={pendingRequests.length ? "faltante" : "vigente"}
            />
          </div>
          <StatusMessage message={pendingRequestsMessage} />
          {pendingRequestsLoading ? <ProcessingPanel title="Cargando solicitudes" message="Buscando solicitudes enviadas." /> : null}
          <div className="portal-list">
            {!pendingRequests.length ? (
              <PortalListItem
                title="Sin solicitudes enviadas pendientes"
                description="Cuando invites choferes, aquí vas a ver los vínculos que todavía no fueron aprobados."
              />
            ) : (
              pendingRequests.map((driver) => (
                <div
                  key={`transport-request-${driver.chofer_cuit}-${driver.id}`}
                  className="company-home-row"
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div className="company-home-row-main">
                    <PortalListItem
                      title={`${driver.chofer_nombre || "Chofer"} ${driver.chofer_apellido || ""}`.trim() || driver.chofer_cuit}
                      description={`CUIT ${driver.chofer_cuit || "-"} · Solicitud ${driverStatusLabel(driver.status)} · Última actualización ${formatDate(
                        driver.updated_at || driver.created_at
                      )}`}
                    />
                  </div>
                  <StatusBadge label={driverStatusLabel(driver.status)} tone={requestStatusTone(driver.status)} />
                </div>
              ))
            )}
          </div>
          {invitationSummaryLines.length ? (
            <div className="invitation-summary">
              <strong>Resumen reciente</strong>
              {invitationSummaryLines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          ) : null}
        </section>
      </section>
      <section className="portal-section-grid drivers-grid">
        <section className="detail-panel detail-panel-drivers">
          <div className="detail-panel-header">
            <div>
              <h2>Choferes vinculados</h2>
              <p className="mini-copy">
                Solo aparecen acá los choferes con vínculo aprobado. Usá la sección de solicitudes si todavía están pendientes.
              </p>
            </div>
            <StatusBadge label={approvedDrivers.length ? `${approvedDrivers.length} choferes` : "Sin choferes aprobados"} tone={approvedDrivers.length ? "vigente" : "faltante"} />
          </div>
          <StatusMessage message={documentsMessage} />
          {documentsLoading ? <ProcessingPanel title="Cargando documentos" message="Buscando archivos del chofer seleccionado." /> : null}
          <div className="portal-list">
            {!approvedDrivers.length ? (
              <PortalListItem
                title="Sin choferes vinculados"
                description="Invitá choferes para empezar a gestionar su documentación desde aquí."
              />
            ) : (
              approvedDrivers.map((driver) => {
                const driverName = `${driver.chofer_nombre || "Chofer"} ${driver.chofer_apellido || ""}`.trim();
                const preferredDocType = driverDocChoice[driver.chofer_cuit] || driverDocumentDefinitions[0]?.tipo;
                const summaryUploadState = (documentUploadStates[driver.chofer_cuit] || {})[preferredDocType];
                const photoStates = documentUploadStates[driver.chofer_cuit] || {};
                const activeDocuments =
                  driver.chofer_cuit === selectedDriverCuit ? latestDocuments : [];
                return (
                  <details
                    key={`transport-chofer-${driver.chofer_cuit}`}
                    className="detail-panel expandable-card driver-card"
                    onToggle={(event) => {
                      if (event.target.open) {
                        setSelectedDriverCuit(driver.chofer_cuit);
                      }
                    }}
                  >
                    <summary className="expandable-card-summary">
                      <div>
                        <h2>{driverName || driver.chofer_cuit}</h2>
                        <p>{`CUIT ${driver.chofer_cuit || "-"} · ${driverStatusLabel(driver.status)}`}</p>
                      </div>
                      <div className="expandable-card-summary-side compact">
                        <label className="field driver-summary-field">
                          <span>Tipo</span>
                          <select
                            value={preferredDocType}
                            onChange={(event) => {
                              setDriverDocChoice((current) => ({
                                ...current,
                                [driver.chofer_cuit]: event.target.value,
                              }));
                            }}
                          >
                            {driverDocumentDefinitions.map((definition) => (
                              <option key={definition.tipo} value={definition.tipo}>
                                {definition.label}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="field driver-summary-field">
                          <span>Subir</span>
                          <input type="file" accept="application/pdf,image/*" onChange={(event) => handleDocumentUpload(preferredDocType, event, driver.chofer_cuit)} />
                        </label>
                        {summaryUploadState?.stage === "processing" ? <p className="mini-copy">Subiendo...</p> : null}
                        {summaryUploadState?.stage === "error" ? (
                          <p className="status-message">{summaryUploadState.message}</p>
                        ) : null}
                      </div>
                      <span className="expandable-card-chevron" aria-hidden="true">
                        ▾
                      </span>
                    </summary>
                    <div className="expandable-card-body">
                      <div className="detail-table">
                        <div className="detail-row">
                          <span>Estado</span>
                          <strong>{driverStatusLabel(driver.status)}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Última actualización</span>
                          <strong>{formatDate(driver.updated_at || driver.created_at)}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Documentos registrados</span>
                          <strong>{driver.documentos?.length || "0"}</strong>
                        </div>
                      </div>
                      {driverPhotoDefinitions.length ? (
                        <div className="portal-list driver-detail-photos">
                          {driverPhotoDefinitions.map((definition) => {
                            const photoState = photoStates[definition.tipo];
                            return (
                              <div key={`driver-photo-${definition.tipo}`} className="portal-list-card">
                                <div>
                                  <strong>{definition.label}</strong>
                                  {photoState?.stage === "processing" ? <p className="mini-copy">Subiendo foto...</p> : null}
                                  {photoState?.stage === "error" ? <p className="status-message">{photoState.message}</p> : null}
                                </div>
                                <label className="field">
                                  <span>Subir foto</span>
                                  <input type="file" accept="image/*" onChange={(event) => handleDocumentUpload(definition.tipo, event, driver.chofer_cuit)} />
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                      {driver.chofer_cuit === selectedDriverCuit && activeDocuments.length ? (
                        <div className="portal-list driver-detail-documents">
                          {driverDocumentDefinitions.map((definition) => {
                            const document = activeDocuments.find((item) => item.tipo_documento === definition.tipo);
                            const docState = (documentUploadStates[driver.chofer_cuit] || {})[definition.tipo];
                            return (
                              <div key={`driver-doc-detail-${definition.tipo}`} className="portal-list-card">
                                <div>
                                  <strong>{definition.label}</strong>
                                  <p>
                                    {document
                                      ? `${document.estado || "Subido"} · ${document.nombre_archivo || ""} · ${formatDate(document.created_at)}`
                                      : "Sin documento"}
                                  </p>
                                  {docState?.stage === "processing" ? (
                                    <p className="mini-copy">Subiendo...</p>
                                  ) : null}
                                  {docState?.stage === "error" ? <p className="status-message">{docState.message}</p> : null}
                                </div>
                                <label className="field">
                                  <span>Subir</span>
                                  <input type="file" accept="application/pdf,image/*" onChange={(event) => handleDocumentUpload(definition.tipo, event, driver.chofer_cuit)} />
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  </details>
                );
              })
            )}
          </div>
        </section>
      </section>
    </PortalLayout>
  );
}

export function PortalEmpresaTransporteCompaniesScreen() {
  const documentPolicyCatalog = usePortalDocumentPolicyCatalog();
  const transportSession = readTransportSession();
  const [companies, setCompanies] = useState([]);
  const [companyOnboardingOpen, setCompanyOnboardingOpen] = useState(false);
  const [companyOnboardingPreviewUrl, setCompanyOnboardingPreviewUrl] = useState("");
  const [companyOnboardingProcessing, setCompanyOnboardingProcessing] = useState(null);
  const [companyFilter, setCompanyFilter] = useState("");
  const [openingDocumentId, setOpeningDocumentId] = useState("");
  const [previewUrls, setPreviewUrls] = useState({});
  const [companyProcessing, setCompanyProcessing] = useState({});
  const [messages, setMessages] = useState({});
  const [hiddenCompanies, setHiddenCompanies] = useState(readHiddenCompanies);
  const companyProcessingTimeoutsRef = useRef({});

  useEffect(() => {
    writeHiddenCompanies(hiddenCompanies);
  }, [hiddenCompanies]);

  const hideCompany = (cuit) => {
    setHiddenCompanies((current) => {
      if (current.includes(cuit)) {
        return current;
      }
      return [...current, cuit];
    });
  };

  async function loadCompanies() {
    const response = { data: await loadScopedTransportCompanies(transportSession?.company_cuit, transportSession?.email) };
    setCompanies(
      (response.data || []).map((titular) => ({
        cuit: titular.cuit,
        razon_social: titular.razon_social,
        email: titular.email || transportSession?.email || "",
        mock: false,
        documentos: titular.documentos || [],
      }))
    );
  }

  useEffect(() => {
    let cancelled = false;

    async function fetchCompanies() {
      try {
        const response = { data: await loadScopedTransportCompanies(transportSession?.company_cuit, transportSession?.email) };
        if (cancelled) {
          return;
        }

        setCompanies(
          (response.data || []).map((titular) => ({
            cuit: titular.cuit,
            razon_social: titular.razon_social,
            email: titular.email || transportSession?.email || "",
            mock: false,
            documentos: titular.documentos || [],
          }))
        );
      } catch (_error) {
        if (!cancelled) {
          setCompanies([]);
        }
      }
    }

    fetchCompanies();
    return () => {
      cancelled = true;
    };
  }, [transportSession?.email]);

  const companiesWithValidCuit = useMemo(
    () => companies.filter((company) => isValidCuit(company?.cuit)),
    [companies]
  );

  const filteredCompanies = useMemo(
    () =>
      companiesWithValidCuit
        .filter((company) => {
          const normalized = companyFilter.trim().toUpperCase();
          if (!normalized) {
            return true;
          }
          return (
            String(company.cuit || "").toUpperCase().includes(normalized) ||
            String(company.razon_social || "").toUpperCase().includes(normalized)
          );
        })
        .filter((company) => !hiddenCompanies.includes(company.cuit)),
    [companiesWithValidCuit, companyFilter, hiddenCompanies]
  );

  const fileInputRefs = useRef({});

  const createCompany = () => {
    setCompanyOnboardingOpen(true);
    setCompanyOnboardingPreviewUrl("");
    setCompanyOnboardingProcessing(null);
    setMessages((current) => ({
      ...current,
      empresas: "Sube la constancia AFIP para crear la empresa desde el documento.",
    }));
  };

  const uploadCompanyAfipCertificate = async (file) => {
    if (!file) {
      return;
    }

    const normalizedType = String(file.type || "").toLowerCase();
    const isPdf = normalizedType === "application/pdf";
    const isImage = normalizedType.startsWith("image/");

    if (!isPdf && !isImage) {
      setCompanyOnboardingPreviewUrl("");
      setCompanyOnboardingProcessing({
        stage: "done",
        label: "Error",
        tone: "vencido",
        fileType: file.type,
      });
      setMessages((current) => ({
        ...current,
        empresas: "La constancia AFIP debe subirse como PDF o imagen.",
      }));
      return;
    }

    if (isImage) {
      setCompanyOnboardingPreviewUrl(URL.createObjectURL(file));
    } else {
      setCompanyOnboardingPreviewUrl("");
    }

    let ocrFile = file;
    let conversionLabel = "direct_image";
    let pdfCuitOverride = "";

    if (isPdf) {
      try {
        const extractedText = await extractTextFromPdf(file);
        const extractedCuit = findCuitInText(extractedText);
        pdfCuitOverride = extractedCuit ? normalizeDigits(extractedCuit) : "";
        if (pdfCuitOverride) {
          console.info(`[constancia-onboarding] pdf_cuit_override=${pdfCuitOverride}`);
        }
      } catch (textError) {
        console.warn("No se pudo extraer texto directo del PDF", textError.message);
      }

      try {
        ocrFile = await convertPdfFirstPageToImage(file);
        conversionLabel = "pdf_to_image";
      } catch (conversionError) {
        const message = conversionError.message || "No pudimos convertir la constancia en imagen.";
        setCompanyOnboardingProcessing({
          stage: "done",
          label: "Error",
          tone: "vencido",
          fileType: file.type,
          details: [message],
        });
        setMessages((current) => ({
          ...current,
          empresas: message,
        }));
        return;
      }
    }

    setCompanyOnboardingProcessing({
      stage: "analyzing",
      label: "Procesando",
      fileType: file.type,
    });
    setMessages((current) => ({ ...current, empresas: "" }));

    try {
      const analysisResponse = await postForm(
        "/afip/constancia-ocr",
        {
          conversion: conversionLabel,
          ocr_path: "rekognition",
          original_file_type: normalizedType,
        },
        { archivo: ocrFile },
        { timeoutMs: 60000, timeoutMessage: "La lectura de la constancia demoró demasiado." }
      );

      const extraction = analysisResponse.data?.extraction || {};
      const extractedFields = extraction.extracted_fields || {};
      const resolvedCuit = normalizeDigits(extractedFields.cuit || analysisResponse.data?.cuit || "");
      console.info(
        `[constancia-onboarding] fileType=${normalizedType} conversion=${conversionLabel} ocr_path=rekognition extractedCuit=${resolvedCuit || "not-found"}`
      );

      if (!resolvedCuit) {
        throw new Error("No pudimos extraer el CUIT desde la constancia AFIP.");
      }

      const lookupBody = await getJson(`/companies/lookup?cuit=${resolvedCuit}`);
      if (!lookupBody?.found || !lookupBody?.company) {
        throw new Error(`El CUIT ${resolvedCuit} no está disponible en el registro oficial del entorno actual.`);
      }
      const officialName = lookupBody?.company?.razon_social || "";
      if (!isRealBusinessName(officialName)) {
        throw new Error(`No pudimos obtener una razón social oficial para el CUIT ${resolvedCuit}.`);
      }

      setCompanyOnboardingProcessing({
        stage: "saving",
        label: "Guardando",
        fileType: file.type,
      });

      const titularResponse = await postForm("/titulares", {
        cuit: resolvedCuit,
        razon_social: officialName,
        email: transportSession?.email || "",
      });
      const savedCompany = titularResponse.data || {};
      const savedBusinessName = savedCompany.razon_social || officialName;
      addOwnedTransportCompany(resolvedCuit);

      if (transportSession?.user_id) {
        const completedSession = await postJson("/portal-transporte/auth/complete-company", {
          user_id: transportSession.user_id,
          company_cuit: resolvedCuit,
          company_name: savedBusinessName,
          company_email: transportSession?.email || "",
        });
        saveTransportSession(completedSession.data);
      }

      await postForm(
        "/documentos",
        {
          entidad_tipo: "titular",
          entidad_id: resolvedCuit,
          tipo_documento: "constancia_afip",
          estado: "vigente",
        },
        { archivo: file },
        { timeoutMs: 90000, timeoutMessage: "La carga de la constancia AFIP demoró demasiado." }
      );

      await loadCompanies();

      setCompanyOnboardingProcessing({
        stage: "done",
        label: "Validado",
        tone: "vigente",
        fileType: file.type,
        details: [
          `Empresa ${savedBusinessName} creada correctamente.`,
          extractedFields.cuit ? `CUIT detectado: ${extractedFields.cuit}.` : null,
        ].filter(Boolean),
      });
      setCompanyOnboardingOpen(false);
      setMessages((current) => ({
        ...current,
        empresas: `Empresa ${savedBusinessName} creada correctamente desde la constancia AFIP.`,
      }));
    } catch (error) {
      setCompanyOnboardingProcessing({
        stage: "done",
        label: "Error",
        tone: "vencido",
        fileType: file.type,
        details: [error.message || "No pudimos completar el alta de la empresa."],
      });
      setMessages((current) => ({ ...current, empresas: error.message }));
    }
  };

  const openCompanyDocument = async (document) => {
    setOpeningDocumentId(String(document.id));
    const popup = window.open("about:blank", "_blank");
    if (popup) {
      popup.opener = null;
    }
    try {
      if (isMockPortalDocument(document)) {
        const mockUrl = buildMockDocumentUrl(document);
        if (popup) {
          popup.location.href = mockUrl;
        } else {
          window.open(mockUrl, "_blank", "noopener,noreferrer");
        }
      } else {
        const response = await getJson(`/documentos/${document.id}/url`);
        if (popup) {
          popup.location.href = response.data.url;
        } else {
          window.open(response.data.url, "_blank", "noopener,noreferrer");
        }
      }
    } catch (error) {
      if (popup) {
        popup.document.open();
        popup.document.write(buildDocumentErrorHtml(error?.message || "No pudimos abrir el archivo."));
        popup.document.close();
      }
      setMessages((current) => ({ ...current, empresas: error.message }));
    } finally {
      setOpeningDocumentId("");
    }
  };

  const uploadCompanyDocument = async ({ entidadId, tipoDocumento, file, captureKey }) => {
    if (!entidadId || !tipoDocumento || !file) {
      return;
    }

    if (String(file.type || "").startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      setPreviewUrls((current) => ({
        ...current,
        [captureKey]: previewUrl,
      }));
    }

    setCompanyProcessing((current) => ({
      ...current,
      [captureKey]: {
        stage: "analyzing",
        label: "Procesando",
        fileType: file.type,
      },
    }));

    try {
      const company = companies.find((item) => item.cuit === entidadId) || {
        cuit: "",
        razon_social: "",
        email: transportSession?.email || "",
      };

      const analysisResponse = await postForm(
        "/documentos/analizar",
        {
          entidad_tipo: "titular",
          entidad_id: company.cuit || "",
          tipo_sugerido: tipoDocumento,
        },
        { archivo: file },
        { timeoutMs: 60000, timeoutMessage: "El analisis del documento demoro demasiado." }
      );

      const documentValidation = analysisResponse.data.company_validation || null;
      const extracted = analysisResponse.data.company_extraction?.extracted_fields || {};
      const resolvedCuit = normalizeDigits(extracted.cuit || company.cuit || "");
      let resolvedBusinessName = extracted.razon_social || company.razon_social || "";

      if (!resolvedCuit) {
        throw new Error("No pudimos extraer el CUIT desde la constancia AFIP.");
      }
      const companyLookup = await getJson(`/companies/lookup?cuit=${resolvedCuit}`).catch(() => null);
      const afipBusinessName = companyLookup?.data?.company?.razon_social || "";
      if (companyLookup?.data?.afip_validated && isRealBusinessName(afipBusinessName)) {
        resolvedBusinessName = afipBusinessName;
      }
      if (!isRealBusinessName(resolvedBusinessName)) {
        throw new Error(`No pudimos extraer una razón social válida desde la constancia AFIP. CUIT leído: ${resolvedCuit}.`);
      }

      await postForm("/titulares", {
        cuit: resolvedCuit,
        razon_social: resolvedBusinessName,
        email: company.email || transportSession?.email || "",
      });
      addOwnedTransportCompany(resolvedCuit);
      setCompanyProcessing((current) => ({
        ...current,
        [captureKey]: {
          stage: "saving",
          label: "Guardando",
          fileType: file.type,
        },
      }));
      const saveResponse = await postForm(
        "/documentos",
        {
          entidad_tipo: "titular",
          entidad_id: resolvedCuit,
          tipo_documento: tipoDocumento,
          fecha_vencimiento: getCompanyDocumentExpiration(extracted),
          estado: analysisResponse.data.estado_documento || "observado",
          validation: documentValidation ? JSON.stringify(documentValidation) : "",
        },
        { archivo: file },
        { timeoutMs: 60000, timeoutMessage: "La carga del documento demoro demasiado." }
      );

      const savedDocument = saveResponse?.data || null;
      if (savedDocument) {
        setCompanies((current) => upsertCompanyDocument(current, resolvedCuit, savedDocument));
      }
      await loadCompanies();
      setPreviewUrls((current) => {
        const next = { ...current };
        delete next[captureKey];
        return next;
      });
      setCompanyOnboardingOpen(false);
      const finalStatus = sanitizeCompanyValidation(documentValidation)?.status || "observed";
      setCompanyProcessing((current) => ({
        ...current,
        [captureKey]: {
          stage: "done",
          label: mapCompanyValidationLabel(finalStatus),
          tone: mapCompanyValidationTone(finalStatus),
          fileType: file.type,
          details: Array.isArray(documentValidation?.reasons) && documentValidation.reasons.length
            ? documentValidation.reasons
            : [finalStatus === "approved" ? "Documento validado." : "Documento procesado."],
        },
      }));
      if (companyProcessingTimeoutsRef.current[captureKey]) {
        clearTimeout(companyProcessingTimeoutsRef.current[captureKey]);
      }
      companyProcessingTimeoutsRef.current[captureKey] = setTimeout(() => {
        setCompanyProcessing((current) => {
          const next = { ...current };
          delete next[captureKey];
          return next;
        });
        delete companyProcessingTimeoutsRef.current[captureKey];
      }, 1800);
    } catch (error) {
      setCompanyProcessing((current) => ({
        ...current,
        [captureKey]: {
          stage: "done",
          label: "Error",
          tone: "vencido",
          fileType: file.type,
          details: [error.message || "No pudimos procesar el documento."],
        },
      }));
      if (companyProcessingTimeoutsRef.current[captureKey]) {
        clearTimeout(companyProcessingTimeoutsRef.current[captureKey]);
      }
      companyProcessingTimeoutsRef.current[captureKey] = setTimeout(() => {
        setCompanyProcessing((current) => {
          const next = { ...current };
          delete next[captureKey];
          return next;
        });
        delete companyProcessingTimeoutsRef.current[captureKey];
      }, 1800);
      setMessages((current) => ({ ...current, empresas: error.message }));
    }
  };

  useEffect(
    () => () => {
      Object.values(companyProcessingTimeoutsRef.current).forEach((timeoutId) => clearTimeout(timeoutId));
    },
    []
  );

  return (
    <PortalLayout
      eyebrow="Portal Empresa de Transporte"
      title="Empresas de transporte"
      description="Administra varias empresas y su documentación societaria."
      backHref="#/portal-empresa-transporte/app"
    >
      <section className="portal-section-grid">
        <section className="detail-panel new-unit-panel">
          <div className="detail-panel-header">
            <h2>+ Nueva empresa</h2>
          </div>
          <div className="portal-list">
            <PortalListItem
              title="Paso 1"
              description="Sube la constancia AFIP en PDF."
            />
          </div>
          <div className="biometry-actions new-unit-actions">
            <button className="submit-button" type="button" onClick={createCompany}>
              Nueva empresa
            </button>
          </div>
        </section>
      </section>

      {companyOnboardingOpen ? (
        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>Nueva empresa</h2>
          </div>
          <div className="portal-list">
            <PortalListItem
              title="Constancia AFIP"
              description="Este es el primer paso del alta. La constancia AFIP debe venir en PDF."
            />
          </div>
          <div className="company-document-list">
            <div className="company-document-row">
              <div className="company-document-row-main">
                <strong>Certificado AFIP</strong>
                <StatusBadge
                  label={companyOnboardingProcessing?.stage && companyOnboardingProcessing.stage !== "done" ? "Procesando" : companyOnboardingProcessing?.label || "Pendiente"}
                  tone={companyOnboardingProcessing?.stage && companyOnboardingProcessing.stage !== "done" ? "faltante" : companyOnboardingProcessing?.tone || "faltante"}
                  details={companyOnboardingProcessing?.stage === "done" ? companyOnboardingProcessing?.details || null : null}
                  loading={Boolean(companyOnboardingProcessing?.stage && companyOnboardingProcessing.stage !== "done")}
                />
              </div>
              <div className="company-document-row-thumb">
                <label className="company-document-thumb-button">
                  <div className="company-document-thumb">
                    {companyOnboardingPreviewUrl ? <img src={companyOnboardingPreviewUrl} alt="Constancia AFIP" /> : <div className="company-document-thumb-empty" />}
                    {companyOnboardingProcessing ? (
                      <div className={`company-document-thumb-processing company-document-thumb-processing-${companyOnboardingProcessing.tone || "faltante"}`}>
                        {companyOnboardingProcessing.stage !== "done" ? <span className="processing-spinner" aria-hidden="true" /> : null}
                        <strong>{companyOnboardingProcessing.label}</strong>
                      </div>
                    ) : null}
                  </div>
                  <input
                    type="file"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        uploadCompanyAfipCertificate(file);
                      }
                      event.target.value = "";
                    }}
                  />
                </label>
              </div>
              <div className="company-document-row-side">
                <button className="secondary-button" type="button" onClick={() => setCompanyOnboardingOpen(false)}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="detail-panel detail-panel-wide fleet-inventory-panel">
        <div className="detail-panel-header">
          <h2>Empresas dadas de alta</h2>
        </div>
        <div className="detail-table company-filter-table">
          <label className="field">
            <span>Filtro</span>
            <input value={companyFilter} onChange={(event) => setCompanyFilter(event.target.value)} placeholder="CUIT o razón social" />
          </label>
        </div>
        <StatusMessage message={messages.empresas} />
        {!filteredCompanies.length ? (
          <div className="portal-list">
            <PortalListItem
              title="Todavía no tienes empresas dadas de alta"
              description="Inicia el alta y sube la constancia AFIP para extraer CUIT y razón social desde el documento."
            />
          </div>
        ) : (
          <div className="fleet-inventory-list">
            {filteredCompanies.map((company) => (
              <CompanyEntityCard
                key={`titular-${company.cuit || company.local_id}`}
                title={company.razon_social}
                subtitle={company.cuit || "CUIT pendiente de extracción"}
                entityId={company.cuit || company.local_id}
                entityData={company}
                documents={company.documentos || []}
                openingDocumentId={openingDocumentId}
                previewUrls={previewUrls}
                processingByKey={companyProcessing}
                onOpenDocument={openCompanyDocument}
                onUploadDocument={uploadCompanyDocument}
                fileInputRefs={fileInputRefs}
                documentPolicyCatalog={documentPolicyCatalog}
                onHideCompany={() => hideCompany(company.cuit)}
              />
            ))}
          </div>
        )}
      </section>
    </PortalLayout>
  );
}

export function PortalEmpresaTransporteFleetScreen() {
  const transportSession = readTransportSession();
  const documentPolicyCatalog = usePortalDocumentPolicyCatalog();
  const batchInputRef = useRef(null);
  const [pendingUnitRequest, setPendingUnitRequest] = useState(() => readPendingUnitRegistrationRequest());
  const [unit, setUnit] = useState(initialVehicle);
  const [messages, setMessages] = useState({});
  const [loadingKey, setLoadingKey] = useState("");
  const [dashboardCuit, setDashboardCuit] = useState(
    () => transportSession?.company_cuit || window.localStorage.getItem("portalTransporteTitularCuit") || ""
  );
  const [recentFleetItems, setRecentFleetItems] = useState([]);
  const [dashboardData, setDashboardData] = useState(MOCK_TRANSPORTE_DASHBOARD);
  const [openingDocumentId, setOpeningDocumentId] = useState("");
  const [activeFleetCaptureKey, setActiveFleetCaptureKey] = useState("");
  const [fleetPreviewUrls, setFleetPreviewUrls] = useState({});
  const [domainFilter, setDomainFilter] = useState("");
  const [unitPhotoAnalysis, setUnitPhotoAnalysis] = useState(null);
  const [unitPhotoStep, setUnitPhotoStep] = useState(0);
  const [unitPhotoStatuses, setUnitPhotoStatuses] = useState({});
  const [pendingPhotoDocuments, setPendingPhotoDocuments] = useState([]);
  const [unitBatchType, setUnitBatchType] = useState("nueva_unidad");
  const [unitBatchFiles, setUnitBatchFiles] = useState([]);
  const [unitBatchAnalysis, setUnitBatchAnalysis] = useState([]);
  const [unitBatchPending, setUnitBatchPending] = useState([]);
  const [unitRtoSnapshot, setUnitRtoSnapshot] = useState(null);
  const [unitRtoLoading, setUnitRtoLoading] = useState(false);
  const [unitBatchProgress, setUnitBatchProgress] = useState(null);
  const [unitCreatedDomain, setUnitCreatedDomain] = useState("");
  const [unitCreationStatus, setUnitCreationStatus] = useState("");
  const [unitRtoSnapshots, setUnitRtoSnapshots] = useState({});
  const [unitRtoLoadingMap, setUnitRtoLoadingMap] = useState({});
  const [unitRtoErrors, setUnitRtoErrors] = useState({});
  const [creationMode, setCreationMode] = useState("");
  const [unitPhotoCaptureOpen, setUnitPhotoCaptureOpen] = useState(false);
  const [unitPhotoCaptureStepIndex, setUnitPhotoCaptureStepIndex] = useState(null);
  const [expandedDocumentStep, setExpandedDocumentStep] = useState(null);

  useEffect(() => {
    if (!pendingUnitRequest) {
      return;
    }

    setUnit((current) => ({
      ...current,
      titular_cuit: pendingUnitRequest.transport_company_id || dashboardCuit,
      dominio: pendingUnitRequest.domain || current.dominio,
      tipo: pendingUnitRequest.unit_type === "tractor_unit" ? "Tractor" : "Remolque",
    }));
  }, [pendingUnitRequest, dashboardCuit]);

  useEffect(() => {
    if (dashboardCuit) {
      return undefined;
    }
    let cancelled = false;

    async function discoverDefaultTitular() {
      try {
        const companies = await loadScopedTransportCompanies(transportSession?.company_cuit, transportSession?.email);
        if (cancelled) {
          return;
        }
        let candidate = null;
        if (companies.length) {
          const latestFleetItems = await loadRecentFleetItemsForTransportCompanies(companies);
          candidate = latestFleetItems.length ? latestFleetItems[0].transport_company_cuit : null;
          if (!candidate) {
            candidate = companies[0].cuit;
          }
        }
        if (candidate) {
          setDashboardCuit(candidate);
          window.localStorage.setItem("portalTransporteTitularCuit", candidate);
        }
      } catch (_error) {
        // ignore
      }
    }

    discoverDefaultTitular();
    return () => {
      cancelled = true;
    };
  }, [dashboardCuit, transportSession?.company_cuit]);

  const hasPersistedDomain = (dominio) => {
    if (!dominio || !dashboardData) {
      return false;
    }

    const normalized = String(dominio).toUpperCase();
    return [...(dashboardData.unidades || []), ...(dashboardData.acoplados || [])].some(
      (item) => String(item.dominio || "").toUpperCase() === normalized && !item.mock
    );
  };

  const currentCapturePhotoStep =
    unitPhotoCaptureStepIndex === null ? null : UNIT_PHOTO_STEPS[unitPhotoCaptureStepIndex] || UNIT_PHOTO_STEPS[0];

  const setCapturePreview = (captureKey, file) => {
    if (!captureKey || !file) {
      return;
    }

    setFleetPreviewUrls((current) => {
      if (current[captureKey]) {
        URL.revokeObjectURL(current[captureKey]);
      }
      return {
        ...current,
        [captureKey]: URL.createObjectURL(file),
      };
    });
  };

  const markPhotoStep = (stepValue, patch) => {
    setUnitPhotoStatuses((current) => ({
      ...current,
      [stepValue]: {
        ...(current[stepValue] || {}),
        ...patch,
      },
    }));
  };

  const markBatchDocumentStatuses = (items, batchType) => {
    const documentTypes = getUnitDocumentTypesForBatch(batchType, items.length);
    items.forEach((item, index) => {
      const documentType = documentTypes[index];
      if (!documentType) {
        return;
      }

      markPhotoStep(documentType, {
        status: item.result?.legible ? "done" : "error",
        file: item.file,
        analysis: item.result,
        source: "archivo",
        label: UNIT_PHOTO_STEPS.find((step) => step.value === documentType)?.label || humanizeLabel(documentType),
      });
    });
  };

  const mapBatchItemsToDocuments = (items, batchType) => {
    const documentTypes = getUnitDocumentTypesForBatch(batchType, items.length);
    return items.map((item, index) => ({
      ...item,
      tipoDocumento: documentTypes[index] || "cedula_frente",
    }));
  };

  useEffect(() => {
    let cancelled = false;

    async function loadDashboard() {
      if (!dashboardCuit) {
        setDashboardData(MOCK_TRANSPORTE_DASHBOARD);
        return;
      }

      try {
        const response = await getJson(`/portal-transporte/titulares/${dashboardCuit}/dashboard`);
        if (!cancelled) {
          setDashboardData(response.data);
          window.localStorage.setItem("portalTransporteTitularCuit", dashboardCuit);
        }
      } catch (error) {
        if (!cancelled) {
          setDashboardData(MOCK_TRANSPORTE_DASHBOARD);
          setMessages((current) => ({
            ...current,
            dashboard: "Mostrando flota mockeada mientras terminamos de resolver el titular desde documentos.",
          }));
        }
      }
    }

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [dashboardCuit]);

  useEffect(() => {
    let cancelled = false;

    async function loadRecentFleetOverview() {
      try {
        const companies = await loadScopedTransportCompanies(dashboardCuit || transportSession?.company_cuit, transportSession?.email);
        if (cancelled) {
          return;
        }
        const latestFleetItems = await loadRecentFleetItemsForTransportCompanies(companies);
        if (!cancelled) {
          setRecentFleetItems(latestFleetItems);
        }
      } catch (_error) {
        if (!cancelled) {
          setRecentFleetItems([]);
        }
      }
    }

    loadRecentFleetOverview();
    return () => {
      cancelled = true;
    };
  }, [dashboardCuit, transportSession?.company_cuit]);

  const openDocument = async (document) => {
    setOpeningDocumentId(String(document.id));
    const popup = window.open("about:blank", "_blank");
    if (popup) {
      popup.opener = null;
    }
    try {
      if (isMockPortalDocument(document)) {
        const mockUrl = buildMockDocumentUrl(document);
        if (popup) {
          popup.location.href = mockUrl;
        } else {
          window.open(mockUrl, "_blank", "noopener,noreferrer");
        }
      } else {
        const response = await getJson(`/documentos/${document.id}/url`);
        if (popup) {
          popup.location.href = response.data.url;
        } else {
          window.open(response.data.url, "_blank", "noopener,noreferrer");
        }
      }
    } catch (error) {
      if (popup) {
        popup.document.open();
        popup.document.write(buildDocumentErrorHtml(error?.message || "No pudimos abrir el archivo."));
        popup.document.close();
      }
      setMessages((current) => ({ ...current, dashboard: error.message }));
    } finally {
      setOpeningDocumentId("");
    }
  };

  const uploadFleetDocument = async ({ entidadTipo, entidadId, tipoDocumento, file, entityData, captureKey }) => {
    if (!entidadTipo || !entidadId || !tipoDocumento || !file) {
      setMessages((current) => ({ ...current, dashboard: "Completa la captura del documento." }));
      return;
    }

    if ((entidadTipo === "unidad" || entidadTipo === "acoplado") && isVehicleInsuranceDocumentType(tipoDocumento) && !hasVehicleCedulaLoaded(entityData)) {
      setMessages((current) => ({
        ...current,
        dashboard: "Antes de subir la póliza tenés que cargar la cédula del dominio.",
      }));
      return;
    }

    if (captureKey && String(file.type || "").startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      setFleetPreviewUrls((current) => {
        if (current[captureKey]) {
          URL.revokeObjectURL(current[captureKey]);
        }
        return {
          ...current,
          [captureKey]: previewUrl,
        };
      });
    }

    setLoadingKey(`fleet-${entidadTipo}-${entidadId}-${tipoDocumento}`);
    try {
      await ensureTransportEntityExists({
        entidadTipo,
        entidadId,
        entityData,
        dashboardCuit,
        dashboardData,
      });

      const saveResponse = await postForm(
        "/documentos",
        {
          entidad_tipo: entidadTipo,
          entidad_id: entidadId,
          tipo_documento: tipoDocumento,
        },
        { archivo: file },
        {
          timeoutMs: 60000,
          timeoutMessage: "La carga del documento demoro demasiado. Reintenta la captura.",
        }
      );
      const savedDocument = saveResponse?.data || null;
      if (savedDocument) {
        setDashboardData((current) => upsertFleetDocument(current, entidadTipo, entidadId, savedDocument));
      }

      try {
        const response = await getJson(`/portal-transporte/titulares/${dashboardCuit}/dashboard`);
        setDashboardData(response.data);
      } catch (_error) {
        // Keep optimistic local state if dashboard refresh is delayed or still mocked.
      }

      setMessages((current) => ({ ...current, dashboard: `${humanizeLabel(tipoDocumento)} cargado correctamente.` }));
  } catch (error) {
    setMessages((current) => ({ ...current, dashboard: error.message }));
  } finally {
    setLoadingKey("");
  }
};

  const ensureUnitRtoSnapshot = async (dominio) => {
    if (!dominio || !dashboardCuit) {
      return;
    }

    setUnitRtoLoadingMap((current) => ({ ...current, [dominio]: true }));
    setUnitRtoErrors((current) => ({ ...current, [dominio]: "" }));

    try {
      const response = await postJson(`/portal-transporte/titulares/${dashboardCuit}/unidades/${dominio}/rto-snapshot`, {});
      setUnitRtoSnapshots((current) => ({
        ...current,
        [dominio]: response.data || null,
      }));
    } catch (error) {
      const message = error?.message || "No pudimos consultar el RTO.";
      setUnitRtoErrors((current) => ({ ...current, [dominio]: message }));
    } finally {
      setUnitRtoLoadingMap((current) => ({ ...current, [dominio]: false }));
    }
  };

  const createDetectedUnit = async (files = {}) => {
    if (!dashboardCuit) {
      setMessages((current) => ({ ...current, unidad: "Carga primero el CUIT de la empresa." }));
      return;
    }

    if (!unit.dominio) {
      setMessages((current) => ({ ...current, unidad: "No pudimos detectar un dominio valido todavia." }));
      return;
    }

    const detectedDomain = unitPhotoAnalysis?.datos_extraidos?.dominio || unitBatchAnalysis[0]?.result?.datos_extraidos?.dominio || unit.dominio;
    const persisted = await persistDetectedUnit(unitPhotoAnalysis || unitBatchAnalysis[0]?.result, detectedDomain);
    if (!persisted) {
      return;
    }

    setUnit(initialVehicle);
    setUnitPhotoAnalysis(null);
    setUnitRtoSnapshot(null);
    setUnitRtoLoading(false);
  };

  const persistDetectedUnit = async (analysis, overrideDomain, documentFiles = {}) => {
    if (!dashboardCuit) {
      setMessages((current) => ({ ...current, unidad: "Carga primero el CUIT de la empresa." }));
      return false;
    }

    const detectedDomain = overrideDomain || unit.dominio;
    if (!detectedDomain) {
      setMessages((current) => ({ ...current, unidad: "Necesitamos detectar un dominio en alguna foto antes de dar de alta la unidad." }));
      return false;
    }

    if (hasPersistedDomain(detectedDomain)) {
      setUnitCreatedDomain(detectedDomain);
      setUnitCreationStatus("created");
      return true;
    }

    const entityTarget = resolveDetectedEntityType(analysis, unit.tipo);
    if (pendingUnitRequest?.domain && String(detectedDomain || "").toUpperCase() !== String(pendingUnitRequest.domain || "").toUpperCase()) {
      setMessages((current) => ({
        ...current,
        unidad: `El dominio detectado debe coincidir con la solicitud (${pendingUnitRequest.domain}).`,
        unidadArchivos: `El dominio detectado debe coincidir con la solicitud (${pendingUnitRequest.domain}).`,
      }));
      setUnitCreationStatus("");
      return false;
    }

    if (pendingUnitRequest?.unit_type) {
      const expectedEntityTarget = pendingUnitRequest.unit_type === "tractor_unit" ? "unidad" : "acoplado";
      if (entityTarget !== expectedEntityTarget) {
        setMessages((current) => ({
          ...current,
          unidad: `La foto corresponde a ${expectedEntityTarget === "unidad" ? "una tractora" : "un remolque"} y debe coincidir con la solicitud.`,
          unidadArchivos: `La foto corresponde a ${expectedEntityTarget === "unidad" ? "una tractora" : "un remolque"} y debe coincidir con la solicitud.`,
        }));
        setUnitCreationStatus("");
        return false;
      }
    }

    const path = entityTarget === "acoplado" ? "/acoplados" : "/unidades";
    const key = entityTarget === "acoplado" ? "acoplado" : "unidad";

    setLoadingKey(key);
    setUnitCreationStatus("creating");
    try {
      await postForm(
        path,
        {
          ...unit,
          dominio: detectedDomain,
          titular_cuit: dashboardCuit,
          expected_unit_type: pendingUnitRequest?.unit_type || "",
        },
        documentFiles
      );
      const frontFile = getFirstFile(documentFiles?.cedula_frente);
      if (frontFile) {
        const captureKey = `unidad:${detectedDomain}:cedula_frente`;
        setCapturePreview(captureKey, frontFile);
      }
      try {
        const response = await getJson(`/portal-transporte/titulares/${dashboardCuit}/dashboard`);
        setDashboardData(response.data);
      } catch (_error) {
        // keep current optimistic state if refresh is delayed
      }
      await refreshUnitDocuments(detectedDomain);
      setMessages((current) => ({
        ...current,
        unidad: `Dominio ${detectedDomain} detectado. Ya puedes seguir subiendo el resto de los documentos.`,
        unidadArchivos: `Dominio ${detectedDomain} detectado. Ya puedes seguir subiendo el resto de los documentos.`,
      }));
      if (pendingUnitRequest && String(detectedDomain || "").toUpperCase() === String(pendingUnitRequest.domain || "").toUpperCase()) {
        closePendingUnitRequestAndReturnToRequests(setPendingUnitRequest);
      }
      setUnitCreatedDomain(detectedDomain);
      setUnitCreationStatus("created");
      await fetchUnitRtoSnapshot(detectedDomain);
      return true;
    } catch (error) {
      setMessages((current) => ({ ...current, [key]: error.message, unidad: error.message, unidadArchivos: error.message }));
      setUnitCreationStatus("");
      return false;
    } finally {
      setLoadingKey("");
    }
  };

  const fetchUnitRtoSnapshot = async (domain) => {
    if (!domain || !dashboardCuit) {
      return null;
    }

    setUnitRtoLoading(true);
    try {
      const response = await postJson(`/portal-transporte/titulares/${dashboardCuit}/unidades/${domain}/rto-snapshot`, {});
      setUnitRtoSnapshot(response.data);
      return response.data;
    } catch (error) {
      setMessages((current) => ({ ...current, unidad: error.message }));
      return null;
    } finally {
      setUnitRtoLoading(false);
    }
  };

  const refreshUnitDocuments = async (domain) => {
    if (!domain || !dashboardCuit) {
      return;
    }

    try {
      const response = await getJson(`/documentos?entidad_tipo=unidad&entidad_id=${domain}`);
      const newestDocs = Array.isArray(response.data) ? response.data : [];
      setDashboardData((current) => upsertUnitDocuments(current, domain, newestDocs));
    } catch (_error) {
      // ignore refresh failures
    }
  };

  const saveDetectedDocuments = async (items, contextKey, entityDomain) => {
    const resolvedDomain = entityDomain || unit.dominio;
    if (!items.length || !resolvedDomain) {
      return;
    }

    setLoadingKey(contextKey);
    try {
      for (const item of items) {
        await postForm(
          "/documentos",
          {
            entidad_tipo: resolveDetectedEntityType(item.analysis || item.result, unit.tipo),
            entidad_id: resolvedDomain,
            tipo_documento: item.tipoDocumento,
            estado: (item.analysis || item.result)?.legible ? "vigente" : "observado",
          },
          { archivo: item.file },
          {
            timeoutMs: 60000,
            timeoutMessage: "La carga de uno de los documentos demoro demasiado.",
          }
        );
      }

      try {
        const response = await getJson(`/portal-transporte/titulares/${dashboardCuit}/dashboard`);
        setDashboardData(response.data);
      } catch (_error) {
        // keep current local state if refresh is delayed
      }
      if (
        pendingUnitRequest?.request_kind === "document" &&
        String(pendingUnitRequest.domain || "").toUpperCase() === String(unit.dominio || "").toUpperCase() &&
        items.some((item) => item.tipoDocumento === pendingUnitRequest.document_type)
      ) {
        closePendingUnitRequestAndReturnToRequests(setPendingUnitRequest);
      }
    } finally {
      setLoadingKey("");
    }
  };

  const flushPendingPhotoDocuments = async (currentDomain) => {
    if (!pendingPhotoDocuments.length || !currentDomain) {
      return [];
    }

    const itemsToFlush = [...pendingPhotoDocuments];
    await saveDetectedDocuments(itemsToFlush, "unidad-foto-guardar", currentDomain);
    setPendingPhotoDocuments([]);
    itemsToFlush.forEach((item) => {
      const captureKey = `unidad:${currentDomain}:${item.tipoDocumento}`;
      setCapturePreview(captureKey, item.file);
    });
    return itemsToFlush;
  };

  const analyzeUnitPhoto = async (file, stepIndex = unitPhotoStep) => {
    if (!dashboardCuit) {
      setMessages((current) => ({ ...current, unidad: "Carga primero el CUIT de la empresa antes de procesar la foto." }));
      return;
    }

    const selectedStep = UNIT_PHOTO_STEPS[stepIndex] || UNIT_PHOTO_STEPS[0];
    setUnitPhotoAnalysis(null);
    setUnitCreationStatus("processing");
    markPhotoStep(selectedStep.value, {
      status: "processing",
      file,
      source: "foto",
      label: selectedStep.label,
    });
    setLoadingKey("unidad-foto");
    try {
      const response = await postForm(
        "/documentos/analizar",
        {
          entidad_tipo: "unidad",
          tipo_sugerido: selectedStep.value,
        },
        { archivo: file },
        {
          timeoutMs: 60000,
          timeoutMessage: "El analisis de la cedula demoro demasiado. Reintenta la captura.",
        }
      );
      const analysis = response.data;
      setUnitPhotoAnalysis(analysis);
      setUnit((current) => ({
        ...current,
        dominio: analysis.datos_extraidos?.dominio || current.dominio,
        marca: analysis.datos_extraidos?.marca || current.marca,
        modelo: analysis.datos_extraidos?.modelo || current.modelo,
        tipo: analysis.datos_extraidos?.tipo || current.tipo,
      }));
      setMessages((current) => ({
        ...current,
        unidad: analysis.legible ? "Documento procesado correctamente." : "No pudimos leer bien la foto. Reintenta o usa carga por archivo.",
      }));
      if (analysis.datos_extraidos?.dominio) {
        const detectedDomain = analysis.datos_extraidos?.dominio;
        const alreadyPersisted = hasPersistedDomain(detectedDomain);
        const persisted = await persistDetectedUnit(
          analysis,
          detectedDomain,
          alreadyPersisted ? {} : { cedula_frente: file }
        );
        if (persisted) {
          if (alreadyPersisted) {
            await saveDetectedDocuments(
              [
                {
                  file,
                  analysis,
                  tipoDocumento: selectedStep.value,
                },
              ],
              "unidad-foto-guardar",
              detectedDomain
            );
            const captureKey = `unidad:${detectedDomain}:${selectedStep.value}`;
            setCapturePreview(captureKey, file);
          }
          await flushPendingPhotoDocuments(detectedDomain);
          markPhotoStep(selectedStep.value, {
            status: "done",
            file,
            analysis,
            source: "foto",
          });
          setMessages((current) => ({
            ...current,
            unidad: `Dominio ${detectedDomain} detectado. Unidad creada y documento guardado.`,
          }));
        }
      } else {
        setPendingPhotoDocuments((current) => [
          ...current.filter((item) => item.tipoDocumento !== selectedStep.value),
          {
            file,
            analysis,
            tipoDocumento: selectedStep.value,
          },
        ]);
        setUnitCreationStatus("ready");
        markPhotoStep(selectedStep.value, {
          status: analysis.legible ? "done" : "error",
          file,
          analysis,
          source: "foto",
        });
      }
      setUnitPhotoStep(Math.min(stepIndex + 1, UNIT_PHOTO_STEPS.length - 1));
      setUnitPhotoCaptureStepIndex(null);
    } catch (error) {
      setMessages((current) => ({ ...current, unidad: error.message }));
      setUnitCreationStatus("");
      markPhotoStep(selectedStep.value, {
        status: "error",
        file,
        source: "foto",
      });
      setUnitPhotoCaptureStepIndex(null);
    } finally {
      setLoadingKey("");
    }
  };

  const analyzeUnitBatch = async (files, batchType = unitBatchType) => {
    if (!files.length) {
      setMessages((current) => ({ ...current, unidadArchivos: "Selecciona al menos un archivo." }));
      return;
    }

    setUnitBatchFiles(files);
    setUnitBatchPending(files.map((file, index) => ({ id: `${file.name}-${index}`, file })));
    setUnitBatchAnalysis([]);
    setUnitCreationStatus("processing");
    setLoadingKey("unidad-archivos");
    setMessages((current) => ({ ...current, unidadArchivos: "" }));

    try {
      const tipoSugerido =
        batchType === "nueva_unidad"
          ? "cedula_frente"
          : batchType === "cedula_dorso"
            ? "cedula_dorso"
            : batchType === "rto"
              ? "rto"
              : "seguro_camion";

      const results = [];
      for (let index = 0; index < files.length; index += 1) {
        setUnitBatchProgress({
          current: index + 1,
          total: files.length,
          fileName: files[index]?.name || "",
        });
        const response = await postForm(
          "/documentos/analizar",
          {
            entidad_tipo: "unidad",
            tipo_sugerido: tipoSugerido,
          },
          { archivo: files[index] },
          {
            timeoutMs: 60000,
            timeoutMessage: "El analisis de uno de los archivos demoro demasiado. Reintenta la carga.",
          }
        );
        results.push({
          id: `${files[index].name}-${index}`,
          file: files[index],
          result: response.data,
        });
      }

      const sorted = sortUnitBatchResults(results, batchType);
      setUnitBatchAnalysis(sorted);
      setUnitBatchPending([]);
      markBatchDocumentStatuses(sorted, batchType);

      if (batchType === "nueva_unidad" || batchType === "cedula_dorso") {
        const best = sorted.find((item) => item.result?.datos_extraidos?.dominio);
        if (best) {
          setUnit((current) => ({
            ...current,
            dominio: best.result.datos_extraidos?.dominio || current.dominio,
            marca: best.result.datos_extraidos?.marca || current.marca,
            modelo: best.result.datos_extraidos?.modelo || current.modelo,
            tipo: best.result.datos_extraidos?.tipo || current.tipo,
          }));
          const batchDomain = best.result.datos_extraidos?.dominio;
          const persisted = await persistDetectedUnit(best.result, batchDomain);
          if (persisted) {
            await saveDetectedDocuments(mapBatchItemsToDocuments(sorted, batchType), "unidad-archivos-guardar", unit.dominio);
          }
        } else {
          setUnitCreationStatus("ready");
        }
      } else if (unit.dominio && hasPersistedDomain(unit.dominio)) {
        await saveDetectedDocuments(mapBatchItemsToDocuments(sorted, batchType), "unidad-archivos-guardar", unit.dominio);
      } else {
        setUnitCreationStatus("ready");
      }

      setMessages((current) => ({ ...current, unidadArchivos: "Archivos procesados. Si detectamos un dominio, la unidad ya quedo creada y los documentos se guardaron automaticamente." }));
    } catch (error) {
      setUnitBatchPending([]);
      setMessages((current) => ({ ...current, unidadArchivos: error.message }));
      setUnitCreationStatus("");
    } finally {
      setLoadingKey("");
      setUnitBatchProgress(null);
    }
  };

  const saveUnitBatchDocuments = async () => {
    if (!dashboardCuit || !unit.dominio) {
      setMessages((current) => ({ ...current, unidadArchivos: "Necesitamos CUIT empresa y dominio detectado antes de guardar documentos." }));
      return;
    }

    setLoadingKey("unidad-archivos-guardar");
    try {
      if (unitBatchType === "nueva_unidad" || unitBatchType === "cedula_dorso") {
        await createDetectedUnit({});

        for (let index = 0; index < unitBatchAnalysis.length; index += 1) {
          const item = unitBatchAnalysis[index];
          const tipoDocumento = getUnitDocumentTypesForBatch(unitBatchType, unitBatchAnalysis.length)[index] || "cedula_frente";
          await postForm(
            "/documentos",
            {
              entidad_tipo: "unidad",
              entidad_id: unit.dominio,
              tipo_documento: tipoDocumento,
              estado: item.result.legible ? "vigente" : "observado",
            },
            { archivo: item.file },
            { timeoutMs: 60000, timeoutMessage: "La carga de uno de los archivos demoro demasiado." }
          );
        }
      } else {
        const tipoDocumento = unitBatchType === "rto" ? "rto" : "seguro_camion";
        for (const item of unitBatchAnalysis) {
          await postForm(
            "/documentos",
            {
              entidad_tipo: "unidad",
              entidad_id: unit.dominio,
              tipo_documento: tipoDocumento,
              estado: item.result.legible ? "vigente" : "observado",
            },
            { archivo: item.file },
            { timeoutMs: 60000, timeoutMessage: "La carga de uno de los archivos demoro demasiado." }
          );
        }
      }

      const response = await getJson(`/portal-transporte/titulares/${dashboardCuit}/dashboard`);
      setDashboardData(response.data);
      setUnitBatchAnalysis([]);
      setUnitBatchFiles([]);
      setUnitBatchPending([]);
      setMessages((current) => ({ ...current, unidadArchivos: "Archivos guardados correctamente." }));
    } catch (error) {
      setMessages((current) => ({ ...current, unidadArchivos: error.message }));
    } finally {
      setLoadingKey("");
    }
  };

  const normalizedFilter = domainFilter.trim().toUpperCase();
  const dashboardUnits = dashboardData?.unidades || [];
  const dashboardTrailers = dashboardData?.acoplados || [];
  const filteredUnits = dashboardUnits.filter((unidad) => {
    if (!normalizedFilter) {
      return true;
    }
    return String(unidad.dominio || "").toUpperCase().includes(normalizedFilter);
  });
  const filteredTrailers = dashboardTrailers.filter((acoplado) => {
    if (!normalizedFilter) {
      return true;
    }
    return String(acoplado.dominio || "").toUpperCase().includes(normalizedFilter);
  });
  const inventoryFromDashboard = [
    ...filteredUnits.map((unidad) => ({ ...unidad, entityType: "unidad" })),
    ...filteredTrailers.map((acoplado) => ({ ...acoplado, entityType: "acoplado" })),
  ];
  const fallbackInventoryItems = recentFleetItems
    .filter((item) => {
      if (!normalizedFilter) {
        return true;
      }
      return String(item.domain || "").toUpperCase().includes(normalizedFilter);
    })
    .map((item) => ({
      dominio: item.domain,
      marca: item.marca || "",
      modelo: item.modelo || "",
      anio: item.anio || "",
      tipo: item.type || (item.entityType === "acoplado" ? "Remolque" : "Unidad"),
      updated_at: item.updated_at,
      entityType: item.type?.toLowerCase().includes("remolque") ? "acoplado" : "unidad",
      documentos: item.documentos || [],
    }))
    .sort((left, right) => {
      const leftDate = new Date(left.updated_at || left.created_at || 0).getTime();
      const rightDate = new Date(right.updated_at || right.created_at || 0).getTime();
      if (rightDate !== leftDate) {
        return rightDate - leftDate;
      }
      return String(left.dominio || "").localeCompare(String(right.dominio || ""));
    });
  const hasDashboardFleet = inventoryFromDashboard.length > 0;
  const inventoryItems = hasDashboardFleet
    ? inventoryFromDashboard.sort((left, right) => {
        const leftDate = new Date(left.created_at || left.updated_at || 0).getTime();
        const rightDate = new Date(right.created_at || right.updated_at || 0).getTime();
        if (rightDate !== leftDate) {
          return rightDate - leftDate;
        }
        return String(left.dominio || "").localeCompare(String(right.dominio || ""));
      })
    : fallbackInventoryItems;
  const batchOptions = [
    {
      value: "nueva_unidad",
      label: "Cedula frente",
    },
    {
      value: "cedula_dorso",
      label: "Cedula dorso",
    },
    {
      value: "rto",
      label: "RTO",
    },
    {
      value: "seguro_camion",
      label: "Seguro",
    },
  ];

  return (
    <PortalLayout
      eyebrow="Portal Empresa de Transporte"
      title="Unidades"
      description="Aqui ves las unidades validadas y puedes dar de alta tractores o acoplados por foto o por archivo."
      backHref="#/portal-empresa-transporte/app"
    >
      {pendingUnitRequest ? (
        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>{pendingUnitRequest.request_kind === "document" ? "Solicitud documental en curso" : "Solicitud de alta en curso"}</h2>
            <StatusBadge
              label={
                pendingUnitRequest.request_kind === "document"
                  ? getUnitDocumentLabel(pendingUnitRequest.document_type)
                  : getUnitRegistrationLabel(pendingUnitRequest.unit_type)
              }
              tone="faltante"
            />
          </div>
          <div className="panel-body">
            {pendingUnitRequest.request_kind === "document" ? (
              <p>
                Debes cargar <strong>{getUnitDocumentLabel(pendingUnitRequest.document_type).toLowerCase()}</strong> para el dominio{" "}
                <strong>{pendingUnitRequest.domain}</strong> de la empresa <strong>{pendingUnitRequest.transport_company_id || dashboardCuit}</strong>.
              </p>
            ) : (
              <>
                <p>
                  Debes dar de alta el dominio <strong>{pendingUnitRequest.domain}</strong> para la empresa de transporte{" "}
                  <strong>{pendingUnitRequest.transport_company_id || dashboardCuit}</strong>.
                </p>
                <p>La foto de la cédula debe coincidir con el dominio solicitado y con el tipo de unidad requerido.</p>
              </>
            )}
            <div className="biometry-actions">
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  closePendingUnitRequestAndReturnToRequests(setPendingUnitRequest);
                }}
              >
                Cerrar solicitud
              </button>
            </div>
          </div>
        </section>
      ) : null}
      {creationMode === "foto" && unitPhotoCaptureOpen ? (
        <div
          className="capture-modal-backdrop"
          role="presentation"
          onClick={() => {
            setUnitPhotoCaptureOpen(false);
            setUnitPhotoCaptureStepIndex(null);
          }}
        >
          <section className="detail-panel capture-modal-panel" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <div className="detail-panel-header">
              <h2>{currentCapturePhotoStep ? currentCapturePhotoStep.label : "Elegí el documento"}</h2>
              <div className="detail-panel-header-actions">
                <StatusBadge label={currentCapturePhotoStep ? "Captura activa" : "Paso 1"} tone="faltante" />
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => {
                    setUnitPhotoCaptureOpen(false);
                    setUnitPhotoCaptureStepIndex(null);
                  }}
                >
                  Cerrar
                </button>
              </div>
            </div>
            <div className="panel-body">
              {currentCapturePhotoStep ? (
                <CameraCapture
                  mode="documento"
                  title={currentCapturePhotoStep.label}
                  description="Confirmas la foto y procesamos ese documento."
                  onCancel={() => setUnitPhotoCaptureStepIndex(null)}
                  onConfirm={(file) => {
                    setUnitPhotoCaptureOpen(false);
                    analyzeUnitPhoto(file, unitPhotoCaptureStepIndex);
                  }}
                />
              ) : (
                <div className="portal-list">
                  {UNIT_PHOTO_STEPS.map((step, index) => {
                    const stepState = unitPhotoStatuses[step.value] || {};
                    const status =
                      stepState.status || (index < unitPhotoStep ? "done" : index === unitPhotoStep ? "current" : "pending");
                    const tone = status === "done" ? "vigente" : status === "error" ? "vencido" : null;
                    const label = status === "done" ? "Listo" : status === "error" ? "Reintentar" : "";

                    return (
                      <button
                        className="fleet-upload-option-button submit-button"
                        key={`capture-step-${step.value}`}
                        type="button"
                        onClick={() => setUnitPhotoCaptureStepIndex(index)}
                      >
                        <strong>{step.label}</strong>
                        {label ? <StatusBadge label={label} tone={tone} /> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>
      ) : null}
      <section className="portal-section-grid">
        <section className="detail-panel new-unit-panel">
          <div className="detail-panel-header">
            <h2>+ Nueva unidad</h2>
          </div>
          <div className="biometry-actions new-unit-actions">
            <button
              className={creationMode === "foto" ? "submit-button" : "secondary-button"}
              type="button"
              onClick={() => {
                setCreationMode("foto");
                setUnitPhotoCaptureStepIndex(null);
                setUnitPhotoCaptureOpen(true);
              }}
            >
              Tomar foto
            </button>
            <button className={creationMode === "archivo" ? "submit-button" : "secondary-button"} type="button" onClick={() => setCreationMode("archivo")}>
              Subir archivos
            </button>
          </div>
        </section>

        {creationMode === "foto" ? (
        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>Alta de nueva unidad por foto</h2>
            <StatusBadge label="Celular recomendado" tone="faltante" />
          </div>
          <div className="panel-body">
            <p>Usa esta opcion desde un celular. Sacas la foto de la cedula y completamos el alta con OCR y RTO mock.</p>
            <div className="biometry-actions">
              <button
                className="submit-button"
                type="button"
                onClick={() => {
                  setUnitPhotoCaptureStepIndex(null);
                  setUnitPhotoCaptureOpen(true);
                }}
              >
                Tomar foto
              </button>
              <button
                className="secondary-button"
                type="button"
                onClick={() => {
                  setCreationMode("");
                  setUnitPhotoCaptureOpen(false);
                }}
              >
                Cerrar
              </button>
            </div>
            <div className="portal-list compact-list">
              {UNIT_PHOTO_STEPS.map((step, index) => {
                const status = unitPhotoStatuses[step.value]?.status || (index < unitPhotoStep ? "done" : index === unitPhotoStep ? "current" : "pending");
                const tone = status === "done" ? "vigente" : status === "error" ? "vencido" : status === "processing" ? "faltante" : index === unitPhotoStep ? "faltante" : "neutro";
                const label = status === "done" ? "Listo" : status === "error" ? "Reintentar" : status === "processing" ? "Procesando" : index === unitPhotoStep ? "Actual" : "Pendiente";

                return (
                  <div className="portal-list-card" key={step.value}>
                    <strong>{step.label}</strong>
                    <p>{label}</p>
                    <StatusBadge label={label} tone={tone === "neutro" ? "faltante" : tone} />
                  </div>
                );
              })}
            </div>
            {unitCreationStatus === "processing" ? (
              <ProcessingPanel title="Procesando foto" message="Estamos leyendo la cédula. Si detectamos un dominio, creamos la unidad y guardamos el documento." />
            ) : null}
            {unitCreatedDomain ? (
              <div className="detail-feedback">
                <div className="detail-panel-header">
                  <h2>Dominio detectado</h2>
                  <StatusBadge label="Unidad creada" tone="vigente" />
                </div>
                <div className="detail-table">
                  <div className="detail-row">
                    <span>Dominio</span>
                    <strong>{unitCreatedDomain}</strong>
                  </div>
                </div>
              </div>
            ) : null}
            {unitPhotoAnalysis ? (
              <section className="detail-panel biometria-result-card">
                <div className="detail-panel-header">
                  <h2>Lectura de cedula</h2>
                  <StatusBadge label={unitPhotoAnalysis.tipo_label || "Sin clasificar"} tone={unitPhotoAnalysis.legible ? "vigente" : "vencido"} />
                </div>
                <div className="detail-table">
                  <div className="detail-row">
                    <span>Dominio</span>
                    <strong>{unitPhotoAnalysis.datos_extraidos?.dominio || "-"}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Marca</span>
                    <strong>{unitPhotoAnalysis.datos_extraidos?.marca || "-"}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Modelo</span>
                    <strong>{unitPhotoAnalysis.datos_extraidos?.modelo || "-"}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Tipo</span>
                    <strong>{unitPhotoAnalysis.datos_extraidos?.tipo || "-"}</strong>
                  </div>
                  <div className="detail-row">
                    <span>RTO mock</span>
                    <strong>{unitPhotoAnalysis.validacion_rto?.estado_final || "-"}</strong>
                  </div>
                  <div className="detail-row">
                    <span>Tipo sugerido</span>
                    <strong>{unitPhotoAnalysis.entidad_sugerida === "acoplado" ? "Acoplado / semi-remolque" : "Tractor"}</strong>
                  </div>
                </div>
              </section>
            ) : null}
            <StatusMessage message={messages.unidad} />
          </div>
        </section>
        ) : null}

        {creationMode === "archivo" ? (
        <FormCard
          className="unit-upload-form"
          title="Alta de nueva unidad por archivo"
          description="Si estas en escritorio, sube archivos y te ayudamos a interpretarlos. Cada fila corresponde a un documento distinto."
          onSubmit={(event) => {
            event.preventDefault();
            saveUnitBatchDocuments();
          }}
        >
          <input
            ref={batchInputRef}
            type="file"
            multiple
            hidden
          onChange={(event) => {
            const files = Array.from(event.target.files || []);
            setUnitBatchFiles(files);
            if (files.length) {
              analyzeUnitBatch(files, unitBatchType);
            }
            event.target.value = "";
          }}
        />
          <div className="portal-list unit-upload-list">
            {batchOptions.map((option) => {
              const isActive = unitBatchType === option.value;
              const fileCount = isActive ? unitBatchFiles.length : 0;
              const isProcessingThisType =
                isActive && loadingKey === "unidad-archivos" && unitBatchProgress;
              const progressMessage = isProcessingThisType
                ? `Procesando ${unitBatchProgress.current}/${unitBatchProgress.total}${
                    unitBatchProgress.fileName ? ` • ${unitBatchProgress.fileName}` : ""
                  }`
                : fileCount
                  ? `${fileCount} archivo(s) listos para analizar.`
                  : null;
              const buttonLabel = fileCount
                ? isProcessingThisType
                  ? "Procesando..."
                  : "Cambiar archivos"
                : "Subir archivo";

              return (
                <div className="portal-list-card fleet-upload-option" key={option.value}>
                  <div>
                    <strong>{option.label}</strong>
                    {progressMessage ? <p>{progressMessage}</p> : null}
                  </div>
                  <button
                    className={isActive && fileCount ? "submit-button" : "secondary-button"}
                    type="button"
                    onClick={() => {
                      setUnitBatchType(option.value);
                      setUnitBatchFiles([]);
                      setUnitBatchAnalysis([]);
                      setUnitBatchPending([]);
                      setMessages((current) => ({ ...current, unidadArchivos: "" }));
                      if (batchInputRef.current) {
                        batchInputRef.current.value = "";
                        batchInputRef.current.click();
                      }
                    }}
                  >
                    {buttonLabel}
                  </button>
                </div>
              );
            })}
          </div>
          {loadingKey === "unidad-archivos" || loadingKey === "unidad-archivos-guardar" ? (
            <>
              <ProcessingPanel
                title="Procesando archivos"
                message={
                  unitBatchProgress && loadingKey === "unidad-archivos"
                    ? `Procesando ${unitBatchProgress.current}/${unitBatchProgress.total}${
                        unitBatchProgress.fileName ? ` • ${unitBatchProgress.fileName}` : ""
                      }`
                    : "Estamos leyendo los documentos. Si detectamos un dominio, creamos la unidad y guardamos los archivos automaticamente."
                }
              />
              {unitBatchPending.length ? (
                <UnitBatchPendingCard unitBatchType={unitBatchType} files={unitBatchPending} />
              ) : null}
            </>
          ) : null}
          {unitBatchAnalysis.length ? (
            <UnitBatchAnalysisCard unitBatchType={unitBatchType} unitBatchAnalysis={unitBatchAnalysis} unit={unit} />
          ) : null}
          <StatusMessage message={messages.unidadArchivos} />
          {unitRtoLoading ? (
            <ProcessingPanel title="Consultando RTO" message={`Buscando información oficial para el dominio ${unitCreatedDomain || unit.dominio || "-"}...`} />
          ) : null}
       </FormCard>
       ) : null}

        {creationMode ? (
          <>
            <section className="detail-panel">
              <div className="detail-panel-header">
                <h2>Documentos de la unidad</h2>
              </div>
              <div className="panel-body">
                <div className="portal-list compact-list">
                  {UNIT_PHOTO_STEPS.map((step, index) => {
                    const stepState = unitPhotoStatuses[step.value] || {};
                    const status =
                      stepState.status ||
                      (index < unitPhotoStep ? "done" : index === unitPhotoStep ? "current" : "pending");
                    const tone =
                      status === "done"
                        ? "vigente"
                        : status === "error"
                          ? "vencido"
                          : status === "processing"
                            ? "faltante"
                            : index === unitPhotoStep
                              ? "faltante"
                              : "neutro";
                    const label =
                      status === "done"
                        ? "Listo"
                        : status === "error"
                          ? "Reintentar"
                          : status === "processing"
                            ? "Procesando"
                            : index === unitPhotoStep
                              ? "Actual"
                              : "Pendiente";
                    const isRtoStep = step.value === "rto";
                    const rtoInfo = getRtoStepStatus(unitRtoSnapshot, unitPhotoStatuses.rto);
                    const badgeLabel = isRtoStep ? rtoInfo.label : label;
                    const badgeTone = isRtoStep ? rtoInfo.tone : tone === "neutro" ? "faltante" : tone;
                    const isExpanded = expandedDocumentStep === step.value;

                    return (
                      <div key={`unit-doc-status-${step.value}`} className="document-status-wrapper">
                        <div
                          className="portal-list-card document-status-card"
                          role="button"
                          tabIndex={0}
                          onClick={() =>
                            setExpandedDocumentStep((prev) => (prev === step.value ? null : step.value))
                          }
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              setExpandedDocumentStep((prev) => (prev === step.value ? null : step.value));
                            }
                          }}
                        >
                          <strong>{step.label}</strong>
                          <p>{badgeLabel}</p>
                          <StatusBadge label={badgeLabel} tone={badgeTone} />
                        </div>
                        {isExpanded ? (
                          <div className="detail-panel document-summary-panel">
                            <div className="detail-panel-header">
                              <h2>Resumen del documento</h2>
                              <button className="secondary-button" type="button" onClick={() => setExpandedDocumentStep(null)}>
                                Cerrar
                              </button>
                            </div>
                            <div className="panel-body">
                              <div className="detail-table">
                                <div className="detail-row">
                                  <span>Dominio</span>
                                  <strong>
                                    {unitPhotoAnalysis?.datos_extraidos?.dominio || unitCreatedDomain || unit.dominio || "-"}
                                  </strong>
                                </div>
                                <div className="detail-row">
                                  <span>Marca</span>
                                  <strong>
                                    {unitPhotoAnalysis?.datos_extraidos?.marca || unitRtoSnapshot?.rto?.marca || "-"}
                                  </strong>
                                </div>
                                <div className="detail-row">
                                  <span>Modelo</span>
                                  <strong>
                                    {unitPhotoAnalysis?.datos_extraidos?.modelo || unitRtoSnapshot?.rto?.modelo || "-"}
                                  </strong>
                                </div>
                                <div className="detail-row">
                                  <span>RTO estado</span>
                                  <strong>
                                    {unitRtoSnapshot?.document?.validacion?.status === "approved"
                                      ? "Validado"
                                      : unitRtoSnapshot?.document?.estado || "Pendiente"}
                                  </strong>
                                </div>
                                <div className="detail-row">
                                  <span>Vencimiento</span>
                                  <strong>{unitRtoSnapshot?.rto?.fecha_vencimiento || "-"}</strong>
                                </div>
                                <div className="detail-row">
                                  <span>Certificado</span>
                                  <strong>{unitRtoSnapshot?.rto?.certificado_numero || "-"}</strong>
                                </div>
                              </div>
                              {unitPhotoAnalysis?.datos_extraidos?.dominio ? (
                                <div className="axis summary-note">
                                  {unitRtoSnapshot?.rto
                                    ? "El RTO fue validado automáticamente con la API oficial."
                                    : "Falta consultar la API del RTO. Intentá de nuevo o revisá la conexión."}
                                </div>
                              ) : (
                                <div className="axis summary-note">No hay dominio detectado aún. Tomá la foto para generar datos.</div>
                              )}
                              {isRtoStep && unitRtoSnapshot?.rto ? (
                                <section className="detail-panel detail-panel-nested">
                                  <div className="detail-panel-header">
                                    <h3>RTO oficial</h3>
                                    <StatusBadge
                                      label={
                                        unitRtoSnapshot.document?.validacion?.status === "approved"
                                          ? "Validado"
                                          : "Pendiente / observado"
                                      }
                                      tone={
                                        unitRtoSnapshot.document?.validacion?.status === "approved"
                                          ? "vigente"
                                          : "faltante"
                                      }
                                    />
                                  </div>
                                  <div className="panel-body">
                                    <div className="detail-table">
                                      <div className="detail-row">
                                        <span>Dominio</span>
                                        <strong>{unitRtoSnapshot.rto?.dominio || unitCreatedDomain || unit.dominio || "-"}</strong>
                                      </div>
                                      <div className="detail-row">
                                        <span>Marca</span>
                                        <strong>{unitRtoSnapshot.rto?.marca || "-"}</strong>
                                      </div>
                                      <div className="detail-row">
                                        <span>Modelo</span>
                                        <strong>{unitRtoSnapshot.rto?.modelo || "-"}</strong>
                                      </div>
                                      <div className="detail-row">
                                        <span>Vence</span>
                                        <strong>{unitRtoSnapshot.rto?.fecha_vencimiento || "-"}</strong>
                                      </div>
                                      <div className="detail-row">
                                        <span>Certificado</span>
                                        <strong>{unitRtoSnapshot.rto?.certificado_numero || "-"}</strong>
                                      </div>
                                    </div>
                                  </div>
                                </section>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </>
        ) : null}
      </section>

      <section className="detail-panel detail-panel-wide fleet-inventory-panel">
        <div className="detail-panel-header">
          <h2>Dominios dados de alta</h2>
        </div>
        <div className="detail-table">
          <label className="field">
            <span>Filtro por dominio</span>
            <input value={domainFilter} onChange={(event) => setDomainFilter(event.target.value.toUpperCase())} placeholder="AA123BB" />
          </label>
        </div>
        <StatusMessage message={messages.dashboard} />
        {!dashboardData ? (
          <div className="portal-list">
            <PortalListItem title="Todavia no tienes unidades dadas de alta" description="Carga el CUIT de la empresa y da de alta la primera unidad por foto o por archivo." />
          </div>
        ) : (
          <>
            <div className="fleet-inventory-list">
              {inventoryItems.length ? (
                inventoryItems.map((item) => {
                  const snapshot = unitRtoSnapshots[item.dominio];
                  const mergedItem = mergeUnitWithRtoData(item, snapshot);
                  return (
                    <FleetEntityCard
                      key={`${item.entityType}-${item.dominio}`}
                      title={item.dominio}
                      subtitle={`${mergedItem.marca || "-"} ${mergedItem.modelo || ""}`.trim()}
                      tone={item.entityType === "acoplado" ? "faltante" : "vigente"}
                      entityType={item.entityType}
                      entityId={item.dominio}
                      entityData={item}
                      rows={buildRows(mergedItem, ["dominio", "marca", "modelo", "anio", "tipo"])}
                      documents={item.documentos || []}
                      openingDocumentId={openingDocumentId}
                      fleetPreviewUrls={fleetPreviewUrls}
                      onOpenDocument={openDocument}
                      activeCaptureKey={activeFleetCaptureKey}
                      onToggleCapture={setActiveFleetCaptureKey}
                      onUploadDocument={uploadFleetDocument}
                      documentPolicyCatalog={documentPolicyCatalog}
                      rtoSnapshot={snapshot}
                      rtoLoading={unitRtoLoadingMap[item.dominio]}
                      rtoError={unitRtoErrors[item.dominio]}
                      onEnsureUnitRtoSnapshot={() => ensureUnitRtoSnapshot(item.dominio)}
                    />
                  );
                })
              ) : (
                <PortalListItem
                  title="Sin resultados"
                  description={normalizedFilter ? "No encontramos dominios que coincidan con ese filtro." : "Todavia no hay dominios cargados para esta empresa."}
                />
              )}
            </div>
          </>
        )}
      </section>
    </PortalLayout>
  );
}

export function PortalEmpresaTransporteDebugScreen() {
  const documentPolicyCatalog = usePortalDocumentPolicyCatalog();
  const [form, setForm] = useState({
    entidad_tipo: "chofer",
    entidad_id: "",
  });
  const [capturedFile, setCapturedFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [selectedHint, setSelectedHint] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const quickOptions = getTransportDocumentOptions(form.entidad_tipo, documentPolicyCatalog);

  async function analyzeFromCapture(file, hint = "") {
    if (!form.entidad_tipo || !form.entidad_id || !file) {
      setMessage("Completa entidad e identificador antes de confirmar la foto.");
      return;
    }

    setCapturedFile(file);
    setSelectedHint(hint);
    setLoading(true);
    setMessage("");
    setAnalysis(null);

    try {
      const response = await postForm(
        "/documentos/analizar",
        {
          entidad_tipo: form.entidad_tipo,
          tipo_sugerido: hint,
        },
        { archivo: file },
        {
          timeoutMs: 60000,
          timeoutMessage: "El analisis del documento demoro demasiado. Reintenta la captura.",
        }
      );
      setAnalysis(response.data);
      setMessage(
        response.data.legible
          ? "Revisa lo detectado y confirma si corresponde."
          : "No pudimos leer bien la foto. Puedes reintentar o guardarla como observada."
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveDocument(forceObserved = false) {
    const tipoDocumento = analysis?.tipo_detectado || selectedHint;

    if (!form.entidad_tipo || !form.entidad_id || !capturedFile || !tipoDocumento) {
      setMessage("No pudimos identificar el documento. Indica que estas intentando subir y vuelve a intentarlo.");
      return;
    }

    setSaving(true);
    setMessage("");

    try {
      await postForm(
        "/documentos",
        {
          entidad_tipo: form.entidad_tipo,
          entidad_id: form.entidad_id,
          tipo_documento: tipoDocumento,
          fecha_vencimiento: analysis?.fecha_vencimiento || "",
          estado: forceObserved || !analysis?.legible ? "observado" : "vigente",
        },
        { archivo: capturedFile },
        {
          timeoutMs: 60000,
          timeoutMessage: "La carga del documento demoro demasiado. Reintenta la captura.",
        }
      );
      setMessage(`${humanizeLabel(tipoDocumento)} guardado correctamente.`);
      setAnalysis(null);
      setCapturedFile(null);
      setSelectedHint("");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  }

  const needsHint = Boolean(capturedFile) && (!analysis?.tipo_detectado || analysis.confianza < 0.6);

  return (
    <PortalLayout
      eyebrow="Portal Empresa de Transporte"
      title="Carga agil de documentos"
      description="Subes una foto, intentamos reconocer el documento y te pedimos confirmacion antes de guardarlo."
      backHref="#/portal-empresa-transporte/app"
    >
      <section className="detail-panel detail-panel-wide">
        <div className="detail-panel-header">
          <h2>Carga por foto</h2>
          <StatusBadge label="Modo agil" tone="faltante" />
        </div>
        <div className="panel-body">
          <label className="field">
            <span>Entidad</span>
            <select
              value={form.entidad_tipo}
              onChange={(event) => {
                setForm({ ...form, entidad_tipo: event.target.value });
                setSelectedHint("");
                setAnalysis(null);
              }}
            >
              <option value="chofer">chofer</option>
              <option value="unidad">unidad</option>
              <option value="acoplado">acoplado</option>
              <option value="titular">titular</option>
            </select>
          </label>
          <Input label="Identificador de entidad" value={form.entidad_id} onChange={(value) => setForm({ ...form, entidad_id: value })} />
          <CameraCapture
            mode="documento"
            title="Documento"
            description="Confirmas la foto y la analizamos automaticamente."
            onConfirm={(file) => analyzeFromCapture(file, selectedHint)}
          />
          {loading ? <ProcessingPanel title="Analizando documento" message="Estamos leyendo la foto y buscando el tipo de documento." /> : null}
          {!loading && analysis ? (
            <section className="detail-panel biometria-result-card">
              <div className="detail-panel-header">
                <h2>Resultado detectado</h2>
                <StatusBadge
                  label={analysis.tipo_label || "Sin clasificar"}
                  tone={analysis.legible ? (analysis.confianza >= 0.6 ? "vigente" : "faltante") : "vencido"}
                />
              </div>
              <div className="detail-table">
                <div className="detail-row">
                  <span>Tipo detectado</span>
                  <strong>{analysis.tipo_label || "-"}</strong>
                </div>
                <div className="detail-row">
                  <span>Confianza</span>
                  <strong>{Math.round((analysis.confianza || 0) * 100)}%</strong>
                </div>
                {analysis.datos_extraidos?.dominio ? (
                  <div className="detail-row">
                    <span>Dominio</span>
                    <strong>{analysis.datos_extraidos.dominio}</strong>
                  </div>
                ) : null}
                {analysis.datos_extraidos?.dni ? (
                  <div className="detail-row">
                    <span>DNI</span>
                    <strong>{analysis.datos_extraidos.dni}</strong>
                  </div>
                ) : null}
                {analysis.fecha_vencimiento ? (
                  <div className="detail-row">
                    <span>Fecha de vencimiento</span>
                    <strong>{analysis.fecha_vencimiento}</strong>
                  </div>
                ) : null}
                {analysis.validacion_rto?.estado_final ? (
                  <div className="detail-row">
                    <span>RTO mock</span>
                    <strong>{humanizeLabel(analysis.validacion_rto.estado_final)}</strong>
                  </div>
                ) : null}
              </div>
              {analysis.observaciones?.length ? (
                <div className="portal-list">
                  {analysis.observaciones.map((item) => (
                    <PortalListItem key={item} title="Observacion" description={item} />
                  ))}
                </div>
              ) : null}
              {needsHint ? (
                <div className="access-method-grid">
                  {quickOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`portal-list-card access-method-card ${selectedHint === option.value ? "access-method-card-active" : ""}`}
                      type="button"
                      onClick={() => analyzeFromCapture(capturedFile, option.value)}
                    >
                      <strong>{option.label}</strong>
                      <p>{option.description}</p>
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="biometry-actions">
                {analysis.se_puede_guardar ? (
                  <button className="submit-button" type="button" onClick={() => saveDocument(false)} disabled={saving}>
                    {saving ? "Guardando..." : "Confirmar y guardar"}
                  </button>
                ) : null}
                {(selectedHint || analysis.tipo_detectado) && (!analysis.legible || needsHint) ? (
                  <button className="secondary-button" type="button" onClick={() => saveDocument(true)} disabled={saving}>
                    {saving ? "Guardando..." : "Guardar igualmente"}
                  </button>
                ) : null}
              </div>
            </section>
          ) : null}
          {!loading && capturedFile && !analysis ? <CaptureResultCard label="Ultima captura" file={capturedFile} emptyMessage="Todavia no hay captura confirmada." /> : null}
          {saving ? <ProcessingPanel title="Guardando documento" message="Estamos asociando la foto con la entidad elegida." /> : null}
          <StatusMessage message={message} />
        </div>
      </section>
    </PortalLayout>
  );
}

export function PortalEmpresaConsumidoraAuthScreen() {
  return (
    <CompanyAuthScreen
      eyebrow="Portal Empresa Consumidora"
      title="Auth de empresa consumidora"
      description="Alta y login tradicional de la empresa consumidora con CUIT, razon social, email y password."
      primaryHref="#/portal-empresa-consumidora/app"
      primaryLabel="Ingresar al portal de consulta"
      secondaryHref="#/portal-chofer"
      secondaryLabel="Ver portal chofer"
      routeItems={[
        "/portal-empresa-consumidora/auth",
        "/portal-empresa-consumidora/app",
        "/portal-empresa-consumidora/app/eventos/:id",
      ]}
    />
  );
}

export function PortalEmpresaConsumidoraAppScreen() {
  const [eventIdInput, setEventIdInput] = useState("");

  return (
    <PortalLayout
      eyebrow="Portal Empresa Consumidora"
      title="App de consulta segun permisos"
      description="Modulo separado del front principal, orientado a consulta documental y operativa para empresas consumidoras."
      backHref="#/portal-empresa-consumidora/auth"
    >
      <section className="portal-section-grid">
        <section className="detail-panel detail-panel-wide">
          <div className="detail-panel-header">
            <h2>Consulta separada</h2>
          </div>
          <div className="portal-list">
            <PortalListItem title="Aptitud documental" description="Visualiza el estado documental consolidado solo para los casos permitidos." />
            <PortalListItem title="Aptitud operativa" description="Consulta si el evento esta apto para operar en base a documentacion y biometria disponible." />
          </div>
        </section>

        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>Detalle de evento</h2>
          </div>
          <div className="detail-launcher-form detail-launcher-form-vertical">
            <input type="text" value={eventIdInput} placeholder="ID de evento" onChange={(event) => setEventIdInput(event.target.value)} />
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                if (eventIdInput) {
                  window.location.hash = `#/portal-empresa-consumidora/app/eventos/${eventIdInput}`;
                }
              }}
            >
              Consultar aptitud
            </button>
          </div>
        </section>
      </section>
    </PortalLayout>
  );
}

function TransportCompanyAuthScreen() {
  const [view, setView] = useState("register");
  const [form, setForm] = useState(initialTransportAuth);
  const [registerMethod, setRegisterMethod] = useState("");
  const [loginMethod, setLoginMethod] = useState("");
  const [oauthProviders, setOauthProviders] = useState({ google: false, facebook: false });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [registerPhase, setRegisterPhase] = useState("form");
  const [verificationCode, setVerificationCode] = useState("");
  const [forgotPhase, setForgotPhase] = useState("request");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadProviders() {
      try {
        const response = await getJson("/portal-transporte/auth/providers");
        if (!cancelled) {
          setOauthProviders(response.data || { google: false, facebook: false });
        }
      } catch (_error) {
        if (!cancelled) {
          setOauthProviders({ google: false, facebook: false });
        }
      }
    }

    const query = readHashQuery();
    if (query.get("oauth") === "success") {
      const redirectCode = query.get("code") || "";
      if (!redirectCode) {
        setMessage("No pudimos validar la sesión OAuth.");
        return undefined;
      }
      setSubmitting(true);
      postJson("/portal-transporte/auth/oauth/exchange", { code: redirectCode })
        .then((response) => {
          saveTransportSession(response.data);
          setSuccessMessage("Cuenta de transporte iniciada correctamente.");
          window.setTimeout(() => {
            window.location.hash = "#/portal-empresa-transporte/app";
          }, 900);
        })
        .catch((error) => {
          setMessage(error.message || "No pudimos completar el acceso social.");
        })
        .finally(() => {
          setSubmitting(false);
        });
      return undefined;
    }

    if (query.get("oauth") === "error") {
      setMessage(query.get("message") || "No pudimos completar el acceso social.");
    }

    loadProviders();
    return () => {
      cancelled = true;
    };
  }, []);

  async function submitTransportAuth(nextView) {
    setSubmitting(true);
    setMessage("");
    setSuccessMessage("");
    try {
      if (nextView === "register") {
        if (registerMethod !== "email") {
          const error = new Error("Elegí cómo querés crear la cuenta antes de continuar.");
          error.statusCode = 400;
          throw error;
        }
        if (form.password !== confirmPassword) {
          const error = new Error("Las contraseñas no coinciden.");
          error.statusCode = 400;
          throw error;
        }
        const response = await postJson("/portal-transporte/auth/register", {
          email: form.email,
          password: form.password,
        });
        setRegisterPhase("verify");
        setSuccessMessage(response.data?.verification_hint || "Te enviamos un código por email para validar la cuenta.");
        return;
      }

      if (loginMethod !== "email") {
        const error = new Error("Elegí cómo querés ingresar antes de continuar.");
        error.statusCode = 400;
        throw error;
      }

      const response = await postJson("/portal-transporte/auth/login", {
        email: form.email,
        password: form.password,
      });

      saveTransportSession(response.data);
      setSuccessMessage("Ingreso exitoso.");
      window.setTimeout(() => {
        window.location.hash = "#/portal-empresa-transporte/app";
      }, 700);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  function startOauth(provider) {
    setMessage("");
    setSuccessMessage("");
    const url = new URL(`${getAppBaseUrl()}/api/portal-transporte/auth/oauth/${provider}/start`);
    url.searchParams.set("mode", view === "register" ? "register" : "login");
    url.searchParams.set("redirect_to", `${window.location.origin}/#/portal-empresa-transporte/auth`);
    window.location.href = url.toString();
  }

  async function verifyTransportEmail() {
    setSubmitting(true);
    setMessage("");
    setSuccessMessage("");
    try {
      const response = await postJson("/portal-transporte/auth/verify-email", {
        email: form.email,
        code: verificationCode,
      });
      saveTransportSession(response.data);
      setSuccessMessage("Cuenta validada correctamente.");
      window.setTimeout(() => {
        window.location.hash = "#/portal-empresa-transporte/app";
      }, 700);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function resendTransportVerification() {
    setSubmitting(true);
    setMessage("");
    setSuccessMessage("");
    try {
      const response = await postJson("/portal-transporte/auth/resend-verification", {
        email: form.email,
      });
      setSuccessMessage(response.data?.verification_hint || "Te reenviamos un nuevo código.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function requestPasswordReset() {
    setSubmitting(true);
    setMessage("");
    setSuccessMessage("");
    try {
      const response = await postJson("/portal-transporte/auth/forgot-password/request", {
        email: form.email,
      });
      setForgotPhase("confirm");
      setSuccessMessage(response.data?.reset_hint || "Te enviamos un código para restablecer la contraseña.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmPasswordReset() {
    setSubmitting(true);
    setMessage("");
    setSuccessMessage("");
    try {
      await postJson("/portal-transporte/auth/forgot-password/confirm", {
        email: form.email,
        code: resetCode,
        new_password: newPassword,
      });
      setView("login");
      setForgotPhase("request");
      setForm((current) => ({ ...current, password: newPassword }));
      setResetCode("");
      setNewPassword("");
      setSuccessMessage("Contraseña actualizada. Ya puedes ingresar.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
      <PortalLayout
        eyebrow="Portal Empresa de Transporte"
        title="Acceso de empresa de transporte"
        description="Crea tu cuenta, valida el email y accede al portal operativo."
      backHref={getTransportPortalBackHash()}
    >
      <section className="portal-section-grid">
        <FormCard
          title={view === "register" ? (registerPhase === "verify" ? "Validar email" : "Crear cuenta") : view === "forgot" ? "Recuperar acceso" : "Ingresar"}
          description={
            view === "register"
              ? registerPhase === "verify"
                ? "Ingresa el código enviado a tu email para activar la cuenta."
                : "Registra tu cuenta con email y contraseña."
              : view === "forgot"
                ? forgotPhase === "request"
                  ? "Te enviaremos un código por email para recuperar la contraseña."
                  : "Ingresa el código recibido y define una nueva contraseña."
                : "Ingresa con tu email y contraseña."
          }
          onSubmit={(event) => {
            event.preventDefault();
            if (view === "register" && registerPhase === "form") {
              submitTransportAuth("register");
              return;
            }
            if (view === "login") {
              submitTransportAuth("login");
              return;
            }
            if (view === "register" && registerPhase === "verify") {
              verifyTransportEmail();
              return;
            }
            if (view === "forgot" && forgotPhase === "request") {
              requestPasswordReset();
              return;
            }
            confirmPasswordReset();
          }}
        >
          <div className="chip-group">
            <button
              className={view === "register" ? "submit-button" : "secondary-button"}
              type="button"
              onClick={() => {
                setView("register");
                setRegisterPhase("form");
                setRegisterMethod("");
                setLoginMethod("");
                setVerificationCode("");
                setConfirmPassword("");
                setMessage("");
                setSuccessMessage("");
              }}
            >
              Crear cuenta
            </button>
            <button
              className={view === "login" ? "submit-button" : "secondary-button"}
              type="button"
              onClick={() => {
                setView("login");
                setRegisterPhase("form");
                setRegisterMethod("");
                setLoginMethod("");
                setMessage("");
                setSuccessMessage("");
              }}
            >
              Ingresar
            </button>
          </div>
          {view === "register" && registerPhase === "form" ? (
            <div className="transport-auth-methods">
              <p className="transport-oauth-caption">Elegí cómo querés crear tu cuenta.</p>
              <div className="transport-oauth-buttons">
                <button
                  className={`transport-oauth-button ${registerMethod === "email" ? "active" : ""}`}
                  type="button"
                  disabled={submitting}
                  onClick={() => setRegisterMethod("email")}
                >
                  <span className="transport-oauth-icon email" aria-hidden="true">@</span>
                  <span>Email</span>
                </button>
                <button
                  className="transport-oauth-button"
                  type="button"
                  disabled={!oauthProviders.google || submitting}
                  onClick={() => startOauth("google")}
                >
                  <span className="transport-oauth-icon google" aria-hidden="true">
                    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                      <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.24 1.26-.96 2.33-2.04 3.05l3.3 2.56c1.92-1.77 3.04-4.38 3.04-7.49 0-.72-.06-1.42-.18-2.1H12Z" />
                      <path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.63-2.44l-3.3-2.56c-.92.62-2.1.98-3.33.98-2.56 0-4.73-1.73-5.5-4.05H3.08v2.64A10 10 0 0 0 12 22Z" />
                      <path fill="#4A90E2" d="M6.5 13.93A5.98 5.98 0 0 1 6.2 12c0-.67.11-1.32.3-1.93V7.43H3.08A10 10 0 0 0 2 12c0 1.61.39 3.13 1.08 4.57l3.42-2.64Z" />
                      <path fill="#FBBC05" d="M12 5.98c1.47 0 2.8.5 3.84 1.49l2.88-2.88C16.96 2.96 14.7 2 12 2A10 10 0 0 0 3.08 7.43l3.42 2.64C7.27 7.7 9.44 5.98 12 5.98Z" />
                    </svg>
                  </span>
                  <span>Google</span>
                </button>
                <button
                  className="transport-oauth-button"
                  type="button"
                  disabled={!oauthProviders.facebook || submitting}
                  onClick={() => startOauth("facebook")}
                >
                  <span className="transport-oauth-icon facebook" aria-hidden="true">
                    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                      <path fill="currentColor" d="M13.64 22v-8.2h2.76l.42-3.2h-3.18V8.56c0-.93.26-1.56 1.59-1.56h1.7V4.14c-.3-.04-1.3-.14-2.47-.14-2.45 0-4.12 1.49-4.12 4.24v2.36H7.6v3.2h2.74V22h3.3Z" />
                    </svg>
                  </span>
                  <span>Facebook</span>
                </button>
              </div>
            </div>
          ) : null}
          {view === "login" ? (
            <div className="transport-auth-methods">
              <p className="transport-oauth-caption">Elegí cómo querés ingresar.</p>
              <div className="transport-oauth-buttons">
                <button
                  className={`transport-oauth-button ${loginMethod === "email" ? "active" : ""}`}
                  type="button"
                  disabled={submitting}
                  onClick={() => setLoginMethod("email")}
                >
                  <span className="transport-oauth-icon email" aria-hidden="true">@</span>
                  <span>Email</span>
                </button>
                <button
                  className="transport-oauth-button"
                  type="button"
                  disabled={!oauthProviders.google || submitting}
                  onClick={() => startOauth("google")}
                >
                  <span className="transport-oauth-icon google" aria-hidden="true">
                    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                      <path fill="#EA4335" d="M12 10.2v3.9h5.4c-.24 1.26-.96 2.33-2.04 3.05l3.3 2.56c1.92-1.77 3.04-4.38 3.04-7.49 0-.72-.06-1.42-.18-2.1H12Z" />
                      <path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.63-2.44l-3.3-2.56c-.92.62-2.1.98-3.33.98-2.56 0-4.73-1.73-5.5-4.05H3.08v2.64A10 10 0 0 0 12 22Z" />
                      <path fill="#4A90E2" d="M6.5 13.93A5.98 5.98 0 0 1 6.2 12c0-.67.11-1.32.3-1.93V7.43H3.08A10 10 0 0 0 2 12c0 1.61.39 3.13 1.08 4.57l3.42-2.64Z" />
                      <path fill="#FBBC05" d="M12 5.98c1.47 0 2.8.5 3.84 1.49l2.88-2.88C16.96 2.96 14.7 2 12 2A10 10 0 0 0 3.08 7.43l3.42 2.64C7.27 7.7 9.44 5.98 12 5.98Z" />
                    </svg>
                  </span>
                  <span>Google</span>
                </button>
                <button
                  className="transport-oauth-button"
                  type="button"
                  disabled={!oauthProviders.facebook || submitting}
                  onClick={() => startOauth("facebook")}
                >
                  <span className="transport-oauth-icon facebook" aria-hidden="true">
                    <svg viewBox="0 0 24 24" role="presentation" focusable="false">
                      <path fill="currentColor" d="M13.64 22v-8.2h2.76l.42-3.2h-3.18V8.56c0-.93.26-1.56 1.59-1.56h1.7V4.14c-.3-.04-1.3-.14-2.47-.14-2.45 0-4.12 1.49-4.12 4.24v2.36H7.6v3.2h2.74V22h3.3Z" />
                    </svg>
                  </span>
                  <span>Facebook</span>
                </button>
              </div>
            </div>
          ) : null}
          {view === "forgot" ||
          (view === "login" && loginMethod === "email") ||
          (view === "register" && registerPhase === "verify") ||
          (view === "register" && registerPhase === "form" && registerMethod === "email") ? (
            <div className="transport-auth-half-field">
              <Input
                label="Email"
                value={form.email}
                onChange={(value) => setForm((current) => ({ ...current, email: value }))}
              />
            </div>
          ) : null}
          {view === "register" && registerPhase === "verify" ? (
            <Input label="Código de verificación" value={verificationCode} onChange={setVerificationCode} />
          ) : null}
          {view === "forgot" && forgotPhase === "confirm" ? (
            <>
              <div className="transport-auth-half-field">
                <Input label="Código de recuperación" value={resetCode} onChange={setResetCode} />
              </div>
              <div className="transport-auth-half-field">
                <PasswordInput
                  label="Nueva contraseña"
                  value={newPassword}
                  onChange={setNewPassword}
                  visible={showResetPassword}
                  onToggleVisibility={() => setShowResetPassword((current) => !current)}
                />
              </div>
            </>
          ) : null}
          {(view === "login" && loginMethod === "email") || (view === "register" && registerPhase === "form" && registerMethod === "email") ? (
            <>
              <div className="transport-auth-half-field">
                <PasswordInput
                  label="Contraseña"
                  value={form.password}
                  onChange={(value) => setForm((current) => ({ ...current, password: value }))}
                  visible={showPassword}
                  onToggleVisibility={() => setShowPassword((current) => !current)}
                />
              </div>
              {view === "login" && loginMethod === "email" ? (
                <div className="transport-auth-half-field">
                  <div className="transport-inline-link-row transport-inline-link-row-inline">
                    <button
                      className="transport-inline-link"
                      type="button"
                      onClick={() => {
                        setView("forgot");
                        setForgotPhase("request");
                        setResetCode("");
                        setNewPassword("");
                        setMessage("");
                        setSuccessMessage("");
                      }}
                    >
                      Olvidé mi contraseña
                    </button>
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
          {view === "register" && registerPhase === "form" && registerMethod === "email" ? (
            <div className="transport-auth-half-field">
              <PasswordInput
                label="Repetir contraseña"
                value={confirmPassword}
                onChange={setConfirmPassword}
                visible={showConfirmPassword}
                onToggleVisibility={() => setShowConfirmPassword((current) => !current)}
              />
            </div>
          ) : null}
          <div className="biometry-actions">
            <div className="transport-auth-half-field">
              <button className="submit-button transport-auth-submit-button" type="submit" disabled={submitting}>
                {submitting
                  ? "Procesando..."
                  : view === "register" && registerPhase === "form"
                    ? registerMethod === "email"
                      ? "Crear cuenta"
                      : "Elegí un método"
                    : view === "register"
                      ? "Validar email"
                      : view === "forgot" && forgotPhase === "request"
                        ? "Enviar código"
                        : view === "forgot"
                          ? "Actualizar contraseña"
                          : "Ingresar"}
              </button>
            </div>
            {view === "register" && registerPhase === "verify" ? (
              <button className="secondary-button" type="button" disabled={submitting} onClick={resendTransportVerification}>
                Reenviar mail
              </button>
            ) : null}
          </div>
          <StatusMessage message={message} />
          <StatusMessage message={successMessage} />
        </FormCard>
      </section>
    </PortalLayout>
  );
}

function PasswordInput({ label, value, onChange, visible, onToggleVisibility }) {
  return (
    <label className="field">
      <span>{label}</span>
      <div className="password-input-shell">
        <input type={visible ? "text" : "password"} value={value} onChange={(event) => onChange(event.target.value)} />
        <button className="password-visibility-button" type="button" onClick={onToggleVisibility} aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}>
          {visible ? (
            <svg viewBox="0 0 24 24" role="presentation" focusable="false">
              <path d="M2.3 4.28 3.72 2.86l17.42 17.42-1.42 1.42-3.08-3.08A12.6 12.6 0 0 1 12 19.5C6.78 19.5 2.34 16.26.5 12c.86-1.98 2.3-3.78 4.1-5.2L2.3 4.28Zm6.03 6.03a4 4 0 0 0 5.34 5.34l-5.34-5.34Zm3.58-5.79c5.22 0 9.66 3.24 11.5 7.48a12.74 12.74 0 0 1-3.98 4.98l-2.87-2.87A5.98 5.98 0 0 0 9.9 7.44L7.86 5.4a12.9 12.9 0 0 1 4.05-.88Zm-.03 3a4 4 0 0 1 4 4c0 .53-.1 1.03-.28 1.5l-5.22-5.22c.47-.18.97-.28 1.5-.28Z" fill="currentColor" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" role="presentation" focusable="false">
              <path d="M12 5c5.23 0 9.67 3.24 11.5 7-1.83 3.76-6.27 7-11.5 7S2.33 15.76.5 12C2.33 8.24 6.77 5 12 5Zm0 2C8.22 7 4.94 9.16 3.02 12 4.94 14.84 8.22 17 12 17s7.06-2.16 8.98-5C19.06 9.16 15.78 7 12 7Zm0 2.5A2.5 2.5 0 1 1 12 14.5a2.5 2.5 0 0 1 0-5Z" fill="currentColor" />
            </svg>
          )}
        </button>
      </div>
    </label>
  );
}

function CompanyAuthScreen({
  eyebrow,
  title,
  description,
  primaryHref,
  primaryLabel,
  secondaryHref,
  secondaryLabel,
  routeItems,
  registerOnly = false,
}) {
  const [form, setForm] = useState(initialCompanyAuth);
  const [message, setMessage] = useState("");

  const submit = (mode) => {
    if (!form.cuit || !form.razon_social || !form.email || !form.password) {
      setMessage("Completa CUIT, razon social, email y password.");
      return;
    }

    if (!isValidCuit(form.cuit)) {
      setMessage("El CUIT debe tener 11 dígitos válidos.");
      return;
    }

    setMessage(mode === "register" ? "Alta simulada lista. Continua al app del portal." : "Login simulado listo. Continua al app del portal.");
    window.location.hash = primaryHref;
  };

  return (
    <PortalLayout eyebrow={eyebrow} title={title} description={description} backHref="#/">
      <section className="portal-section-grid">
        <FormCard
          title="Alta / login tradicional"
          description="Este portal no comparte el onboarding de identidad del chofer. La autenticacion de empresa queda separada."
          onSubmit={(event) => {
            event.preventDefault();
            submit("register");
          }}
        >
          <Input label="CUIT" value={form.cuit} onChange={(value) => setForm({ ...form, cuit: value })} />
          <Input label="Razon social" value={form.razon_social} onChange={(value) => setForm({ ...form, razon_social: value })} />
          <Input label="Email" value={form.email} onChange={(value) => setForm({ ...form, email: value })} />
          <Input label="Password" type="password" value={form.password} onChange={(value) => setForm({ ...form, password: value })} />
          <div className="biometry-actions">
            <button className="submit-button" type="submit">
              {registerOnly ? primaryLabel : "Crear cuenta"}
            </button>
            {!registerOnly ? (
              <button className="secondary-button" type="button" onClick={() => submit("login")}>
                Ingresar
              </button>
            ) : null}
          </div>
          <StatusMessage message={message} />
        </FormCard>

        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>Rutas del portal</h2>
          </div>
          <div className="portal-list">
            {routeItems.map((item) => (
              <PortalListItem key={item} title={item} description="" />
            ))}
          </div>
        </section>

        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>Separacion de responsabilidades</h2>
          </div>
          <div className="portal-list">
            <PortalListItem title="Chofer" description="Identidad con DNI + selfie, documentos personales y permisos a empresas." />
            <PortalListItem title="Empresa" description="Alta/login tradicional y acceso a su app especifica, sin mezclar formularios de identidad del chofer." />
          </div>
          <div className="biometry-actions">
            <button className="secondary-button" type="button" onClick={() => { window.location.hash = primaryHref; }}>
              {primaryLabel}
            </button>
            <button className="secondary-button" type="button" onClick={() => { window.location.hash = secondaryHref; }}>
              {secondaryLabel}
            </button>
          </div>
        </section>
      </section>
    </PortalLayout>
  );
}

function getTransportDocumentOptions(entidadTipo, policyCatalog = []) {
  const catalogOptions = buildTransportDocumentOptionsFromCatalog(entidadTipo, policyCatalog);
  if (catalogOptions.length) {
    return catalogOptions;
  }
  return DEFAULT_TRANSPORT_DOCUMENT_OPTIONS[entidadTipo] || DEFAULT_TRANSPORT_DOCUMENT_OPTIONS.chofer;
}

function normalizeDigits(value) {
  return String(value || "").replace(/\D/g, "");
}

function isRealBusinessName(value) {
  const normalized = String(value || "").trim();
  if (!normalized) {
    return false;
  }

  const digitsOnly = normalized.replace(/\D/g, "");
  if (digitsOnly.length === 11 && digitsOnly === normalized) {
    return false;
  }

  if (/^empresa\s+\d{11}$/i.test(normalized)) {
    return false;
  }

  return /[A-ZÁÉÍÓÚÜÑ]/i.test(normalized) && !/^\d+$/.test(normalized);
}

function isValidCuit(value) {
  const normalized = normalizeDigits(value);
  if (!/^\d{11}$/.test(normalized)) {
    return false;
  }
  const digits = normalized.split("").map((digit) => Number(digit));
  const multipliers = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  const total = multipliers.reduce((sum, multiplier, index) => sum + multiplier * digits[index], 0);
  const mod = 11 - (total % 11);
  const expected = mod === 11 ? 0 : mod === 10 ? 9 : mod;
  return expected === digits[10];
}

function getTransportCompanyDocumentGroups(policyCatalog = []) {
  const items = getTransportDocumentOptions("titular", policyCatalog);
  const groupedDocumentTypes = new Set([
    "constancia_afip",
    "cm05",
    "estatuto_contrato_social",
    "acta_autoridades",
    "poder",
    "inscripcion_ruta",
    "habilitacion_cnrt",
    "seguro_responsabilidad_civil",
    "seguro_cargas",
  ]);
  const customItems = items.filter((item) => !groupedDocumentTypes.has(item.value));

  return [
    {
      key: "impositiva",
      label: "Documentacion impositiva",
      items: items.filter((item) => ["constancia_afip", "cm05"].includes(item.value)),
    },
    {
      key: "legal",
      label: "Documentacion legal",
      items: items.filter((item) => ["estatuto_contrato_social", "acta_autoridades", "poder"].includes(item.value)),
    },
    {
      key: "operativa",
      label: "Documentacion operativa",
      items: items.filter((item) =>
        ["inscripcion_ruta", "habilitacion_cnrt", "seguro_responsabilidad_civil", "seguro_cargas"].includes(item.value)
      ),
    },
    {
      key: "custom",
      label: "Documentacion adicional",
      items: customItems,
    },
  ].filter((group) => group.items.length);
}

function buildTransportDriverDocumentDefinitions(policyCatalog = []) {
  return getTransportDocumentOptions("chofer", policyCatalog).map((item) => ({
    tipo: item.value,
    label: item.label,
  }));
}

function FleetEntityCard({
  title,
  subtitle,
  tone,
  entityType,
  entityId,
  entityData,
  rows,
  documents,
  onOpenDocument,
  openingDocumentId,
  fleetPreviewUrls,
  activeCaptureKey,
  onToggleCapture,
  onUploadDocument,
  documentPolicyCatalog,
  rtoSnapshot,
  rtoLoading,
  rtoError,
  onEnsureUnitRtoSnapshot,
}) {
  const latestDocuments = getLatestDocumentsByType(documents, entityType);
  const options = getTransportDocumentOptions(entityType, documentPolicyCatalog);
  const [isOpen, setIsOpen] = useState(false);
  const rtoStatus = useMemo(() => getRtoStepStatus(rtoSnapshot), [rtoSnapshot]);
  const snapshot = rtoSnapshot || {};
  const rtoData = snapshot.rto || {};
  const detailRows = useMemo(() => {
    const baseRows = [...rows];
    const normalizedState = String( rtoStatus.label || "" ).replace(/\n/g, " ").trim();
    if (!rtoData?.fecha_vencimiento && !rtoData?.certificado_numero && !normalizedState) {
      return baseRows;
    }

    const stateLabel = normalizedState || "-";
    baseRows.push({ label: "Estado RTO", value: stateLabel });
    baseRows.push({ label: "Vencimiento", value: formatDate(rtoData?.fecha_vencimiento) });
    baseRows.push({ label: "Certificado", value: rtoData?.certificado_numero || "-" });
    return baseRows;
  }, [rows, rtoData?.fecha_vencimiento, rtoData?.certificado_numero, rtoStatus.label]);

  useEffect(() => {
    if (!isOpen || typeof onEnsureUnitRtoSnapshot !== "function") {
      return;
    }
    if (rtoSnapshot !== undefined || rtoLoading || rtoError) {
      return;
    }
    onEnsureUnitRtoSnapshot(entityId);
  }, [isOpen, entityId, onEnsureUnitRtoSnapshot, rtoSnapshot, rtoLoading, rtoError]);

  return (
    <details
      className="detail-panel expandable-card fleet-card"
      onToggle={(event) => {
        const nextOpen = event.currentTarget.open;
        setIsOpen(nextOpen);
        if (!nextOpen) {
          onToggleCapture("");
        }
      }}
    >
      <summary className="expandable-card-summary">
        <div>
          <h2>{title}</h2>
          <p>{subtitle || "Sin detalle adicional"}</p>
        </div>
        <div className="expandable-card-summary-side">
          <StatusBadge label={entityType === "acoplado" ? "Acoplado" : "Tractor"} tone={entityType === "acoplado" ? "faltante" : "vigente"} />
          <StatusBadge label={latestDocuments.length ? `${latestDocuments.length} docs` : "Sin docs"} tone={tone} />
          <span className="expandable-card-chevron" aria-hidden="true">
            ▾
          </span>
        </div>
      </summary>
      <div className="expandable-card-body fleet-card-body">
        <section className="fleet-card-overview">
          <div className="detail-table">
            {detailRows.map((row) => (
              <div className="detail-row" key={`${title}-${row.label}`}>
                <span>{row.label}</span>
                <strong>{row.value}</strong>
              </div>
            ))}
          </div>
        </section>
        <section className="company-document-group fleet-card-documents">
          <div className="company-document-group-header">
            <h3>Documentos</h3>
          </div>
          <div className="company-document-list fleet-document-list">
            {options.length ? (
              options.map((option) => {
                const documento =
                  latestDocuments.find((item) => matchesTransportDocumentType(entityType, item.tipo_documento, option.value)) || null;
                const captureKey = `${entityType}:${entityId}:${option.value}`;
                const isCapturing = activeCaptureKey === captureKey;

                const status =
                  option.value === "rto"
                    ? { label: rtoStatus.label, tone: rtoStatus.tone }
                    : buildTransportDocumentStatus(documento);

                return (
                  <FleetDocumentCard
                    key={captureKey}
                    option={option}
                    documento={documento}
                    status={status}
                    captureKey={captureKey}
                    isCapturing={isCapturing}
                    openingDocumentId={openingDocumentId}
                    previewUrl={fleetPreviewUrls?.[captureKey] || ""}
                    parentOpen={isOpen}
                    onOpenDocument={onOpenDocument}
                    onToggleCapture={onToggleCapture}
                    onUploadDocument={onUploadDocument}
                    entityType={entityType}
                    entityId={entityId}
                    entityData={entityData}
                  />
                );
              })
            ) : (
              <PortalListItem title="Sin documentos" description="Todavia no hay documentos cargados para esta entidad." />
            )}
          </div>
        </section>
      </div>
    </details>
  );
}

function CompanyEntityCard({
  title,
  subtitle,
  entityId,
  entityData,
  documents,
  openingDocumentId,
  previewUrls,
  processingByKey,
  onOpenDocument,
  onUploadDocument,
  fileInputRefs,
  documentPolicyCatalog,
  onHideCompany,
}) {
  const latestDocuments = getLatestDocumentsByType(documents, "titular");
  const groupedOptions = getTransportCompanyDocumentGroups(documentPolicyCatalog);
  const authoritySummary = buildCompanyAuthoritySummary(documents);

  return (
    <details className="detail-panel expandable-card fleet-card company-card">
      <summary className="expandable-card-summary">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        <div className="expandable-card-summary-side">
          <StatusBadge label={latestDocuments.length ? `${latestDocuments.length} docs` : "Sin docs"} tone={latestDocuments.length ? "vigente" : "faltante"} />
          <span className="expandable-card-chevron" aria-hidden="true">
            ▾
          </span>
        </div>
      </summary>
      <div className="expandable-card-body">
        {onHideCompany ? (
          <div className="company-card-hide-row">
            <button
              className="secondary-button"
              type="button"
              onClick={() => {
                if (onHideCompany) {
                  onHideCompany();
                }
              }}
            >
              Quitar empresa
            </button>
          </div>
        ) : null}
        <section className="company-authority-summary">
          <div className="company-document-group-header">
            <h3>Autoridades y apoderados</h3>
          </div>
          <div className="company-authority-summary-grid">
            <div className="company-authority-summary-block">
              <strong>Autoridades</strong>
              {authoritySummary.authorities.length ? (
                authoritySummary.authorities.map((item, index) => <span key={`authority-${index}`}>{item}</span>)
              ) : (
                <span>Sin autoridades identificadas.</span>
              )}
            </div>
            <div className="company-authority-summary-block">
              <strong>Apoderados</strong>
              {authoritySummary.attorneys.length ? (
                authoritySummary.attorneys.map((item, index) => <span key={`attorney-${index}`}>{item}</span>)
              ) : (
                <span>Sin apoderados identificados.</span>
              )}
            </div>
          </div>
        </section>
        {groupedOptions.map((group) => (
          <section className="company-document-group" key={group.key}>
            <div className="company-document-group-header">
              <h3>{group.label}</h3>
            </div>
            <div className="company-document-list">
              {buildCompanyGroupRows(group, latestDocuments, documents, entityId).map(({ option, documento, captureKey, rowKey }) => (
                <CompanyDocumentRow
                  key={rowKey}
                  option={option}
                  documento={documento}
                  captureKey={captureKey}
                  openingDocumentId={openingDocumentId}
                  previewUrl={previewUrls[captureKey] || ""}
                  processingState={processingByKey[captureKey] || null}
                  onOpenDocument={onOpenDocument}
                  onUploadDocument={onUploadDocument}
                  entityId={entityId}
                  fileInputRefs={fileInputRefs}
                />
              ))}
            </div>
          </section>
        ))}
      </div>
    </details>
  );
}

function CompanyDocumentRow({
  option,
  documento,
  captureKey,
  openingDocumentId,
  previewUrl,
  processingState,
  onOpenDocument,
  onUploadDocument,
  entityId,
  fileInputRefs,
}) {
  const status = buildTransportDocumentStatus(documento);
  const statusDetails = buildCompanyStatusDetails(documento, status);
  const sanitizedValidation = sanitizeCompanyValidation(documento?.validacion);
  const badgeLabel = processingState?.stage && processingState.stage !== "done" ? "Procesando" : status.label;
  const badgeTone = processingState?.stage && processingState.stage !== "done" ? "faltante" : status.tone;
  const badgeDetails =
    processingState?.stage === "done" && Array.isArray(processingState?.details) && processingState.details.length
      ? processingState.details
      : processingState?.stage && processingState.stage !== "done"
        ? null
        : statusDetails;
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const hasPersistedDocument = Boolean(documento?.id || documento?.s3_key || documento?.nombre_archivo);
  const normalizedMimeType = String(documento?.mime_type || processingState?.fileType || "").toLowerCase();
  const isPdfLikeDocument =
    normalizedMimeType.includes("pdf") ||
    /\.pdf$/i.test(String(documento?.nombre_archivo || ""));

  function openFilePicker() {
    const input = fileInputRefs.current[captureKey];
    if (input) {
      input.value = "";
      input.click();
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function loadThumbnail() {
      if (!documento?.id || !String(documento?.mime_type || "").startsWith("image/")) {
        setThumbnailUrl("");
        return;
      }

      try {
        const response = await getJson(`/documentos/${documento.id}/url`);
        if (!cancelled) {
          setThumbnailUrl(response.data.url || "");
        }
      } catch (_error) {
        if (!cancelled) {
          setThumbnailUrl("");
        }
      }
    }

    loadThumbnail();
    return () => {
      cancelled = true;
    };
  }, [documento?.id, documento?.mime_type]);

  return (
    <div className="company-document-row">
      <div className="company-document-row-main">
        <strong>{option.label}</strong>
        <StatusBadge
          label={badgeLabel}
          tone={badgeTone}
          details={badgeDetails}
          loading={Boolean(processingState?.stage && processingState.stage !== "done")}
        />
        {sanitizedValidation ? (
          <p className="mini-copy">Estado de validación: {humanizeLabel(sanitizedValidation.status || "-")}</p>
        ) : null}
      </div>
      <div className="company-document-row-thumb">
        <button
          className="company-document-thumb-button"
          type="button"
          onClick={() => {
            if (processingState) {
              return;
            }
            if (documento) {
              onOpenDocument(documento);
              return;
            }
            openFilePicker();
          }}
          disabled={Boolean(processingState) || (Boolean(documento) && openingDocumentId === String(documento?.id))}
        >
          <div className="company-document-thumb">
            {previewUrl || thumbnailUrl ? (
              <>
                <img src={previewUrl || thumbnailUrl} alt={option.label} />
                <span className="company-document-thumb-badge">IMG</span>
              </>
            ) : isPdfLikeDocument ? (
              <div className="company-document-thumb-pdf">
                <span>PDF</span>
              </div>
            ) : hasPersistedDocument ? (
              <div className="company-document-thumb-pdf">
                <span>DOC</span>
              </div>
            ) : (
              <div className="company-document-thumb-empty" />
            )}
            {processingState ? (
              <div className={`company-document-thumb-processing company-document-thumb-processing-${processingState.tone || "faltante"}`}>
                {processingState.stage !== "done" ? <span className="processing-spinner" aria-hidden="true" /> : null}
                <strong>{processingState.label}</strong>
              </div>
            ) : null}
          </div>
        </button>
        <span className="company-document-thumb-date">
          {documento?.fecha_vencimiento ? `Vence ${formatDate(documento.fecha_vencimiento)}` : documento?.created_at ? `Alta ${formatDate(documento.created_at)}` : ""}
        </span>
      </div>
      <div className="company-document-row-side">
        <input
          ref={(node) => {
            fileInputRefs.current[captureKey] = node;
          }}
          type="file"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (!file) {
              return;
            }
            onUploadDocument({
              entidadId: entityId,
              tipoDocumento: option.value,
              file,
              captureKey,
            });
            event.target.value = "";
          }}
        />
        <button
          className={`secondary-button company-document-action ${documento ? "" : "company-document-action-wide"}`}
          type="button"
          disabled={Boolean(processingState)}
          onClick={openFilePicker}
        >
          {documento ? "Actualizar" : "Subir documento"}
        </button>
      </div>
    </div>
  );
}

function ContractSignatureResultCard({ result, defaultOpen = true }) {
  const validated = result?.biometric?.validado;
  return (
    <details className="detail-panel expandable-card contract-result-card" open={defaultOpen}>
      <summary className="expandable-card-summary">
        <div>
          <h2>Resultado de firma</h2>
          <p>{validated ? "Biometría aprobada" : "Revisar validación biométrica"}</p>
        </div>
        <div className="expandable-card-summary-side">
          <StatusBadge label={validated ? "Firmado" : "Observado"} tone={validated ? "vigente" : "faltante"} />
          <span className="expandable-card-chevron" aria-hidden="true">
            ▾
          </span>
        </div>
      </summary>
      <div className="expandable-card-body">
        <div className="detail-table">
          <div className="detail-row">
            <span>Similitud biométrica</span>
            <strong>{result?.biometric?.similitud ? `${Number(result.biometric.similitud).toFixed(2)}%` : "-"}</strong>
          </div>
          <div className="detail-row">
            <span>Estado del documento</span>
            <strong>{humanizeLabel(result?.contract?.status || "-")}</strong>
          </div>
        </div>
      </div>
    </details>
  );
}

function buildCompanyGroupRows(group, latestDocuments, allDocuments, entityId) {
  const rows = [];

  group.items.forEach((option) => {
    if (option.value !== "poder") {
      const documento = latestDocuments.find((item) => matchesTransportDocumentType("titular", item.tipo_documento, option.value)) || null;
      rows.push({
        option,
        documento,
        captureKey: `titular:${entityId}:${option.value}`,
        rowKey: `titular:${entityId}:${option.value}:${documento?.id || "empty"}`,
      });
      return;
    }

    const powers = [...allDocuments]
      .filter((item) => matchesTransportDocumentType("titular", item.tipo_documento, "poder"))
      .sort((left, right) => new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime());

    powers.forEach((documento, index) => {
      rows.push({
        option: { ...option, label: `Poder ${index + 1}` },
        documento,
        captureKey: `titular:${entityId}:poder:${documento?.id || index}`,
        rowKey: `titular:${entityId}:poder:${documento?.id || index}`,
      });
    });

    const canAddNextPower =
      powers.length === 0 ||
      powers.every((documento) => documento?.validacion?.status === "approved" || documento?.estado === "vigente");

    if (canAddNextPower) {
      rows.push({
        option: { ...option, label: `Poder ${powers.length + 1}` },
        documento: null,
        captureKey: `titular:${entityId}:poder:new:${powers.length + 1}`,
        rowKey: `titular:${entityId}:poder:new:${powers.length + 1}`,
      });
    }
  });

  return rows;
}

function buildCompanyAuthoritySummary(documents) {
  const authorities = [];
  const attorneys = [];
  const latestAuthorityDocument = [...(documents || [])]
    .filter((documento) => matchesTransportDocumentType("titular", documento?.tipo_documento, "acta_autoridades"))
    .sort((left, right) => new Date(right?.created_at || 0).getTime() - new Date(left?.created_at || 0).getTime())[0];

  [latestAuthorityDocument, ...(documents || []).filter((documento) => matchesTransportDocumentType("titular", documento?.tipo_documento, "poder"))].filter(Boolean).forEach((documento) => {
    const summary = documento?.validacion?.metadata?.extracted_summary || null;
    if (!summary) {
      return;
    }

    if (matchesTransportDocumentType("titular", documento.tipo_documento, "acta_autoridades")) {
      const authorityPeople = Array.isArray(summary.authority_people) && summary.authority_people.length
        ? summary.authority_people
        : [];

      authorityPeople.filter((person) => looksLikeTrustedAuthority(person)).forEach((person) => {
        authorities.push(
          [
            person.name,
            person.role,
            person.dni ? `DNI ${person.dni}` : "",
            person.valid_until ? buildAuthorityValidityLabel(person.valid_until) : "",
          ]
            .filter(Boolean)
            .join(" · ")
        );
      });
    }

    if (matchesTransportDocumentType("titular", documento.tipo_documento, "poder")) {
      const attorneyPeople = Array.isArray(summary.attorney_people) && summary.attorney_people.length
        ? summary.attorney_people
        : [];

      attorneyPeople
        .filter((person) => hasContractSigningPower(person) && looksLikeTrustedAuthority(person))
        .forEach((person) => {
        attorneys.push(
          [
            person.name,
            Array.isArray(person.power_scopes) && person.power_scopes.length ? `Poderes: ${person.power_scopes.join(", ")}` : "",
            person.dni ? `DNI ${person.dni}` : "",
          ]
            .filter(Boolean)
            .join(" · ")
        );
      });
    }
  });

  return {
    authorities: Array.from(new Set(authorities)),
    attorneys: Array.from(new Set(attorneys)),
  };
}

function looksLikeTrustedAuthority(person) {
  const normalized = String(person?.name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized || normalized.split(" ").length < 2) {
    return false;
  }

  const blockedFragments = [
    "DE LA SOCIEDAD",
    "EN SU CARACTER",
    "QUIEN EXPRESA",
    "DURANTE EL EJERCICIO",
    "TITULAR Y UN",
    "TITULAR UNICO",
    "ADMINISTRATIVO QUIEN MANIFIESTA",
    "PODER GENERAL",
    "PALABRA JUDICIAL",
    "DESIGNACION DE UN",
  ];

  return !blockedFragments.some((fragment) => normalized.includes(fragment));
}

function looksLikeHumanPersonName(value) {
  const normalized = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/[^A-Z\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!normalized || normalized.split(" ").length < 2) {
    return false;
  }

  const blockedTokens = [
    "REPUBLICA",
    "ARGENTINA",
    "MERCOSUR",
    "DOCUMENTO",
    "NACIONAL",
    "IDENTIDAD",
    "REGISTRO",
    "NRO",
    "NUMERO",
    "EJEMPLAR",
    "TRAMITE",
    "SEXO",
    "NOMBRE",
    "APELLIDO",
  ];

  if (blockedTokens.some((token) => normalized.includes(token))) {
    return false;
  }

  return true;
}

function hasContractSigningPower(person) {
  if (!person?.name || person?.can_sign === false) {
    return false;
  }

  const scopes = Array.isArray(person.power_scopes) ? person.power_scopes.map((item) => String(item || "").toLowerCase()) : [];
  if (!scopes.length) {
    return false;
  }

  const hasPositive = scopes.some((scope) => ["administracion", "representacion_general", "firma_contratos"].includes(scope));
  if (!hasPositive) {
    return false;
  }

  const onlyRestricted = scopes.every((scope) => ["igj", "judicial", "bancario", "administrativo", "legal"].includes(scope));
  return !onlyRestricted;
}

function buildAuthorityValidityLabel(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  date.setHours(23, 59, 59, 999);
  return date.getTime() < Date.now() ? `Mandato vencido ${formatDate(value)}` : `Vigente hasta ${formatDate(value)}`;
}

function FleetDocumentCard({
  option,
  documento,
  status,
  captureKey,
  isCapturing,
  openingDocumentId,
  previewUrl,
  parentOpen,
  onOpenDocument,
  onToggleCapture,
  onUploadDocument,
  entityType,
  entityId,
  entityData,
}) {
  const fileInputRef = useRef(null);
  const [showUpdateOptions, setShowUpdateOptions] = useState(false);
  const thumbnailUrl = useDocumentThumbnail(documento, previewUrl, { disabled: isCapturing });
  const hasPersistedDocument = Boolean(documento?.id || documento?.s3_key || documento?.nombre_archivo);
  const normalizedMimeType = String(documento?.mime_type || "").toLowerCase();
  const isPdfLikeDocument =
    normalizedMimeType.includes("pdf") ||
    /\.pdf$/i.test(String(documento?.nombre_archivo || ""));

  useEffect(() => {
    if (!parentOpen) {
      setShowUpdateOptions(false);
    }
  }, [parentOpen]);

  return (
    <div
      className={`company-document-row fleet-document-card fleet-document-card-${status.tone} ${
        isCapturing ? "fleet-document-card-capturing" : ""
      }`}
      key={captureKey}
    >
      <div className="company-document-row-main fleet-document-card-main">
        <strong>{option.label}</strong>
        <div className="fleet-document-status">
          <StatusBadge label={status.label} tone={status.tone} />
          <span className={`fleet-document-date fleet-document-date-${status.tone}`}>
            {documento?.fecha_vencimiento
              ? `Vence ${formatDate(documento.fecha_vencimiento)}`
              : documento?.created_at
                ? `Alta ${formatDate(documento.created_at)}`
                : "Sin foto"}
          </span>
        </div>
      </div>
      <div className="company-document-row-thumb fleet-document-card-thumb-wrap">
        {isCapturing ? (
          <CameraCapture
            mode="documento"
            title={option.label}
            description="Confirma la foto y el documento se guarda automaticamente."
            showHeader={false}
            onCancel={() => {
              onToggleCapture("");
            }}
            onConfirm={(file) => {
              onToggleCapture("");
              onUploadDocument({
                entidadTipo: entityType,
                entidadId: entityId,
                tipoDocumento: option.value,
                file,
                entityData,
                captureKey,
              });
            }}
          />
        ) : (
          <>
            <div className="company-document-thumb fleet-document-thumb">
              {previewUrl || thumbnailUrl ? (
                <img src={previewUrl || thumbnailUrl} alt={option.label} />
              ) : isPdfLikeDocument ? (
                <div className="company-document-thumb-pdf fleet-document-thumb-pdf">
                  <span className="fleet-document-thumb-pdf-icon" aria-hidden="true">
                    PDF
                  </span>
                  <span className="fleet-document-thumb-pdf-label">Archivo cargado</span>
                </div>
              ) : hasPersistedDocument ? (
                <div className="company-document-thumb-pdf fleet-document-thumb-pdf">
                  <span className="fleet-document-thumb-pdf-icon" aria-hidden="true">
                    DOC
                  </span>
                  <span className="fleet-document-thumb-pdf-label">Archivo cargado</span>
                </div>
              ) : (
                <div className="company-document-thumb-empty fleet-document-thumb-empty">Sin foto</div>
              )}
            </div>
            <span className="company-document-thumb-date">
              {documento?.fecha_vencimiento
                ? `Vence ${formatDate(documento.fecha_vencimiento)}`
                : documento?.created_at
                  ? `Alta ${formatDate(documento.created_at)}`
                  : ""}
            </span>
          </>
        )}
      </div>
      <div className="company-document-row-side fleet-document-card-side">
        {!isCapturing ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) {
                  return;
                }

                setShowUpdateOptions(false);
                onUploadDocument({
                  entidadTipo: entityType,
                  entidadId: entityId,
                  tipoDocumento: option.value,
                  file,
                  entityData,
                  captureKey,
                });
                event.target.value = "";
              }}
            />
            <div className="document-card-actions fleet-document-actions">
              {documento ? (
                <>
                  {showUpdateOptions ? (
                    <>
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={() => {
                          setShowUpdateOptions(false);
                          onToggleCapture(captureKey);
                        }}
                      >
                        Sacar foto
                      </button>
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={() => {
                          if (fileInputRef.current) {
                            fileInputRef.current.value = "";
                            fileInputRef.current.click();
                          }
                        }}
                      >
                        Subir archivo
                      </button>
                    </>
                  ) : (
                    <button className="secondary-button" type="button" onClick={() => setShowUpdateOptions(true)}>
                      Actualizar
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button className="submit-button" type="button" onClick={() => onToggleCapture(captureKey)}>
                    Subir foto
                  </button>
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    Subir archivo
                  </button>
                </>
              )}
              {documento && !showUpdateOptions ? (
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => onOpenDocument(documento)}
                  disabled={openingDocumentId === String(documento.id)}
                >
                  {openingDocumentId === String(documento.id) ? "Abriendo..." : "Ver archivo"}
                </button>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
  </div>
  );
}

function useDocumentThumbnail(document, fallbackUrl = "", options = {}) {
  const { disabled = false } = options;
  const [thumbnailUrl, setThumbnailUrl] = useState(fallbackUrl || "");

  useEffect(() => {
    if (fallbackUrl) {
      setThumbnailUrl(fallbackUrl);
      return;
    }

    if (disabled || !document?.id || !String(document?.mime_type || "").startsWith("image/")) {
      setThumbnailUrl("");
      return;
    }

    let cancelled = false;

    getJson(`/documentos/${document.id}/url`)
      .then((response) => {
        if (!cancelled) {
          setThumbnailUrl(response.data.url || "");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setThumbnailUrl("");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [document?.id, document?.mime_type, fallbackUrl, disabled]);

  return thumbnailUrl;
}

function UnitBatchAnalysisCard({ unitBatchType, unitBatchAnalysis, unit }) {
  const primaryResult = unitBatchAnalysis[0]?.result || {};
  const isNewUnit = unitBatchType === "nueva_unidad";

  return (
    <section className="detail-panel biometria-result-card">
      <div className="detail-panel-header">
        <h2>Resumen detectado</h2>
        <StatusBadge label={primaryResult.tipo_label || "Documento"} tone={primaryResult.legible ? "vigente" : "faltante"} />
      </div>
      <div className="unit-batch-review">
        <div className="detail-table unit-batch-review-main">
          <div className="detail-row">
            <span>Dominio</span>
            <strong>{unit.dominio || primaryResult.datos_extraidos?.dominio || "-"}</strong>
          </div>
          <div className="detail-row">
            <span>Marca</span>
            <strong>{unit.marca || primaryResult.datos_extraidos?.marca || "-"}</strong>
          </div>
          <div className="detail-row">
            <span>Modelo</span>
            <strong>{unit.modelo || primaryResult.datos_extraidos?.modelo || "-"}</strong>
          </div>
          <div className="detail-row">
            <span>Tipo</span>
            <strong>
              {unit.tipo || primaryResult.datos_extraidos?.tipo || (primaryResult.entidad_sugerida === "acoplado" ? "Acoplado / semi-remolque" : "-")}
            </strong>
          </div>
          <div className="detail-row">
            <span>RTO mock</span>
            <strong>{primaryResult.validacion_rto?.estado_final || "-"}</strong>
          </div>
          <div className="detail-row">
            <span>Clasificacion</span>
            <strong>
              {primaryResult.entidad_sugerida === "acoplado" ? "Acoplado / semi-remolque" : "Tractor"}
            </strong>
          </div>
        </div>
        <div className="unit-batch-review-files">
          {unitBatchAnalysis.map((item, index) => (
            <BatchPreviewCard
              key={item.id}
              file={item.file}
              title={isNewUnit ? (index === 0 ? "Cedula frente" : "Cedula dorso") : humanizeLabel(unitBatchType)}
              description={item.result.tipo_label || item.file.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function UnitBatchPendingCard({ unitBatchType, files }) {
  const isNewUnit = unitBatchType === "nueva_unidad";

  return (
    <section className="detail-panel biometria-result-card">
      <div className="detail-panel-header">
        <h2>Archivos cargados</h2>
        <StatusBadge label="Procesando" tone="faltante" />
      </div>
      <div className="unit-batch-review-files">
        {files.map((item, index) => (
          <BatchPreviewCard
            key={item.id}
            file={item.file}
            title={isNewUnit ? (index === 0 ? "Cedula frente" : "Cedula dorso") : humanizeLabel(unitBatchType)}
            description="Archivo recibido. Estamos leyendo el documento."
          />
        ))}
      </div>
    </section>
  );
}

function BatchPreviewCard({ file, title, description }) {
  const [previewUrl, setPreviewUrl] = useState("");

  useEffect(() => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      setPreviewUrl("");
      return undefined;
    }

    const nextUrl = URL.createObjectURL(file);
    setPreviewUrl(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [file]);

  return (
    <div className="portal-list-card unit-batch-file-card">
      <div className="unit-batch-file-thumb">
        {previewUrl ? <img src={previewUrl} alt={title} /> : <div className="unit-batch-file-empty">Archivo</div>}
      </div>
      <div className="unit-batch-file-meta">
        <strong>{title}</strong>
        <p>{description}</p>
      </div>
    </div>
  );
}

function buildTransportDocumentStatus(documento) {
  if (!documento) {
    return {
      label: "Sin foto",
      tone: "faltante",
      reason: "Documento no cargado.",
    };
  }

  const sanitizedValidation = sanitizeCompanyValidation(documento.validacion);
  const expiration = documento.fecha_vencimiento ? new Date(documento.fecha_vencimiento) : null;
  if (expiration) {
    expiration.setHours(23, 59, 59, 999);
  }

  const isExpired = expiration && !Number.isNaN(expiration.getTime()) && expiration.getTime() < Date.now();

  if (documento.estado === "rechazado" || sanitizedValidation?.status === "rejected") {
    return {
      label: "Rechazado",
      tone: "vencido",
      reason: "Documento rechazado por validacion.",
    };
  }

  if (documento.estado === "observado" || sanitizedValidation?.status === "observed") {
    return {
      label: "Observado",
      tone: "faltante",
      reason: "Documento cargado pero con estado observado.",
    };
  }

  if (isExpired) {
    return {
      label: "Vencido",
      tone: "vencido",
      reason: "Documento vencido o con fecha invalida.",
    };
  }

  if (sanitizedValidation?.status === "approved" || documento.estado === "vigente") {
    return {
      label: "Validado",
      tone: "vigente",
      reason: "Documento validado.",
    };
  }

  return {
    label: "Observado",
    tone: "faltante",
    reason: "Documento cargado pendiente de validacion.",
  };
}

function getRtoStepStatus(snapshot, photoStatus = {}) {
  const hasPhoto = Boolean(photoStatus?.file && photoStatus.status === "done");
  if (!snapshot || !snapshot.rto) {
    return {
      state: "pendiente",
      label: "Pendiente",
      tone: "faltante",
      hasPhoto,
    };
  }

  const validationStatus = snapshot.document?.validacion?.status;
  const docState = String(snapshot.document?.estado || "").toLowerCase();
  const expirationTimestamp = snapshot.rto?.fecha_vencimiento ? Date.parse(snapshot.rto.fecha_vencimiento) : null;
  const now = Date.now();
  let baseState = "pendiente";

  if (validationStatus === "approved" || snapshot.rto?.vigente === true) {
    baseState = "aprobado";
  } else if (
    docState === "vencido" ||
    (snapshot.rto?.vigente === false && expirationTimestamp && expirationTimestamp < now)
  ) {
    baseState = "vencido";
  } else if (validationStatus === "observed" || snapshot.rto?.vigente === false) {
    baseState = "observado";
  } else if (docState === "pendiente_foto") {
    baseState = "pendiente";
  }

  const tone = baseState === "aprobado" ? "vigente" : baseState === "vencido" ? "vencido" : "faltante";
  const suffix = hasPhoto ? "con foto" : "sin foto";
  const label = baseState === "pendiente" ? "Pendiente" : `${capitalize(baseState)}\n${suffix}`;

  return {
    state: baseState,
    label,
    tone,
    hasPhoto,
  };
}

function capitalize(value) {
  if (!value) {
    return "";
  }
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function buildCompanyStatusDetails(documento, status) {
  const sanitizedValidation = sanitizeCompanyValidation(documento?.validacion);
  if (!sanitizedValidation) {
    return status?.reason || null;
  }

  const metadata = sanitizedValidation?.metadata || {};
  const summary = metadata?.extracted_summary || null;
  const interpretationSource = summary?.interpretation_source || metadata?.interpretation_source || null;
  const interpretationError = summary?.interpretation_error || metadata?.interpretation_error || null;
  const extraDetails = [];
  if (interpretationSource === "bedrock") {
    extraDetails.push("Interpretado con IA.");
  } else if (interpretationSource === "ocr_rules") {
    extraDetails.push("Interpretado por OCR.");
  } else {
    extraDetails.push("Fuente de interpretacion no disponible.");
  }

  if (interpretationError) {
    extraDetails.push(`Fallo IA: ${interpretationError}`);
  }

  if (matchesTransportDocumentType("titular", documento?.tipo_documento, "acta_autoridades")) {
    const names = Array.isArray(summary?.authority_people) ? summary.authority_people.map((item) => item?.name).filter(Boolean) : [];
    if (names.length) {
      extraDetails.push(`Autoridades detectadas: ${names.join(", ")}.`);
    }
  }

  if (matchesTransportDocumentType("titular", documento?.tipo_documento, "poder")) {
    const names = Array.isArray(summary?.attorney_people) ? summary.attorney_people.map((item) => item?.name).filter(Boolean) : [];
    if (names.length) {
      extraDetails.push(`Apoderados detectados: ${names.join(", ")}.`);
    }
  }

  const reasons = Array.isArray(sanitizedValidation.reasons) ? sanitizedValidation.reasons : [];
  if (reasons.length) {
    return [...extraDetails, ...reasons];
  }

  const checks = { ...(sanitizedValidation.checks || {}) };
  const passedChecks = Object.entries(checks)
    .filter(([, value]) => value?.passed)
    .map(([key]) => humanizeValidationCheck(key));

  if (passedChecks.length) {
    return [...extraDetails, ...passedChecks];
  }

  return extraDetails.length ? extraDetails : status?.reason || null;
}

function sanitizeCompanyValidation(validation) {
  if (!validation || typeof validation !== "object") {
    return null;
  }

  const checks = { ...(validation.checks || {}) };
  delete checks.business_purpose_transport_related;

  const reasons = Array.isArray(validation.reasons)
    ? validation.reasons.filter(
        (reason) => !/objeto social no parece compatible con transporte o logistica/i.test(String(reason || ""))
      )
    : [];

  const hasFailedChecks = Object.values(checks).some((value) => value?.passed === false);
  const status = !hasFailedChecks && reasons.length === 0 && validation.status === "rejected" ? "approved" : validation.status;

  return {
    ...validation,
    status,
    checks,
    reasons,
  };
}

function humanizeValidationCheck(key) {
  const labels = {
    document_type_match: "Tipo de documento validado.",
    cuit_match: "CUIT coincide con la empresa.",
    insured_cuit_match: "CUIT asegurado coincide con la empresa.",
    business_name_match: "Razon social compatible.",
    tax_status_active: "Estado fiscal activo.",
    recent_filing: "Presentacion reciente.",
    has_jurisdictions: "Jurisdicciones leidas.",
    legal_doc_recognized: "Documento legal reconocido.",
    authority_role_valid: "Cargo leido correctamente.",
    authority_name_match: "Nombre de autoridad compatible.",
    authority_dni_match: "DNI de autoridad compatible.",
    status_enabled: "Estado habilitado o vigente.",
    not_expired: "Documento vigente.",
    policy_number_present: "Poliza identificada.",
    policy_current: "Poliza vigente.",
  };

  if (key.startsWith("field_")) {
    return `${key.replace(/^field_/, "").replace(/_present$/, "").replace(/_/g, " ")} identificado.`;
  }

  return labels[key] || key;
}

function getCompanyDocumentExpiration(extractedFields = {}) {
  return (
    extractedFields.fecha_vencimiento ||
    extractedFields.valid_until ||
    extractedFields.vigencia_hasta ||
    extractedFields.expiration_date ||
    null
  );
}

function mapCompanyValidationLabel(status) {
  if (status === "approved") {
    return "Validado";
  }

  if (status === "rejected") {
    return "Rechazado";
  }

  return "Observado";
}

function mapCompanyValidationTone(status) {
  if (status === "approved") {
    return "vigente";
  }

  if (status === "rejected") {
    return "vencido";
  }

  return "faltante";
}

function getLatestDocumentsByType(documents, entidadTipo = "") {
  const latestByType = new Map();

  documents.forEach((documento) => {
    const normalizedType = normalizeTransportDocumentType(entidadTipo, documento.tipo_documento);
    const current = latestByType.get(normalizedType);
    if (!current) {
      latestByType.set(normalizedType, documento);
      return;
    }

    const currentDate = new Date(current.created_at || 0).getTime();
    const nextDate = new Date(documento.created_at || 0).getTime();
    if (nextDate >= currentDate) {
      latestByType.set(normalizedType, documento);
    }
  });

  return Array.from(latestByType.values()).sort((left, right) => {
    const rightDate = new Date(right.created_at || 0).getTime();
    const leftDate = new Date(left.created_at || 0).getTime();
    return rightDate - leftDate;
  });
}

function mergeUnitWithRtoData(unit, snapshot) {
  if (!unit || !snapshot?.rto) {
    return unit;
  }

  const rto = snapshot.rto;
  const overrides = {};

  if (rto.marca) {
    overrides.marca = rto.marca;
  }

  if (rto.modelo) {
    overrides.modelo = rto.modelo;
  }

  if (rto.anioModelo) {
    overrides.anio = rto.anioModelo;
  }

  if (rto.categoria && !unit.tipo) {
    overrides.tipo = humanizeLabel(rto.categoria);
  }

  return {
    ...unit,
    ...overrides,
  };
}

function sortUnitBatchResults(results, unitBatchType) {
  if (unitBatchType !== "nueva_unidad") {
    return results;
  }

  return [...results].sort((left, right) => {
    const leftScore = buildCedulaBatchScore(left.result);
    const rightScore = buildCedulaBatchScore(right.result);
    return rightScore - leftScore;
  });
}

function buildCedulaBatchScore(result) {
  let score = Math.round((result?.confianza || 0) * 100);
  if (result?.datos_extraidos?.dominio) {
    score += 20;
  }
  if (result?.validacion_rto?.estado_final === "aprobado") {
    score += 10;
  }
  return score;
}

function resolveDetectedEntityType(analysis, fallbackTipo = "") {
  const suggested = String(analysis?.entidad_sugerida || "").trim().toLowerCase();

  if (suggested === "acoplado" || suggested === "unidad") {
    return suggested;
  }

  const text = `${String(analysis?.datos_extraidos?.tipo || "")} ${String(fallbackTipo || "")}`.toUpperCase();
  if (/(ACOPLAD|REMOLQUE|SEMI\s*REMOLQUE|SEMIRREMOLQUE)/.test(text)) {
    return "acoplado";
  }

  return "unidad";
}

function upsertFleetDocument(current, entidadTipo, entidadId, savedDocument) {
  if (!current) {
    return current;
  }

  const collectionKey = entidadTipo === "acoplado" ? "acoplados" : "unidades";
  const nextCollection = (current[collectionKey] || []).map((item) => {
    if (String(item.dominio) !== String(entidadId)) {
      return item;
    }

    const remainingDocuments = (item.documentos || []).filter(
      (documento) =>
        normalizeTransportDocumentType(entidadTipo, documento.tipo_documento) !==
        normalizeTransportDocumentType(entidadTipo, savedDocument.tipo_documento)
    );

    return {
      ...item,
      mock: false,
      documentos: [savedDocument, ...remainingDocuments],
    };
  });

  return {
    ...current,
    [collectionKey]: nextCollection,
  };
}

function upsertCompanyDocument(currentCompanies, companyCuit, savedDocument) {
  if (!Array.isArray(currentCompanies) || !savedDocument) {
    return currentCompanies;
  }

  return currentCompanies.map((company) => {
    if (String(company.cuit) !== String(companyCuit)) {
      return company;
    }

    const remainingDocuments = (company.documentos || []).filter(
      (documento) =>
        normalizeTransportDocumentType("titular", documento.tipo_documento) !==
        normalizeTransportDocumentType("titular", savedDocument.tipo_documento)
    );

    return {
      ...company,
      documentos: [savedDocument, ...remainingDocuments],
    };
  });
}

function upsertUnitDocuments(current, domain, documents = []) {
  if (!current || !domain) {
    return current;
  }

  const normalized = String(domain || "").toUpperCase();
  const mergeCollection = (collectionKey) => {
    const collection = current[collectionKey] || [];
    return collection.map((item) => {
      if (String(item.dominio || "").toUpperCase() === normalized) {
        return {
          ...item,
          documentos: documents,
        };
      }
      return item;
    });
  };

  return {
    ...current,
    unidades: mergeCollection("unidades"),
    acoplados: mergeCollection("acoplados"),
  };
}

function getFirstFile(value) {
  if (!value) {
    return null;
  }

  return Array.isArray(value) ? value[0] : value;
}

function buildMockDocumentUrl(document) {
  const html = `
    <!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Documento mock</title>
        <style>
          body { font-family: sans-serif; margin: 0; padding: 32px; background: #f6f2e8; color: #1f2a2a; }
          .card { max-width: 720px; margin: 0 auto; background: white; border-radius: 24px; padding: 32px; box-shadow: 0 16px 40px rgba(0,0,0,.08); }
          .badge { display: inline-block; padding: 8px 12px; border-radius: 999px; background: #d65636; color: white; font-weight: 700; margin-bottom: 16px; }
          h1 { margin: 0 0 12px; }
          p { margin: 8px 0; line-height: 1.5; }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="badge">Mock</div>
          <h1>${humanizeLabel(document.tipo_documento || "documento")}</h1>
          <p>Este archivo pertenece a la flota mockeada del portal de transporte.</p>
          <p><strong>ID:</strong> ${document.id}</p>
          <p><strong>Tipo:</strong> ${document.tipo_documento || "-"}</p>
          <p><strong>Estado:</strong> ${document.estado || "-"}</p>
          <p><strong>Fecha:</strong> ${formatDate(document.fecha_vencimiento || document.created_at || "") || "-"}</p>
        </div>
      </body>
    </html>
  `;

  return URL.createObjectURL(new Blob([html], { type: "text/html" }));
}

function buildDocumentErrorHtml(message) {
  return `
    <!doctype html>
    <html lang="es">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>No pudimos abrir el archivo</title>
        <style>
          body { font-family: sans-serif; margin: 0; padding: 32px; background: #f6f2e8; color: #1f2a2a; }
          .card { max-width: 720px; margin: 0 auto; background: white; border-radius: 24px; padding: 32px; box-shadow: 0 16px 40px rgba(0,0,0,.08); }
          .badge { display: inline-block; padding: 8px 12px; border-radius: 999px; background: #d65636; color: white; font-weight: 700; margin-bottom: 16px; }
          h1 { margin: 0 0 12px; }
          p { margin: 8px 0; line-height: 1.5; }
        </style>
      </head>
      <body>
        <div class="card">
          <div class="badge">Error</div>
          <h1>No pudimos abrir el archivo</h1>
          <p>${message}</p>
        </div>
      </body>
    </html>
  `;
}

async function ensureTransportEntityExists({ entidadTipo, entidadId, entityData, dashboardCuit, dashboardData }) {
  if (!entityData?.mock) {
    return;
  }

  await postForm("/titulares", {
    cuit: dashboardCuit,
    razon_social: dashboardData?.titular?.razon_social || "Transportes Mock SA",
    email: dashboardData?.titular?.email || "",
  });

  if (entidadTipo === "acoplado") {
    await postForm("/acoplados", {
      titular_cuit: dashboardCuit,
      dominio: entidadId,
      marca: entityData?.marca || "",
      modelo: entityData?.modelo || "",
      anio: entityData?.anio || "",
      tipo: entityData?.tipo || "",
    });
    return;
  }

  await postForm("/unidades", {
    titular_cuit: dashboardCuit,
    dominio: entidadId,
    marca: entityData?.marca || "",
    modelo: entityData?.modelo || "",
    anio: entityData?.anio || "",
    tipo: entityData?.tipo || "",
  });
}
